/**
 * 系統更新公告資料。
 *
 * ⚠️ 網頁後台與 App 是兩條產品線，公告分開兩個分頁，不要混在一起講 ——
 * 店家看到「首頁新增今日值班」會去網頁後台找，找不到就來問客服。
 * 每一筆用 platform 標明是哪一邊：
 *   platform: 'web'（預設，可省略）→ 網頁後台
 *   platform: 'app'                → 手機 App
 *
 * 每次有新公告：在 ANNOUNCEMENTS 陣列「最前面」加一筆，
 * 並把「那個平台」的最新版本字串改掉（LATEST_VERSION / LATEST_VERSION_APP）。
 * 兩邊各自記「上次讀過的版本」，所以 App 出新公告不會讓網頁分頁也亮紅點。
 */

// 點進公告頁後寫入 localStorage 的 key（依平台各存一份）
export const ANNOUNCEMENT_READ_KEY = 'beauty:lastReadAnnouncementVersion'
export const ANNOUNCEMENT_READ_KEY_APP =
  'beauty:lastReadAnnouncementVersion:app'

/**
 * 各平台的最新版本：必須與「該平台第一筆」的 version 同步。
 *
 * ⚠️ 不是 ANNOUNCEMENTS[0].version —— 分平台之後，陣列第一筆可能是 App 的。
 */
export const LATEST_VERSION = '2026-11-04' // 網頁後台
export const LATEST_VERSION_APP = '2026-11-03' // App

/** 公告頁的分頁定義。順序就是分頁顯示順序。 */
export const ANNOUNCEMENT_PLATFORMS = [
  { key: 'web', label: '網頁後台' },
  { key: 'app', label: '手機 App' }
]

/** 舊公告沒有 platform 欄位（分平台之前寫的），一律當成網頁後台 */
export const platformOf = (ann) => (ann?.platform === 'app' ? 'app' : 'web')

export const latestVersionOf = (platform) =>
  platform === 'app' ? LATEST_VERSION_APP : LATEST_VERSION

export const readKeyOf = (platform) =>
  platform === 'app' ? ANNOUNCEMENT_READ_KEY_APP : ANNOUNCEMENT_READ_KEY

/** 某個平台有沒有未讀公告 */
export const hasUnreadAnnouncement = (platform) => {
  try {
    return (
      window.localStorage.getItem(readKeyOf(platform)) !==
      latestVersionOf(platform)
    )
  } catch (e) {
    // localStorage 被禁用時寧可亮紅點，也不要讓店家錯過新功能
    return true
  }
}

/** 任何一個平台有未讀就回 true —— Sidebar 那顆紅點用這個 */
export const hasAnyUnreadAnnouncement = () =>
  ANNOUNCEMENT_PLATFORMS.some((p) => hasUnreadAnnouncement(p.key))

/**
 * 每筆 section 的格式：
 *  - kind: 'feature'（新功能）| 'bugfix'（修正）| 'improvement'（體驗優化）
 *  - title: 一句話標題（給店家看，不要技術詞）
 *  - description: 用店家聽得懂的話描述「之前怎樣 / 現在怎樣」
 *  - howToUse: 純文字（適合「不用做任何事」這種短句）
 *  - howToUseSteps: [{ heading?, steps: [string] }]，當有多情境或多步驟時用這個
 *  - devNote: （可選）工程備註，只在 admin 端的更新紀錄頁顯示，店家公告頁不會出現。請全程使用中文，避免英文技術名詞。
 *  - shopHidden: （可選）強制不在店家公告頁顯示，即便 kind 是 feature/improvement。
 *
 * 顯示規則：
 *  - 店家公告頁（/announcements）：只顯示 kind = feature / improvement，bugfix 全部過濾掉。
 *  - Admin 更新紀錄頁（/admin/changelog）：顯示全部，含 devNote。
 */
export const ANNOUNCEMENTS = [
  {
    version: '2026-11-04',
    platform: 'web',
    title: '11/4 堂票核銷算施作業績＋堂票發放紀錄',
    summary:
      '客人用堂票來做，施作人員現在也拿得到技術業績與技術抽成（每堂 = 堂票售價 ÷ 堂數）；堂票模組另外多了「發放紀錄」分頁，可以依日期回頭查每一張堂票是怎麼發出去的。',
    sections: [
      {
        kind: 'feature',
        title: '堂票發放紀錄',
        description:
          '以前只能在「堂票紀錄」看每張票現在剩幾堂，想知道這個月發了哪些票、哪些是贈送、哪些是結帳時買的，得一張一張點開。現在「發放紀錄」會依發放時間列出每一筆，清楚標示「銷售／贈送」「後台發放／結帳購買」、金額與支付方式、銷售人員、經手人；結帳購買的還會顯示訂單編號。上方會統計篩選期間的發放筆數、總堂數、銷售金額與贈送筆數。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「堂票模組」→「堂票發放紀錄」（或在堂票頁面上方切換到「發放紀錄」分頁）',
              '預設顯示本月，可以改日期區間，也能用會員、堂票名稱、來源、銷售／贈送、銷售人員篩選',
              '點列表右邊的眼睛圖示，會跳到「堂票紀錄」並直接篩出那張票，可以看剩餘堂數與使用歷程'
            ]
          }
        ],
        devNote:
          '新增發放紀錄查詢介面，依發放日期以店家時區篩選，統計的銷售金額排除贈送與已作廢的票。資料直接取自現有的堂票資料，不新增資料表，舊資料沒有銷售／贈送欄位的一律視為銷售。'
      },
      {
        kind: 'feature',
        title: '堂票核銷也算施作人員的業績與抽成',
        description:
          '以前客人拿堂票來做，因為這一堂的錢在賣票時就收了，做的人拿不到任何技術業績與抽成。現在每核銷一堂，施作人員會拿到「堂票售價 ÷ 堂數」的技術業績（例如 3 堂 $3,000 的票，每做一堂算 $1,000），抽成比例跟一般技術項目一樣（有服務抽成規則照規則，沒有就照員工的業績階梯），也會併入總業績、影響業績階梯。賣票的人照舊拿堂票銷售業績，兩邊分開算。',
        howToUseSteps: [
          {
            heading: '業績算給誰',
            steps: [
              '結帳時用堂票折抵服務：算給那個服務的設計師',
              '結帳時「加購堂票」並填「本次核銷」：會多一個「施作人員」欄位，沒選就算給銷售人員',
              '在堂票紀錄手動「扣張」：可以選施作人員，預設是登入的自己'
            ]
          },
          {
            heading: '在哪裡看',
            steps: [
              '「薪水與業績管理」→ 月薪資彙總，點進員工明細，技術抽成裡標示「堂票核銷」的那幾筆，會寫出每堂怎麼算'
            ]
          }
        ],
        howToUse: '11/1 起的核銷才會計算，10 月以前已結算的薪資不受影響。',
        devNote:
          '核銷紀錄新增施作人員欄位；舊紀錄依「購買時核銷算給銷售人員、折抵服務算給設計師、手動扣張算給操作的人」推回。核銷明細併進技術抽成明細，因此總業績、階梯、薪資明細、業績分解報表都會一起算到；不扣金流手續費，每次固定抽成不重複給。退堂會沖回同一張票最近一次核銷的施作人員。員工自收的堂票與服務不計。'
      }
    ]
  },
  {
    version: '2026-11-03',
    platform: 'app',
    title: '11/3 App：紅利點數',
    summary:
      'App 也能用紅利點數了：顧客資料可以看點數、加點、兌換，設定頁可以改送點規則和兌換項目，跟網頁後台同步。',
    sections: [
      {
        kind: 'feature',
        title: '顧客資料多了「紅利點數」卡片',
        description:
          '打開顧客資料，分期下方會看到這位客人的紅利點數和最近 5 筆紀錄，按「全部紀錄」可以看完整明細。客人打卡、介紹親友時按「加點」選項目；要換課程時按「兌換」選項目，點數不夠的項目會標出還差幾點，兌換後客人會收到站內與 LINE 通知。店主與店長另外有「校正」，可以填任意點數並寫下原因。結帳、儲值的自動送點不用操作，作廢時也會自動扣回。',
        howToUse:
          '「顧客」→ 點進顧客資料 → 紅利點數卡片。店家還沒開啟紅利點數、這位客人也沒有任何點數紀錄時，卡片不會出現。'
      },
      {
        kind: 'feature',
        title: '設定 → 紅利點數',
        description:
          '可以開關紅利點數、設定「結帳消費每多少元送 1 點」「儲值每多少元送 1 點」，以及新增或停用加點項目、兌換項目。改完和網頁後台【優惠模組 → 紅利點數】是同一份設定。',
        howToUse:
          '「設定」→「紅利點數」。只有店主與店長可以修改，其他員工只能看。',
        devNote:
          '顧客卡片沿用分期卡的樣式與底部面板；權限讀 員工角色 API，比照分期的做法，讀取失敗時先放行、由後端把關。後端紅利點數的顧客 API 改成顧客編號與平台會員編號都接受（與儲值金 API 一致），因為 App 顧客頁傳的編號可能是後者。顧客資料編輯不再送出點數欄位，舊版 App 送了也會被後端忽略。'
      }
    ]
  },
  {
    version: '2026-11-02',
    platform: 'web',
    title: '11/2 紅利點數上線：結帳、儲值自動送點，兌換有紀錄',
    summary:
      '開啟紅利點數後，客人結帳、儲值會依你設定的比例自動累積點數；好評、介紹親友可以手動加點，兌換課程或商品時直接在顧客資料扣點，每一筆都查得到。',
    sections: [
      {
        kind: 'feature',
        title: '紅利點數：自動送點、手動加點、兌換',
        description:
          '以前顧客資料上的「積分」只是一個可以手動改的數字，結帳不會自動加，改過也查不到原因。現在可以設定「消費每 1000 元送 1 點」、「儲值每 1000 元送 1 點」這類規則，結帳和儲值時系統會自動加點。用儲值金或紅利金付的部分不會再送點，避免同一筆錢送兩次點。好評打卡、介紹親友這類活動，可以先設定成加點項目，店員在顧客資料選項目就能加點；兌換也一樣先設定兌換項目（例如 20 點換經典管理課程擇一），選了就扣點，點數不夠會擋下來。結帳單作廢或儲值紀錄刪除時，當初送的點會自動扣回。每一筆加點、扣點都會記下時間、原因和經手人。點數沒有期限。客人在美感網站的會員中心「紅利點數」可以看到自己在你店裡的點數、累積方式和可兌換的項目；兌換後客人會收到通知，自動加點不會通知，避免訊息太多。',
        howToUseSteps: [
          {
            heading: '設定規則（店主、店長）',
            steps: [
              '左側選單進入「優惠模組」→ 切到「紅利點數」分頁',
              '打開「啟用紅利點數」，填入結帳和儲值的送點比例',
              '新增加點項目（例：五星好評 1 點、介紹親友 3 點）和兌換項目（例：經典管理課程擇一 20 點），按「儲存設定」'
            ]
          },
          {
            heading: '幫客人加點、兌換',
            steps: [
              '打開顧客資料，在儲值金旁邊切到「紅利點數」',
              '按「加點」或「兌換」，選項目後確認；下方會列出每一筆點數紀錄',
              '要把紙本集點卡轉進系統，店主、店長可以按「校正」直接輸入點數並填原因'
            ]
          }
        ],
        devNote:
          '新增點數異動紀錄表與 /api/businesses/:businessId/points 一組路由（商務以上方案），新增 points:view、points:manage、points:settings 三個權限。結帳單的點數用「應得點數減已給點數」同步，所以結帳、補款、分期收款、線上商店訂單、作廢都呼叫同一支，重複呼叫不會重複送點；計點比例存成快照，店家之後改比例不影響舊單。儲值依實收金額計點（扣掉應收帳款、不含贈送），刪除儲值紀錄會扣回。顧客編輯表單不再寫入點數，避免把開啟表單時的舊點數存回去蓋掉自動加的點；既有點數在第一次異動時自動補一筆期初餘額。顧客合併時一併搬移點數紀錄。客人端新增 /api/client/points（只列有開啟點數的店，校正原因與店員備註不外露），前台新增 /account/points；兌換時發站內通知與 LINE。'
      }
    ]
  },
  {
    version: '2026-10-28',
    platform: 'web',
    title: '10/28 結帳的施作照片自動進顧客照片紀錄 / 結帳紀錄可以只看有照片的',
    summary:
      '結帳時上傳的施作照片，現在會自動出現在顧客資料的「照片記錄」裡，每一次都標好日期和結帳備註；結帳紀錄也可以一鍵篩出有施作照片的單子。',
    sections: [
      {
        kind: 'feature',
        title: '顧客「照片記錄」自動帶入結帳時的施作照片',
        description:
          '以前結帳時傳的施作照片只掛在那筆預約上，想看一位客人歷次做過什麼，得一筆一筆點開結帳紀錄。現在打開顧客資料，「照片記錄」下面會多一區「最近施作」，列出這位客人最近 3 次結帳的照片，每一次都標出消費日期、做了哪些服務和當時寫的結帳備註。按「查看全部施作照片」會進到這位客人專屬的施作照片頁，依月份排好所有紀錄，可以用服務項目或日期篩選，例如只看「染髮」那幾次，放大照片後還能一路往前翻，回頭看「上次染的是哪個色號」一眼就有答案。照片會跟預約詳情同步：在預約詳情刪掉或改成不給客人看，這裡也會跟著變。沒有對客人公開的照片右下角會帶一個眼睛圖示。',
        howToUse:
          '不用做任何事。結帳時照常上傳施作照片，之後打開那位客人的顧客資料就看得到；之前已經上傳過的照片也會一起出現。',
        devNote: '照片沒有複製進顧客的 photos，而是新增 GET /api/businesses/:businessId/service-photos/customers/:customerId 直接查施作照片，依預約分組並帶回結帳單的消費日期、時間與備註（同一筆預約結過兩次時取沒作廢、最新的那張；照片已上傳但還沒結帳的退回用預約日期、備註留空）。歸屬判斷是「照片的 customerId 是他」或「照片所在預約的結帳單顧客是他」，後者補回預約當下還沒建顧客檔的情況。顧客合併時一併搬移施作照片的 customerId。結帳紀錄列表 GET /checkout/orders 新增 hasPhoto=1，先 distinct 出有未刪除照片的預約，再以 bookingId 篩選；施作照片新增 { businessId, isDeleted, bookingId } 索引。'
      },
      {
        kind: 'feature',
        title: '結帳紀錄可以只看「有施作照片」的單子',
        description:
          '結帳紀錄的排序列旁邊多了一個「只看有施作照片」勾選，勾起來就只列出有上傳施作照片的結帳單，可以再搭配日期、服務人員等條件一起篩，方便整理作品或回頭找某次的施作成果。',
        howToUse:
          '「結帳管理 → 結帳紀錄」，勾選排序方式右邊的「只看有施作照片」，列表會立刻更新。'
      }
    ]
  },
  {
    version: '2026-10-28',
    platform: 'app',
    title: '10/28 App：顧客照片紀錄自動帶入施作照片 / 結帳紀錄可篩有照片的',
    summary:
      'App 的顧客資料「照片紀錄」會自動列出每一次結帳的施作照片和備註；結帳紀錄多了「有施作照片」篩選。',
    sections: [
      {
        kind: 'feature',
        title: '顧客「照片紀錄」自動帶入結帳時的施作照片',
        description:
          '打開顧客資料往下捲到「照片紀錄」，會看到這位客人最近 3 次結帳的施作照片，標出日期、服務和當時的結帳備註。按「查看全部施作照片」可以看所有紀錄，依月份排好，還能用時間範圍或服務項目篩選；點照片可以放大、一路左右滑、存到手機相簿。照片和預約詳情是同步的，不用另外整理。',
        howToUse: '不用做任何事。之前上傳過的施作照片也會一起出現。'
      },
      {
        kind: 'feature',
        title: '結帳紀錄可以只看「有施作照片」的單子',
        description:
          '結帳紀錄上方的狀態篩選列最後多了一顆「有施作照片」，點一下就只列出有照片的結帳單，可以跟月份、服務人員、狀態一起搭配使用，再點一次取消。',
        howToUse: '「結帳紀錄」→ 點篩選列最右邊的「有施作照片」。'
      }
    ]
  },
  {
    version: '2026-10-18',
    platform: 'web',
    title: '10/18 客人預約後，可以一鍵在 LINE 傳訊息給你',
    summary:
      '打開新開關後，客人預約完會看到「傳送預約資訊給店家」按鈕，按一下就會用他自己的 LINE 傳訊息到你的官方帳號，你在聊天室就能直接跟他確認項目和時間。',
    sections: [
      {
        kind: 'feature',
        title: '預約後請客人在 LINE 傳訊息給店家',
        description:
          '以前陌生客在美感預約後，系統的 LINE 通知只會單向發給客人，客人沒有主動開口，你在 LINE 官方帳號的聊天室裡根本看不到這個人，需要先確認項目或時間時找不到人。現在「LINE 整合」設定多了「預約後請客人傳訊息給店家」開關，打開後，客人收到的預約通知和預約完成頁都會多一顆「傳送預約資訊給店家」按鈕。客人按下後，會用他自己的 LINE 傳一則訊息到你的官方帳號，內容有預約的服務、時間、姓名和預約編號，你在聊天室就能直接回覆他。客人傳過訊息的預約，預約詳情會顯示「客人已在 LINE 聯繫」。店家自己新增的預約不會出現這顆按鈕。',
        howToUseSteps: [
          {
            heading: '開啟功能',
            steps: [
              '左側選單進入「設定」→「LINE 整合」（需已串接本店自己的 LINE 官方帳號）',
              '打開「預約後請客人傳訊息給店家」，按「保存設定」'
            ]
          },
          {
            heading: '到 LINE 官方帳號後台開啟「聊天」',
            steps: [
              '登入 LINE Official Account Manager，進入「設定」→「回應設定」',
              '把「聊天」打開（可以和 Webhook 同時開），沒開的話客人的訊息不會出現在聊天室',
              '建議同時關閉「自動回應訊息」，避免客人收到重複的罐頭回覆'
            ]
          }
        ],
        devNote:
          '店家 LINE 設定新增 contactPromptEnabled（預設關閉）與自動抓取的官方帳號 ID（透過取得機器人資訊 API）。預約通知走店家官方帳號時加「傳送文字」按鈕與快速回覆，走美感平台官方帳號或在完成頁時改用預填訊息連結開啟店家對話框。店家 webhook 收到開頭為【預約通知】且帶預約編號（_id 後 5 碼）的文字時，寫入 booking.lineContact.contactedAt。LINE 設定頁會查詢官方帳號是否已開啟聊天並提示。'
      }
    ]
  },
  {
    version: '2026-10-11',
    platform: 'web',
    title: '10/11 堂票可以在線上預約頁推薦',
    summary:
      '你可以把堂票設成在美感預約頁推薦，客人選到適用的服務時就看得到，有興趣的客人會在預約備註告訴你。',
    sections: [
      {
        kind: 'feature',
        title: '堂票可以設定「在線上預約頁推薦」',
        description:
          '以前客人在線上預約時不知道店裡有堂票方案，只能到店才聽你介紹。現在堂票範本多了一個開關「在線上預約頁推薦」，打開後，客人選到這張堂票適用的服務時，預約頁會出現「買堂票更划算」，顯示堂數、售價和每堂均價。客人勾選「我想了解」，送出的預約備註會多一行【想了解堂票】，你到店時就知道可以介紹。客人手上已經有適用的堂票時，預約頁也會提醒他到店可以扣抵。堂票一樣在店內販售，線上不會直接收款。',
        howToUseSteps: [
          {
            steps: [
              '左側選單進入「堂票設定」，編輯要推薦的堂票範本',
              '打開「在線上預約頁推薦」，存檔',
              '範本說明會一起顯示給客人看，建議寫清楚適用項目與使用期限'
            ]
          }
        ],
        devNote:
          '堂票範本新增 showOnBookingPage（預設關閉，員工自收等內部用的堂票不會被看到）；新增公開 API GET /api/business_C/:businessId/class-ticket-offers?productIds=，只回有打開推薦、且適用所選服務的範本；客人端堂票列表多回 applicableServiceIds。'
      }
    ]
  },
  {
    version: '2026-10-11',
    platform: 'app',
    title: '10/11 堂票可以設定在預約頁推薦',
    summary:
      'App 的堂票範本多了「在線上預約頁推薦」開關，客人預約時就看得到你的堂票方案。',
    sections: [
      {
        kind: 'feature',
        title: '堂票範本新增「在線上預約頁推薦」',
        description:
          '以前客人在線上預約時不知道店裡有堂票方案。現在在 App 編輯堂票範本時，可以打開「在線上預約頁推薦」：客人在美感預約頁選到這張堂票適用的服務時，會看到堂數、售價和每堂均價，勾選「我想了解」的話，預約備註會多一行【想了解堂票】。',
        howToUseSteps: [
          {
            steps: [
              '進入「堂票範本」，點要推薦的範本編輯',
              '打開「在線上預約頁推薦」，儲存'
            ]
          }
        ],
        devNote:
          'App 堂票範本表單新增 showOnBookingPage 開關；後端欄位與網頁後台共用。'
      }
    ]
  },
  {
    version: '2026-10-05',
    platform: 'web',
    title: '10/5 分期款在顧客資料頁、結帳頁就看得到、收得到',
    summary:
      '客人有分期還沒繳完，打開他的顧客資料或幫他結帳時，畫面會直接提醒「本次應繳多少」，按一下就能收款，不用再切到分期收款頁找人。',
    sections: [
      {
        kind: 'improvement',
        title: '顧客資料頁、結帳頁直接顯示分期，並可當場收款',
        description:
          '以前分期只能在「分期收款」頁查，客人下次來店時，如果結帳的不是當初開分期的那位，或分期的客人一多，很容易忘了這位客人還有一期沒繳。現在只要客人還有分期沒收完：顧客資料頁上方會出現提醒，右側多了一個「分期」分頁，可以看每一筆的進度與歷史；結帳頁（現場結帳、帶入預約結帳都一樣）的「付費資訊」最上面會列出這位客人的分期，並標出「本次應繳」的金額。按「收款」就能登錄，不必離開結帳畫面。「每次來店繳」的分期，客人一來就會提示繳下一期；按月、按週的分期，則會把已到期還沒繳的期數一起算進本次應繳。',
        howToUseSteps: [
          {
            heading: '結帳時順便收分期',
            steps: [
              '照平常的方式結帳，選好顧客',
              '往下到「付費資訊」，最上面會看到這位客人的分期提醒',
              '按「收款」，金額會自動帶入本次應繳，確認付款方式後送出',
              '分期款是另外入帳的，不要再把這筆錢加進下面的付款欄位'
            ]
          },
          {
            heading: '在顧客資料頁查看',
            steps: [
              '打開顧客資料，右側切到「分期」分頁',
              '可以看到每一筆分期的已收、未收與下期日期，按「明細」看每一期與收款紀錄'
            ]
          }
        ],
        devNote:
          '新增 GET /api/businesses/:businessId/installments/by-customer/:customerId，接受顧客編號或平台會員編號（帶入預約結帳時手上可能只有後者），計畫沒綁顧客編號的舊資料改用電話比對；回傳的每筆計畫多了 dueNowAmount / dueNowPeriods（本次應繳），規則是固定週期取到期日小於等於今天的未收期數、每次來店繳取下一期。分期收款控制器的「今天」改以店家時區計算。前端把收款視窗、明細視窗抽成 components/installment 底下的共用元件，分期收款頁、顧客資料頁、結帳頁共用同一份。'
      },
      {
        kind: 'improvement',
        title: '同意書可以設定「每位顧客簽一次」或「只發給新客」',
        description:
          '像顧客資料表、初診問卷這種建檔一次就好的表單，以前每次預約都會再發一次，熟客每次來都要重填。現在施作同意書多了「簽署次數」可以選：每次預約都發（原本的做法）、每位顧客簽一次就好（簽過就不再發，從沒簽過的舊客下次預約還是會收到）、只發給新客（第一次來店預約、且沒簽過的顧客才會收到）。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「同意書模組 → 同意書設定」，編輯那份同意書',
              '在「簽署次數」選「每位顧客簽一次就好」或「只發給新客」，存檔',
              '要請熟客重簽時，到預約詳情手動選這份同意書補發即可，不受這個設定限制'
            ]
          }
        ],
        devNote:
          '範本新增 signFrequency（every_booking / once / new_customer，預設 every_booking）。判斷集中在 consentFormService.createAndSendForBooking，預約成立、行前排程、客人在預約成功頁點開、店家手動發送（未指定範本時）都會套用；店家手動指定範本補發則不受限制。once：這位顧客簽過該範本任一版本就不發，另有其他預約發出的未過期待簽時也不重發；new_customer 另外要求這家店沒有其他未取消的預約、也沒有未作廢的結帳紀錄。'
      }
    ]
  },
  {
    version: '2026-10-05',
    platform: 'app',
    title: '10/5 App 的顧客詳情、結帳、訂單詳情都看得到分期並可收款',
    summary:
      '客人有分期還沒繳完，在 App 打開顧客、幫他結帳或看訂單時都會直接顯示，並可當場收款。',
    sections: [
      {
        kind: 'improvement',
        title: 'App 也能在顧客詳情、結帳、訂單詳情直接收分期款',
        description:
          '之前 App 只有在「設定 → 分期收款」裡才看得到分期。現在顧客詳情最上方會顯示這位客人的分期進度；結帳時選好顧客，「付款方式」區塊最上面會提醒還有幾筆分期、本次應繳多少，按「收款」就能登錄；開過分期的訂單，訂單詳情也多了「分期」區塊可以看進度、收款。沒有分期收款權限的員工只看得到資料，不會出現收款按鈕。',
        howToUse:
          '不用做任何設定。客人有進行中的分期時，畫面會自動出現提醒；按「收款」確認金額與付款方式即可。'
      },
      {
        kind: 'improvement',
        title: 'App 的同意書設定也能選「每位顧客簽一次」或「只發給新客」',
        description:
          '在 App 編輯施作同意書時，多了「簽署次數」可以切換，跟電腦版是同一個設定：顧客資料表這類建檔一次就好的表單，不必再每次預約都發。',
        howToUse: '設定 → 同意書管理 → 右上角範本設定 → 編輯同意書，點「簽署次數」切換後儲存。'
      }
    ]
  },
  {
    version: '2026-09-30',
    platform: 'web',
    title: '9/30 員工自收項目拆帳：客人直接付給員工的項目，改成「員工繳回店家」',
    summary:
      '有些項目是客人直接把錢付給老師、老師月底再繳一個比例給店家。現在可以把這類項目設成「員工自收」，薪資不會再把它加進去，而是另外列出「本人所得／應繳店家」，營收報表也只算店家收到的那份。',
    sections: [
      {
        kind: 'feature',
        title: '服務抽成：結算方式「員工自收，繳回店家 %」',
        description:
          '以前這類項目只能設成「員工抽 70%」，結果會被加進薪資，好像店家要付這筆錢。現在服務項目抽成規則多了「結算方式」，選「員工自收」並填繳回比例（例如 30%）後：這個項目不算薪資、也不算業績階梯；薪資明細多一區「自收項目拆帳」，逐筆列出收費、本人所得、應繳店家與當月合計；營收報表的技術營收只算繳回店家的部分。',
        howToUseSteps: [
          {
            heading: '設定項目',
            steps: [
              '「薪水與業績管理 → 服務項目抽成設定」展開要設定的項目',
              '「結算方式」選「員工自收，繳回店家一定比例」，填入比例後儲存',
              '到「每月員工薪資」按重新計算，本月薪資就會套用新設定'
            ]
          },
          {
            heading: '月底對帳',
            steps: [
              '「每月員工薪資」列表多一欄「自收應繳店家」',
              '點「查看細項」，在「自收項目拆帳」看到每一筆與合計'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '員工自收堂票：每核銷一堂再繳回',
        description:
          '套票如果也是老師直接收全額（例如四堂 40,000），在堂票範本勾選「員工自收堂票」，售價就不會算進店家堂票營收，也不會算堂票銷售抽成。之後客人每做一堂，以「售價 ÷ 總堂數」當作這次的收費，依該項目的繳回比例算出應繳店家（例：40,000 ÷ 4 × 30% = 3,000）。',
        howToUse:
          '「堂票設定」編輯範本，打開「員工自收堂票」後儲存；該堂票可折抵的服務也要設成「員工自收」。',
        devNote:
          '服務抽成規則新增結算方式與繳回比例，堂票範本新增員工自收旗標。拆帳明細由新的共用模組計算，薪資明細與營收報表共用同一份。已存在的薪資摘要要按重新計算才會更新。'
      },
      {
        kind: 'feature',
        title: '堂票銷售抽成：可改成「全店總業績 × 個人比例」',
        description:
          '有些店的堂票抽成不是誰賣誰抽，而是全店當月賣出的堂票加總，每個人依自己的比例抽。現在「堂票銷售抽成設定」多了「抽成基數」可以選「全店總業績」，只有填了個人堂票抽成比例的人會參與；薪資明細會列出全店每一張堂票與售出人員。',
        howToUse:
          '「薪水與業績管理 → 堂票銷售抽成設定」的「抽成基數」選「全店總業績」後儲存；個人比例在「技術服務抽成設定」填。'
      },
      {
        kind: 'feature',
        title: '堂票範本：不計入堂票銷售業績／抽成',
        description:
          '有些套票店家照常收錢，但約定不算堂票業績、只給每次執行的技術費。範本打開這個選項後，售價仍算店家營收，但不算任何人的堂票業績與抽成。',
        howToUse:
          '「堂票設定」編輯範本，打開「不計入堂票銷售業績／抽成」；技術費到「服務項目抽成設定」填「每次固定抽成」。'
      }
    ]
  },
  {
    version: '2026-09-26',
    platform: 'web',
    title: '9/26 薪資抽成：每次固定抽成、指定人員比例、個人堂票比例、商品團積獎金',
    summary:
      '技術費按次給、同一個項目不同人抽不同 %、每個人賣堂票抽的比例不一樣、指定商品賣出後大家平分獎金，現在都可以直接設定，薪資會自動算好。',
    sections: [
      {
        kind: 'feature',
        title: '服務抽成：每次固定抽成',
        description:
          '有些項目是「做一次給 100 元技術費」，跟售價無關，客人用堂票核銷（售價 0）也照給。以前只能拿材料費欄位硬湊，現在服務項目抽成規則多了「每次固定抽成」，可以單獨使用，也可以跟 % 一起用。薪資明細會寫成「固定 100 × 1 次 = 100」。',
        howToUse:
          '「薪水與業績管理 → 服務項目抽成設定」展開項目，在「每次固定抽成」填金額後儲存。'
      },
      {
        kind: 'feature',
        title: '服務抽成：指定人員比例',
        description:
          '同一個項目，某位老師抽 70%、其他人不抽，現在可以直接設定。清單裡的人用自己的比例，其他人一律用「設計師抽成」的 %，填 0 就是 0，不會再套用員工業績階梯。',
        howToUse:
          '「服務項目抽成設定」展開項目，按「＋ 新增指定人員」，選人、填比例後儲存。'
      },
      {
        kind: 'feature',
        title: '員工個人堂票抽成比例',
        description:
          '每個人賣出堂票的抽成比例可以不一樣，例如 A 抽 8%、B 抽 10%。有填就用個人比例，留空就沿用「堂票銷售抽成設定」的全店階梯。',
        howToUse: '「薪水與業績管理 → 技術服務抽成設定」選員工，填「個人堂票抽成」後儲存。'
      },
      {
        kind: 'feature',
        title: '商品團積獎金（指定商品，成員均分）',
        description:
          '指定商品不論誰賣出，全店當月賣出件數 × 每件獎金，由指定成員平分，例如 100ml 每瓶 1000、30ml 每瓶 500，三個人平分。這些商品不會再另外按比例抽成，但業績照樣算給賣出的人。',
        howToUse:
          '「薪水與業績管理 → 產品銷售抽成設定」下方打開「團積獎金」，加入商品與每件獎金，再選要平分的成員後儲存。'
      }
    ]
  },
  {
    version: '2026-09-17',
    platform: 'app',
    title: '9/17 App：首頁看得到今日值班 / 結帳可以直接傳施作照片',
    summary:
      '打開 App 首頁就知道今天誰上班；結帳的時候順手把施作照片一起傳了，不用再回預約詳情補。',
    sections: [
      {
        kind: 'feature',
        title: 'App 首頁顯示「今日值班」',
        description:
          '以前 App 首頁只看得到今天誰休假，要知道誰上早班、誰上晚班得另外點進班表。現在日曆下方多了一列「今日值班」，直接列出當天排了哪幾個班別、各是誰，點一下還能看到每個班別的實際時間與完整名單。點選其他日期時這一列會跟著那一天走，標題也會變成該日期。沒有排班的日子、或店休那天不會顯示，不會多佔版面。',
        howToUse:
          '不用做任何事。有在「班表」排過班就會自動顯示；沒有班表檢視權限的員工看不到這一列。',
        devNote:
          '休假資料只記得「誰不在」，上班的班別不會產生 dayOff，所以非得另外打 GET /:businessId/schedule 不可 —— 整理邏輯與 B 端 Appointments.js 共用同一套規則，抽成 src/utils/shiftDisplay.js。兩個照搬的坑：一、班表每一格存的是排班當下的顏色與名稱快照，店家後來改了班別設定舊格子還留著舊值（同一個「早六」會忽藍忽綠），所以名稱與顏色一律跟著現行 shiftTypes 走；二、時間相反，要顯示那天實際排的時間而不是現在的設定。403（沒有班表權限）與空月份都靜默視為沒有資料，不跳錯誤。標籤色沿用 B 端的混色比例（底色往白色混 0.86、文字往黑色壓 0.45），兩邊看起來才是同一個班別。這一列跟著 selectedDate 走而不是今天，標題會在非今日時改成「M/D 值班」—— 旁邊那列「今日休假」還是寫死今日，之後要一起修。'
      },
      {
        kind: 'feature',
        title: 'App 結帳時可以直接上傳施作照片，結帳紀錄也看得到',
        description:
          '以前用 App 結帳完，要再回到預約詳情才能補傳施作照片，設計師常常忙完就忘了。現在結帳頁「備註」下面多了一區，做完直接拍一拍選進去，按結帳就會一起送出，照片會自動掛到那筆預約上，客人在自己的預約紀錄看得到。另外翻「結帳紀錄」時，每一筆下面會直接出現當次的照片縮圖；點進訂單詳情可以放大看，沒有對客人公開的那幾張右下角會帶一個眼睛圖示。純賣商品、沒有關聯預約的單子不會有這一區。',
        howToUseSteps: [
          {
            heading: '結帳時一起傳',
            steps: [
              '照平常的方式結帳，往下捲到「備註」下面的「施作照片」',
              '按「選擇照片」從相簿挑，最多 20 張，選錯可以按右上角的叉叉移除',
              '按「結帳」送出，照片會在訂單建立後自動上傳',
              '沒傳也沒關係，之後在預約詳情還是可以補'
            ]
          },
          {
            heading: '之後要看',
            steps: [
              '「結帳管理 → 結帳紀錄」，每一筆下面就是當次的照片',
              '點進那筆訂單詳情可以放大、左右翻'
            ]
          }
        ],
        devNote:
          '上傳時機刻意放在訂單建立成功之後 —— 照片掛在預約上，而預約 ID 在結帳送出前不一定存在（現場結帳就沒有）。與 B 端 CheckoutPage 同一個模式：不 await 進主流程的成敗判斷（訂單是錢、照片不是），失敗只提示一次並告訴店家可到預約詳情補傳；沒有 bookingId 就提示「這筆沒有關聯預約」。送出前先清空 state，不清的話店家接著結下一筆時這批照片會跟著送到下一筆預約上。結帳紀錄用批次端點 GET /service-photos/bookings?bookingIds= 在列表父層一次撈完再分給各張卡片，一頁十筆各自請求就是十個往返。列表裡的縮圖是唯讀模式（不掛燈箱 Modal、pointerEvents 關掉）：每張卡片各掛一個 Modal 太浪費，而且卡片本身可點，子層吃掉點擊店家就進不了訂單詳情 —— 放大改在訂單詳情做。方案與權限判斷抽成 useServicePhotoAccess hook，ServicePhotoSection 原本自己寫的那段一併改用它。另外修掉 iPhone HEIC 傳不上去的問題：expo-image-picker 的 iOS 原生碼遇到 HEIC 會原封不動回傳（ImageUtils.swift 的 UTType.heic 分支，quality 設多少都不轉檔），而後端 sharp 0.33 的預編譯 libvips 沒有 HEVC 編解碼器 —— 實測 metadata() 會誤報成功、真的解像素才丟 No decoding plugin。前端三支選圖都加上 preferredAssetRepresentationMode: compatible 讓 iOS 相簿先轉 JPEG，後端另外加 heic-convert 當 sharp 的備援。作品集與顧客照片原本是 compressImage catch 後回傳原 buffer、再被改名成 .jpg，存出來是打不開的圖，一併修正。'
      }
    ]
  },
  {
    version: '2026-09-15',
    platform: 'web',
    title: '9/15 新增分期收款 / 結帳紀錄看得到施作照片',
    summary: '客人可以分期繳款了，每一期收了沒、誰逾期，系統幫您盯著。',
    sections: [
      {
        kind: 'feature',
        title: '新增「分期收款」，客人可以分期繳款給店裡',
        description:
          '以前客人想把一筆大額療程分成幾次繳，只能自己在備註寫「還欠多少」，或是每次來店重新開一張單。錢收到哪裡了、這個月有誰該繳沒繳，只能靠記憶和翻紀錄。現在結帳時可以直接設定分期：分幾期、多久繳一期、第一期什麼時候到期，系統會自動排出每一期的到期日與金額。客人每次來繳款，到「分期收款」頁按一下登錄，進度就會自己往前跑。頁面最上方三個數字分別是「未收餘額、逾期金額、進行中計畫」，逾期的客人會標紅色排在最前面，拿起電話就能打。要收分期手續費也可以，設好費率系統會自動算進應收金額；這筆手續費不會算進設計師的業績，不用擔心同一組服務因為分期就多算一次抽成。營收報表也跟著加了「應收帳款」：這個月新增多少應收、收回多少、還有多少沒收、誰逾期了，一頁看完。',
        howToUseSteps: [
          {
            heading: '結帳時設定分期',
            steps: [
              '照平常的方式結帳，到付款方式那一區',
              '按「分期」欄位旁邊的「設定分期」',
              '「分期總額」填客人總共要分期繳的錢，期數就填您跟客人說的期數',
              '客人今天先繳的那一筆，填在「頭期款」（現金欄位不用再填一次）',
              '選繳款週期（預設「每次來店」）與首期付款日（預設今天），要收手續費就填費率',
              '確認下方試算的每一期金額與日期，按「套用」再送出結帳'
            ]
          },
          {
            heading: '客人來繳款時',
            steps: [
              '左側選單「結帳管理 → 分期收款」',
              '用姓名或電話找到那位客人，按「登錄收款」',
              '金額會自動帶入這期應繳，確認「收款日期」是今天再送出',
              '按「明細」可以看到每一期的狀態與過去每一次的收款紀錄'
            ]
          },
          {
            heading: '查誰逾期了',
            steps: [
              '在「分期收款」頁把上方的篩選切到「已逾期」',
              '逾期最久的排最前面，畫面上直接看得到電話'
            ]
          }
        ],
        devNote:
          '頭期款同時是排程的第 1 期：填了頭期款就把第 1 期的金額設成它，剩下的錢平均分給後面幾期，而不是維持平均值再拿頭期款去沖 —— 後者會讓店家填 1,270 卻看到「第 1 期 1,266」，多出來的 4 元被默默挪到第 2 期，對不上他跟客人談的數字。這條規則在後端 buildSchedule、B 端 buildScheduleLocal、App 端 buildInstallmentSchedule 三處各有一份，已用同參數比對過三邊結果一致。頭期款刻意記成分期帳本的第一筆 InstallmentPayment，而不是加進訂單的 payment.cash：兩邊都記會讓現金流把同一筆錢算兩次（訂單那條以 time.date 計入、分期那條以 paidAt 計入，結帳當下是同一天），而且走分期帳本才能讓期數維持店家講給客人聽的數字（分 3 期就是 3 期、第 1 期立刻顯示已收），不會變成「分 2 期」讓單據跟口頭說法對不上。createOrder 建完計畫後補這筆收款並 recalcPlanAndOrder，同時把 installmentPaid 同步回手上那份訂單文件 —— 後面堂票流程還會再 save 幾次，不同步有機會把已收金額洗回 0。頻率 per_visit 一律不算逾期（到期日只是系統排的預估值，客人本來就哪天來哪天繳，照預估日判斷會讓逾期名單塞滿沒有欠錢不還的人），這條規則在 InstallmentPlan.getOverdueAmount、installmentController.decorate、ReportController.getReceivablesData 三處各有一份，要一起改。新增 InstallmentPlan / InstallmentPayment 兩個 Model。收款明細刻意獨立一個 collection 而不是掛在計畫的子陣列：現金流報表要用「收款日」做區間加總，子陣列得靠 $unwind，量大就慢，也接不上 getCashFlowData 既有的逐筆寫法。InstallmentPayment.paidAt 是這個模組的核心 —— 既有現金流是以 CheckoutOrder.time.date（消費日期）當期間條件，分期收款若沿用會把客人三個月後繳的錢算回結帳當天，日報表與月報表都會錯，所以 getCashFlowData 另外查一次 InstallmentPayment 並以 paidAt 計入 paymentBreakdown 與 netCashInflow（刷卡／行動支付收的期款不計入實際入帳，與結帳單處理一致）。CheckoutOrder.payment 新增 installmentPaid：calculatePaidAmount 只認 installmentPaid、不認 installment，所以 amounts.unpaid 會隨客人繳款一期一期降下來；刻意不去累加 payment.cash，那會讓現金流算回結帳日。amounts.installmentFeeAmount 比照 surchargeAmount，commissionService 新增 getCommissionExcludedExtras 把服務費與分期手續費一起排除在業績基數之外（兩支 getEffective* 共用同一個 helper，避免日後只改一支導致兩張報表打架）；差別是分期手續費是店家真的賺到的錢，所以不從 getNetPaymentFee 扣，另外累計成 income.installmentFee，不併進 operatingRevenue。營收報表新增 getReceivablesData（未收餘額與逾期是「到今天為止的存量」，本期新增與收回是「區間流量」，期間定義刻意不同）。訂單作廢連動把計畫改成 canceled，但已收的收款紀錄一律保留 —— 那些錢是真的收過的，抹掉現金流會對不起來。抽成認列時點維持結帳日不變。權限 installment:view / installment:manage，方案門檻 brand（routes 的三個 gate 綁在 /:businessId/installments 路徑上，不是整個 router）。'
      },
      {
        kind: 'improvement',
        title: '結帳紀錄與顧客的結帳明細，現在看得到施作照片',
        description:
          '之前上傳的施作照片只能到預約詳情裡看。現在翻結帳紀錄、或從顧客資料點開那筆結帳明細時，下面會直接出現當次的照片縮圖，點一下就能放大、左右翻。沒有對客人公開的那幾張會在右下角帶一個眼睛圖示，一眼分得出哪些客人看得到、哪些只有店裡看得到。',
        howToUse: '不用做任何事。有上傳過照片的結帳紀錄會自動顯示。',
        devNote:
          '新增 GET /service-photos/bookings?bookingIds=a,b,c 批次端點（每筆預約回前 N 張 + 總數），結帳紀錄列表在父層一次撈完再分給各張卡片 —— 一頁十筆各自請求就是十個往返，翻頁還會再來一次。新增共用元件 ServicePhotoThumbs，支援列表模式（父層餵 photos）與單筆模式（自己打 API，顧客詳情的結帳 Modal 用這個），方案與權限自己判斷，沒開通 return null。'
      }
    ]
  },
  {
    version: '2026-09-14',
    title: '9/14 回訪提醒可以查發送紀錄 / 預約首頁看得到當天班別 / 深夜時段預設收起來',
    summary: '回訪提醒多了發送紀錄，查得到發給誰、誰沒收到；首頁的預約表也更好讀了。',
    sections: [
      {
        kind: 'feature',
        title: '新增「節慶／公告通知」，中秋禮、會員公告不會再漏掉人',
        description:
          '以前要發中秋禮、節慶祝賀、會員公告這種通知，只能借用「再回訪提醒」，把「距離上次到店」設成 1 天當作「不管多久沒來都發」。問題是回訪提醒的名單是從「已完成預約」長出來的，而且會自動排除「最近才回來過」的客人——這對「好久不見，該回來保養了」是對的，對公告卻完全不對。實際發生過：華榮店發鑽石會員中秋禮，三位鑽石會員沒收到，兩位是因為當天剛到店被當成「最近有來」剔除，一位是因為她的預約還停在未結案、系統認定她一筆已完成預約都沒有。而且畫面上完全看不出少了誰。現在提醒類型多了第三種「節慶／公告通知」：名單直接由會員等級或標籤決定，不看客人幾時來過，連一筆已完成預約都沒有的客人也發得到。預設每位客人只會收到一次，之後再按發送只會補給還沒收到的人，已經收過的不會被重複打擾——而且這個判斷連這個功能上線前發過的舊紀錄都算進去。另外預覽畫面現在會直接告訴您「這次會發給幾人、符合條件但不會發的有幾人、每一位是什麼原因」，不會再默默少人。',
        howToUseSteps: [
          {
            heading: '建立一則節慶公告',
            steps: [
              '左側選單「顧客 → 回訪提醒 → 新增提醒配置」',
              '「提醒類型」選「節慶／公告通知」',
              '在「篩選條件」挑會員等級或標籤，例如鑽石',
              '寫好通知內容後儲存，回到列表按「預覽」確認名單',
              '確認沒問題再按「發送」'
            ]
          },
          {
            heading: '把現有的公告設定改成新類型',
            steps: [
              '編輯原本那則設定，把「提醒類型」改成「節慶／公告通知」',
              '儲存後按「預覽」，已經收過的客人會列在「不會發」裡面並註明收過幾次',
              '按發送，只會補發給還沒收到的人'
            ]
          }
        ],
        devNote:
          'timing.type 新增 broadcast。getRecipientsByConfig 在客戶篩選之後直接分流，完全不進 bookingQuery，所以不受「已完成預約」與 revisit 的「近期有回來就不發」防呆影響——後者是這次漏發的直接原因（華榮店鑽石會員 daysSinceLastVisit=1，當天到店者被剔除）。去重改用 RevisitReminderLog：公告的收件人可能沒有任何預約可掛，Booking.revisitReminders 記不了。計次時兩份紀錄取聯集而不是相加，用 (bookingId, sentAt) 當同一次發送的身分——回填腳本把 Booking.revisitReminders 原封不動複製進 log，跑過回填的店家每一次歷史發送都同時存在兩邊，相加會把「收過 2 次」算成 4 次，整批會員被誤判成已收過。為了讓這個比對成立，sendRemindersToRecipients 裡兩份紀錄改成共用同一個 sentAt（原本是兩次各自的 new Date()，差幾毫秒）。broadcast 不吃全店頻率上限（店家自己按的一次性通知，被額度默默擋掉就是重演這次的事故），排程查詢加上 timing.type != broadcast，controller 另有 normalizeBroadcastConfig 把 autoSend.enabled 壓成 false 並補齊 schema 需要的欄位，避免從 revisit 改過來的設定殘留排程。預覽 API 對 broadcast 多回傳 excluded（已收過／沒有聯絡管道），前端直接列出來。{lastVisitDate} 變數在沒有已完成預約時改成輸出空字串，不再印出 Invalid Date。'
      },
      {
        kind: 'feature',
        title: '回訪提醒查得到發送紀錄了，也看得出誰沒收到',
        description:
          '以前發完回訪提醒，畫面上只會跳一個「成功幾筆、失敗幾筆」的數字，關掉就沒了。想知道上個月到底發給哪些客人、同一位客人被發過幾次、或是有沒有人根本沒收到，完全查不到。更麻煩的是，客人沒綁 LINE 又沒有 Email 的時候，系統只是默默跳過，連失敗都沒記，您看到的「成功 38 筆」其實不知道少了誰。現在「回訪提醒」頁上方多了「發送紀錄」分頁：每一位收件人一筆，看得到發送時間、客人姓名、是哪一個提醒設定發的、走 LINE 還是 Email、成功還是失敗，失敗的會寫出原因（例如「這位客人沒有綁定 LINE，也沒有可用的 Email」）。還能依提醒設定、日期區間、自動或手動、成功或失敗篩選，也可以直接搜客人姓名。上方三個數字分別是發送批次、成功送達與沒送到——「批次」是按下發送或排程執行的次數，同一次發給 30 個人算 1 次、30 人次。紀錄保留一年。',
        howToUseSteps: [
          {
            heading: '查某位客人被提醒過幾次',
            steps: [
              '左側選單「顧客 → 回訪提醒」',
              '上方切到「發送紀錄」分頁',
              '右邊的搜尋框輸入客人姓名後按 Enter'
            ]
          },
          {
            heading: '找出沒收到的人',
            steps: [
              '在「發送紀錄」把「全部結果」改成「失敗」',
              '「原因」欄會寫出為什麼沒送到，多半是客人沒綁 LINE 也沒留 Email',
              '請客人加 LINE 或補一個 Email，下次就收得到了'
            ]
          }
        ],
        devNote:
          '新增 RevisitReminderLog collection，一位收件人一次發送一筆，存設定名稱、類型與客人姓名的快照，設定改名或刪除後舊紀錄仍讀得懂。刻意不沿用 Booking.revisitReminders：那份是給冷卻期與次數上限判斷用的，只記成功也不記操作者，混在一起會讓補送邏輯被查詢需求綁住。失敗現在也寫紀錄，但不影響補送——補送判斷讀的還是 Booking.revisitReminders，失敗的下次照樣再試。sendRemindersToRecipients 多收 options（trigger、operator），手動發送由 controller 帶入操作者；同一次發送共用一個 batchId，摘要的「發送批次」是對 batchId 去重算出來的。寫紀錄用 insertMany 並包在 try 裡，寫失敗只記 log 不往外丟——訊息已經送到客人手上，這時候丟例外只會讓上層以為整批沒發成功而重發。sentAt 掛 TTL 索引 365 天自動清。查詢 API 是 GET /api/business_B/:businessId/revisit-reminder-logs，沿用既有的 requireSubscription(brand) 與 scopedProtectByBusinessId，沒有新增權限碼；路由刻意放在 revisit-reminder-configs/:configId 之外，否則會被當成 configId 吃掉。這支 controller 是檔尾一次列出 module.exports 的寫法，新 handler 已補進清單（漏掉會讓 Express 啟動就崩）。'
      },
      {
        kind: 'feature',
        title: '預約首頁直接看得到「今天誰上什麼班」',
        description:
          '以前要知道今天誰上早班、誰上晚班，得另外打開「班表」那一頁比對，回到預約表又忘了。而且首頁上方只寫得出「某某 休假 整日」，就算您在班表把假別分成特休、例休、事假，首頁也一律顯示「休假」兩個字。現在月、週、日三個檢視的日期上方，都會列出當天排到的上班班別，同一個班的人併成一行，例如「早三 Irene」「晚三 一敏」，左邊的色條跟您在班別設定挑的顏色一樣。假別也改成寫出您自己取的名稱——排「特休」就顯示特休，不會再全部變成「休假」。全店公休那天不會列班別，手機版班別超過兩個會收成「其他 N 個班別」，格子不會被塞爆。',
        howToUse:
          '不用做任何事。只要班表上有排班就會自動顯示；班別的顏色與名稱跟著「班表 → 班別設定」走，改了設定首頁立刻跟著變。',
        devNote:
          '首頁改讀月班表 API（GET /api/businesses/:id/schedule），依可見區間決定要抓哪幾個月，同一個月來回切檢視不重打。班別依 code 分組、顯示用的名稱與顏色取現行 shiftTypes（格子裡存的是排班當下的快照，店家改過設定後同一個班別會有新舊兩種顏色），時間則用格子的快照，因為那才是那天實際排的。休假標籤原本寫死「休假」，改成顯示 dayOff.name，但只採用 source=schedule 的那種：手動休假的 name 實際資料裡有「整日休假」「休假時段」這種通用字，也有「5/22」「已滿」這類隨手備註，印出來會變成「Irene 5/22 整日」。沒有班表檢視權限的角色會拿到 403，當作沒有班表資料處理，日曆照常運作。標籤字級與內距一併對齊預約卡片（0.8em / 1px 4px），週與日檢視的全天列改為置中——那一列是 row-reverse 的 flex，預設會整包貼右，但全天列沒有日期數字，靠右沒有意義。'
      },
      {
        kind: 'improvement',
        title: '週／日檢視不再從半夜開始，深夜時段預設收起來',
        description:
          '以前週檢視和日檢視的時間軸一律從 00:00 畫到 24:00。十點開門、七點打烊的店，等於每次進來都要先捲過十格空白才看得到第一筆預約，列印出來也有一大半是空的。現在時間軸預設只畫到營業時間的前後各一小時，深夜那段自動收起來。要強調的是，收起來的只是畫面，不會有任何單子被藏住：那天只要有預約、休假或排班落在收合範圍外，時間軸就會自動往外撐到蓋住它。如果您是二十四小時營業，或常態接深夜的單，可以在預約設定把完整 24 小時打開。',
        howToUseSteps: [
          {
            heading: '想改回顯示完整 24 小時',
            steps: [
              '左側選單點「設定 → 預約設定」',
              '把「首頁顯示完整 24 小時」打開',
              '按「保存」，回到首頁重新整理就會從 00:00 畫到 24:00'
            ]
          }
        ],
        devNote:
          'appointmentSettings 新增 showFullDayTimeline（布林，預設 false）。GET /api/business_B/:id/appointment-settings 一併回傳 timelineRange，由後端依 opening_hours 算出最早開店與最晚打烊、前後各加一小時並對齊整點；沒填或填得不成立的店家退回 08:00–23:00。前端拿 timelineRange 當底線，再依目前可見區間內的預約、休假事件與班表班別往外撐，最後才餵給 FullCalendar 的 slotMinTime/slotMaxTime——那兩個參數是直接把時間列砍掉，落在外面的事件會整張不見，所以自動撐開是必要的，不是加分項。scrollTime 同步夾住，否則只開下午的店會因為捲動起點落在時間軸外而看起來沒作用。這次只動 B 端首頁，App 端的預約畫面維持原狀。'
      },
      {
        kind: 'feature',
        title: '預約月曆滑過去就看得到客人資料與備註，不用一筆一筆點開',
        description:
          '以前想確認一筆預約是誰、有沒有特殊備註，只能點開視窗看，看完關掉再點下一筆。早上要把當天十幾筆預約過一遍，就得點開關掉十幾次，光是找「哪一位是上次說要換手法的那個」就要花好幾分鐘。現在滑鼠移到任何一筆預約上停一下，旁邊就會跳出預覽卡，不用點也不會蓋住底下的預約：上排是客人姓名與預約狀態，被您列入黑名單的客人會多一個紅色標記；接著是預約時間與預計結束時間、服務項目、服務人員、預約人數；再來是手機、生日、首次到店、最近到店與會員等級；最後是「顧客備註」與「這筆預約的備註」兩塊，兩者來源不同所以分開顯示——前者是客戶檔案上的長期備註（例如膚況、禁忌、偏好），後者是客人這次預約時填的。備註太長會收到五行，要看全文或要做任何操作，還是點開預約。月檢視、週檢視、日檢視三種都可以用。',
        howToUse:
          '不用做任何設定，滑鼠移到預約上停一下就會出現。手機與平板沒有滑鼠，維持原本「點一下開啟預約詳情」。',
        devNote:
          '走 FullCalendar 內建的 eventMouseEnter／eventMouseLeave，整張日曆只維護一張卡，不是每筆事件包一層 Popover（一個月幾百筆就是幾百個監聽器）。卡片設 pointer-events: none，否則滑過去的瞬間會蓋住底下的事件，變成點不到又一直閃。進場延遲 180 毫秒，避免滑鼠只是掃過就連續彈出。捲動與滾輪事件會強制關閉，不然卡片會停在原地指著一筆已經捲走的預約。位置靠近視窗右緣會翻到游標左側，上下也夾在可視範圍內。資料面：月曆那支 API 本來就為了生日在查 Customer，這次把 select 改成整包 businessSpecificData，多回一個 customerProfile（備註、標籤、黑名單、會員等級、首次與最近到店），沒有多一次查詢。首次到店取的是該顧客在這家店的建檔時間，資料搬遷匯入的舊客會是匯入日，不是真正的首次消費。'
      },
    ]
  },
  {
    version: '2026-09-10',
    title: '9/10 預約縮減流程減去手機驗證 / 結帳增加照片紀錄 / 顧客評價可回覆',
    summary:
      '一次更新多件重要事項！',
    sections: [
      {
        kind: 'feature',
        title: '顧客評價可以回覆了，客人寫評價也能附照片',
        description:
          '以前客人在您的店家頁留下評價，您只能看著，沒有任何回應的方法。被誤解了沒辦法解釋，被稱讚了也沒機會說一聲謝謝——對還在比較的潛在客人來說，一整排沒人回應的評價，看起來就像沒人在管這間店。現在左側選單多了「顧客評價」，您收到的每一則評價都集中在這裡：可以只看「未回覆」的、只看有照片的、或把低分的先挑出來處理，也可以用關鍵字搜尋。點「回覆這則評價」直接打字送出，回覆就會顯示在店家頁那則評價的正下方，標示為「店家回覆」，所有人都看得到。回覆之後還能再編輯或刪除。同一時間，客人那邊也一起升級了：寫評價時可以附上最多三張照片，別人看到覺得有參考價值可以按「有幫助」，不需要登入也能按。誰能回覆是分角色的——店主、店長、櫃檯可以回全店的評價；設計師只能回指名自己的那幾則，其他人的評價只看得到、不會出現回覆按鈕。',
        howToUseSteps: [
          {
            heading: '一、回覆一則評價',
            steps: [
              '左側選單點「顧客評價」',
              '上方切到「未回覆」，先把還沒處理的挑出來',
              '在想回的那則按「回覆這則評價」，打上內容後按「送出回覆」',
              '回覆會立刻顯示在店家頁該則評價下方，標示為「店家回覆」'
            ]
          },
          {
            heading: '二、修改或刪除已經送出的回覆',
            steps: [
              '找到那則評價，粉色的「店家回覆」區塊右上角有「編輯」與「刪除」',
              '編輯後重新送出即可覆蓋原本的內容',
              '刪除後客人端就看不到店家回覆了，該則評價會回到「未回覆」'
            ]
          },
          {
            heading: '三、回覆時可以怎麼寫',
            steps: [
              '好評：具體謝謝客人提到的那件事，比空泛的「謝謝支持」更有溫度',
              '客訴：先承接對方的感受，再說明您已經怎麼調整，不要在公開的回覆裡爭辯細節',
              '記得回覆是公開的——真正要處理的個案，回覆裡留一句「已私訊您」再私下聯繫'
            ]
          }
        ],
        devNote:
          '工程備註：Review model 新增 photos（陣列，上限 3，schema 與 API 兩層都擋）、reply（content / repliedAt / repliedBy）、helpfulCount 三個欄位。C 端：POST /api/user/review-photos 上傳照片（支援登入身分或評分信 token 兩種來源，走 compressImage + uploadToB2 存到 review_photos），write-review 收 photos；GET /api/business_C/:businessId/reviews 支援 page / limit / rating / hasPhoto / hasReply / sort，並多回一個 summary（總數、平均、環境／服務／價位平均、星級分佈、推薦率）；POST / DELETE /api/business_C/reviews/:reviewId/helpful 不需登入，同裝置只算一次是前端用 localStorage 控的。B 端：新增 business/controller/reviewController.js 與 GET /api/business_B/:businessId/reviews、PUT / DELETE /api/business_B/:businessId/reviews/:reviewId/reply。權限新增 review:view / review:manage / review:reply_own，reply_own 由 controller 比對 review.stylist 是否為當前使用者。不掛 requireSubscription，全方案開放。'
      },
      {
        kind: 'feature',
        title: '預約單會標出「手機未驗證」的客人，客人端則少一道簡訊關卡',
        description:
          '以前只要不是用手機號碼註冊的客人（也就是絕大多數用 LINE 登入的人），在按下「送出預約」的那一刻，一律會被要求收一封簡訊、輸入六位數驗證碼。那一關擋掉的不只是可疑的人，更多是本來要跟您約的客人——他當下手機不在手邊、收不到簡訊、或單純覺得麻煩，就這樣關掉頁面了。現在改成「該驗才驗」：需要訂金或線上付款的單一定要驗；已經加過您官方帳號的客人、在您店裡消費過的老客、用手機號碼註冊的會員，就直接讓他約完。剩下沒有經過簡訊驗證的那些單，不會偷偷放過去——預約詳情的電話旁邊會多一個橘色的「手機未驗證」標記（手機 App 上顯示為「未驗證」），代表這支號碼是客人自己填寫的、系統沒有替您確認過。看到標記不代表這位客人有問題，只是提醒您：如果是第一次來的客人、又是比較長時間的服務，值得先撥一通或傳個訊息確認再排進去。如果您的店就是希望每一位客人都必須通過簡訊驗證，也可以整店打開嚴格模式（這個開關目前需要聯繫我們協助開啟）。',
        howToUseSteps: [
          {
            heading: '在哪裡看到標記',
            steps: [
              '到「預約」點進任何一筆預約的詳情頁',
              '看「聯絡電話」那一列，號碼右邊如果有橘色的「手機未驗證」，代表這筆沒有經過簡訊驗證',
              '把游標移到標記上會出現說明；手機 App 的預約詳情同樣看得到'
            ]
          },
          {
            heading: '什麼情況下會出現',
            steps: [
              '客人用 LINE 或 Google 登入，而且沒有加過您的官方帳號、也還沒在您店裡消費過',
              '這筆預約不需要訂金、也不是線上付款',
              '反過來說：用手機號碼註冊的會員、驗證過手機的客人、需要訂金的單，都不會出現這個標記'
            ]
          },
          {
            heading: '看到標記可以做什麼',
            steps: [
              '第一次來的客人：到店前一天先用電話或訊息確認一次',
              '長時間或高單價的服務：確認後再把時段完全鎖給他',
              '如果對方完全聯絡不上，可以直接在預約詳情取消，時段就釋放出來了'
            ]
          }
        ],
        devNote:
          '客人端把「一律要求簡訊驗證」改成分級驗證，判斷邏輯集中在前台的 needsPhoneOtp：手機註冊或已驗證過手機一律放行；店家嚴格模式（Business.requirePhoneVerify，預設關閉、目前後台還沒有開關 UI）優先於其他放行條件；已綁定本店官方帳號、本店有消費紀錄則放行；需訂金或非現金付款則要求驗證。Booking 新增 phoneVerified 欄位，在建立預約當下寫入快照，不隨帳號日後補驗而變動——之後要回答「未驗證手機的單 no-show 率是不是真的比較高」，必須知道當時的狀態。舊單與店家自建的單沒有這個欄位，值是 undefined，B 端與 App 端都只在明確等於 false 時才顯示標記，避免把當時其實驗證過的客人誤標。B 端預約詳情的回傳物件是手挑欄位組出來的，phoneVerified 有另外補進去；列表 API 是整份展開回傳，不需要改。這次沒有新增權限碼與方案門檻：標記顯示在既有的預約詳情裡，看得到預約的人就看得到標記。',
        shopHidden: false
      },
      {
        kind: 'feature',
        title: '施作照片可以留在預約紀錄裡，客人自己看得到',
        description:
          '以前一次服務做完，成果照只存在設計師的手機或相簿裡。客人隔了兩個月想回頭確認「上次做的是哪個顏色」，只能翻 LINE 對話；想介紹朋友來，也拿不出當時的照片。現在結帳的時候，可以直接在備註下方把這次的照片傳上去，客人打開自己的預約紀錄，那筆預約就會多一個「施作照片」可以點開看。結帳當下太忙沒傳也沒關係，事後打開那筆預約的詳情頁隨時能補，補上去客人那邊立刻就看得到。每一張照片旁邊都有「客人可見」的開關，預設是開的；如果拍的是膚況特寫、或是留著自己存證用的照片，把那一張的開關關掉，客人就看不到，但店家這邊還在。一筆預約最多 20 張。要特別說明的是：這個功能只把「照片」給客人看，預約備註和結帳備註完全不受影響，客人一樣看不到。',
        howToUseSteps: [
          {
            heading: '結帳時直接傳',
            steps: [
              '照平常的流程結帳，捲到「選擇日期和時間」最下面的「施作照片」',
              '點「選擇照片」，可以一次選多張',
              '按「完成訂單」，照片會在訂單建立後自動上傳'
            ]
          },
          {
            heading: '結帳後補傳或修改',
            steps: [
              '到「預約」找到那筆預約，點進詳情頁',
              '捲到最下面的「施作照片」，點右上「上傳照片」',
              '不想給客人看的那張，把它下面的「客人可見」關掉',
              '傳錯的照片點垃圾桶圖示刪除，客人那邊也會同步消失'
            ]
          },
          {
            heading: '客人怎麼看',
            steps: [
              '客人登入後到「預約管理」',
              '該筆預約的卡片上會出現「施作照片」按鈕',
              '點開就是相簿，可以放大單張檢視'
            ]
          }
        ],
        devNote:
          '照片另存一個 collection（ServicePhoto），沒有掛在預約文件底下。原因是客人端取預約的兩支 API 目前沒有欄位白名單、是把整份預約文件展開回傳的，照片如果存在預約上，標記為不給客人看的那幾張會跟著噴出去，可見性開關等於形同虛設；分開存，客人端就只查得到該看的那幾筆。店家端與客人端是兩支不同的查詢：店家端回全部並帶上傳者，客人端只回開放可見的那幾筆、而且只挑圖片網址與說明文字，上傳者與內部排序不外流。方案門檻是高階方案，權限碼 servicePhoto:view 與 servicePhoto:manage，預設開給店長、設計師、櫃檯——設計師是實際拍照的人，這個角色在過去的模組上線最常被漏掉。照片上傳時同時產出原圖（長邊 1600）與縮圖（長邊 500），相簿格線只載縮圖，避免一次拉十幾張全尺寸圖。一律轉成 JPEG，客人用 iPhone 直傳的 HEIC 瀏覽器不一定吃得動。刪除是軟刪除、B2 檔案保留，照片有可能是日後糾紛的依據。結帳頁的照片刻意不在選檔當下上傳：現場客的預約是在送出訂單那一刻才建立的，那時還沒有預約 ID 可以掛，所以先留在畫面上，等訂單建立、拿到預約 ID 之後才批次送出；上傳失敗只跳提示，不影響訂單本身的成敗判斷——訂單是錢，照片不是。客人端列表用一次 aggregate 統計每筆預約的可見照片張數，沒有逐筆 countDocuments，那是每次開頁都會打的查詢。B 端 permission.json、B 端與 App 端的 useMembership、方案資訊頁都已同步。'
      },
      {
        kind: 'feature',
        title: '一般員工可以自己排自己今天以後的班',
        description:
          '以前班表的編輯權限只給店主、店長、櫃檯與設計師，職稱是「一般員工」的人打開班表只有唯讀，畫面下方還會寫「只有店長或櫃檯可以編輯班表」。想換班、想把某天標成事假，都得傳訊息請主管幫忙填，主管人不在就只能等。現在一般員工可以直接排自己的班，但只限「自己」和「今天以後」：別人的格子點不動，已經過去的日期也鎖起來——那些天已經算進工時與薪資，不該事後被改。班別設定（早班幾點到幾點）與「複製上月班表」仍然只有主管能動，員工只能從主管建好的班別裡挑。主管端完全不用改設定，這個權限預設就會生效；如果某位員工不希望他自己排，到「員工管理 → 權限」把他的「編輯自己的班表」關掉即可。',
        howToUseSteps: [
          {
            heading: '員工怎麼排自己的班',
            steps: [
              '打開「班表」，會看到自己那一列已經可以點了',
              '選好上面的班別（或「清除」），在自己今天以後的格子點一下就填入',
              '點自己的名字可以一次填滿接下來整個月',
              '按「儲存」；手機或 App 上直接點日期挑班別，挑完就存好了'
            ]
          },
          {
            heading: '主管想收回這個權限',
            steps: [
              '到「員工管理」，點該位員工的「權限」',
              '在「班表」區塊把「編輯自己的班表」取消勾選並儲存',
              '之後他打開班表就會恢復成唯讀'
            ]
          }
        ],
        devNote:
          '新增 schedule:manage_own 權限，預設掛在 employee 角色上。刻意不沿用 schedule:manage —— 權限比對的萬用字元只認「模組:*」，manage_own 不會被自動放大成 manage，兩者可以並存。後端路由把儲存月班表改成接受 manage 或 manage_own，真正的限制寫在儲存班表的處理裡：沒有 manage 的人，送上來的每一格都必須是自己、而且日期不得早於台北時間的今天，違反就整批擋下回 403 並附上原因，不會只存一半。班別範本、同步未來班別、複製上月三支仍然只認 manage。B 端與 App 端都先在畫面上擋一次，避免使用者白畫一輪才被退回：B 端的整列／整欄快速填改成逐格檢查權限，只會填到自己有權限的格子；App 端把原本單一的「可編輯」旗標拆成「全店」與「只能自己」兩種，權限查詢失敗時兩者都放行，最壞情況是跳一個後端回傳的提示，比店主明明有權限卻只能看要好。B 端 permission.json 已同步，不同步的話員工權限設定頁會看不到這個項目。'
      },
      {
        kind: 'feature',
        title: '刷卡、行動支付可以向客人加收服務費，金額系統自動算',
        description:
          '以前結帳的金流手續費一律是店家的成本，報表會從收入扣掉，實際入帳永遠比帳面少一截。現在到「設定 → 金流手續費」可以打開「向客人加收服務費」，之後結帳輸入刷卡金額時，系統會自動算出要加收多少，並在畫面上直接顯示「客人實付」——刷卡機照那個數字刷就好，不用自己心算。計算方式有兩種。預設的「補足銀行手續費」會收到「扣掉手續費後剛好等於應收」為止——因為銀行是對客人實際刷的總額抽成，單純把應收乘上 2.8% 其實還是會少收（1,000 元的單少 0.78 元，金額越大差越多，超過 1,276 元就一定補不回來）。如果希望客人自己乘費率就對得上，也可以選「單純把應收乘上費率」，差額由店家吸收。顯示名稱預設是「服務費」，可以自己改，這個名稱會同時出現在結帳畫面、收據和客人的消費紀錄上。另外，這筆錢不算店家的營業收入，報表不會虛增，設計師的業績也不會被灌水；原本設定成「抽成要扣手續費」的店家，加收之後設計師不會再被扣一次。刷卡與行動支付可以分開決定要不要收，預設兩個都是關閉的。',
        howToUseSteps: [
          {
            heading: '開啟功能',
            steps: [
              '到「設定 → 金流手續費」（只有店主與店長看得到這個頁面）',
              '確認上方的刷卡、行動支付費率跟您的收單行合約一致',
              '打開「向客人加收服務費」，選擇要對刷卡、行動支付哪幾種收',
              '選計算方式（建議用「補足銀行手續費」）與顯示名稱（預設「服務費」）',
              '畫面上會用 1,000 元試算，直接顯示客人實付與店家實入多少，確認後按儲存'
            ]
          },
          {
            heading: '結帳時怎麼用',
            steps: [
              '刷卡欄位照舊填「這筆要刷多少貨款」，不用自己加服務費',
              '下方的對帳提示條會顯示服務費金額與「客人實付」',
              '刷卡機刷「客人實付」那個數字',
              '客人在自己的預約紀錄裡會看到這筆服務費單獨列一行'
            ]
          }
        ],
        devNote:
          '結帳訂單的金額結構新增「加收服務費」欄位與顯示名稱快照，服務費含在應收裡但單獨記一筆。商家設定新增負擔方、計算方式（補足銀行手續費／單純乘費率）、進位方式、顯示名稱，以及刷卡與行動支付各自的開關；預設全部關閉，且負擔方為店家時兩個開關自動失效，設定讀不到時絕不會擅自加收。計算公式獨立成一支共用模組，後端、B 端、App 端各一份複本，驗證腳本會逐字比對三份是否同步——一旦漂移，結帳頁與後端會算出不同金額且靜默發生。前端刻意不把服務費放進表單欄位：結帳頁的自動配款與溢收防護都建立在「付款欄位總和等於應收」上，服務費改為送出時才併進刷卡金額與應收，並附上未含服務費的計算基數供後端用同一公式重算驗證，不符則擋下建單。抽成方面，業績基數的兩支函式改為先扣掉服務費，否則縮放比例會大於 1 把設計師業績灌水；「抽成扣手續費」規則的分攤基數改用「毛手續費減去已向客人加收的部分」，避免客人付了一次、設計師又被扣一次。營收報表三處把應收當營業收入的地方同步扣除。權限新增「金流手續費與服務費設定」，預設只有店主與店長；讀取費率刻意不設限，否則設計師會結不了帳。結帳紀錄頁、App 訂單詳情、收據與 CSV 匯出都補上這筆的顯示。舊訂單的服務費一律為 0，五千筆真實訂單回歸驗證確認業績基數與改動前完全相同。'
      }
    ]
  },
  {
    version: '2026-09-09',
    title: '9/9 預約時長可以為個別客人調整，會員等級到期前會自動提醒客人',
    summary:
      '這次兩件事。第一件是預約的服務時長終於可以改了：以前時長固定等於服務項目的預設時長加總，同一個服務遇到髮量多、狀況比較複雜的客人，明明要多做半小時，行事曆上還是照原本的時間畫，後面的單就被壓到。現在編輯預約時可以勾「自訂時長」直接填分鐘數，行事曆版位與撞單判斷都會跟著改，而且之後再加購服務也不會被蓋掉。第二件是會員等級到期提醒：以前會籍到期是系統當天默默降等，客人完全不知情，等到下次來店發現折扣沒了才問櫃檯；現在可以設定到期前幾天主動通知客人，內容自己寫，還能帶出「還差多少可以續等」。',
    sections: [
      {
        kind: 'feature',
        title: '預約的服務時長可以為個別客人自訂',
        description:
          '以前服務時長是唯讀的，永遠等於服務項目的預設時長加總。可是同樣是護髮，有些客人髮量多要多留 30 分鐘，有些客人只補根不用那麼久 —— 時長不能改，行事曆的版位就是錯的，後面的預約會被排太滿或空出一段沒人用的時間。現在編輯預約時可以勾「自訂時長」自己填分鐘數，新增預約時也可以先指定。自訂過的預約會標上「自訂」，之後再幫客人加購服務也不會被自動加總蓋掉；想改回自動，取消勾選就好。',
        howToUseSteps: [
          {
            heading: '幫既有預約調整',
            steps: [
              '進入預約詳情，按「編輯」',
              '在「服務時長」勾選「自訂時長」，填入實際需要的分鐘數',
              '儲存後行事曆的版位長度、時段衝突判斷都會依這個時長計算'
            ]
          },
          {
            heading: '新增預約時就指定',
            steps: [
              '選好服務項目後，在「服務時長」打開「自訂這筆預約的時長」',
              '填入分鐘數再送出',
              'App 端的新增預約與預約詳情同樣支援'
            ]
          }
        ],
        devNote:
          'Booking schema 加 durationMode（auto｜manual，預設 auto）。後端 createBooking／updateBooking 收 duration + durationMode，manual 時以店家輸入為準並驗證 5～1440 分鐘，服務項目異動的加總邏輯改成只在 auto 模式下覆蓋 duration；要回自動必須明確送 durationMode: auto（此時以現有 services 快照回推）。結帳流程（checkoutController 的服務加總重算）同樣跳過 manual，避免結帳把自訂時長洗掉；起訖時間換算的 resolveBookingDuration 維持原行為，因為那是實際施作時間。⚠️ getBookingById 是手動挑欄位組回傳物件的，durationMode 必須一起帶出來，否則前端一進編輯模式會被當成 auto 並在儲存時把自訂值洗掉。B 端 BookingDetail 與 Appointments 新增預約表單、App 端 AppointmentDetailScreen 與 AddAppointmentScreen 皆已接上；App 端檢視模式原本是用服務項目重算顯示，一併改為讀 booking.duration。'
      },
      {
        kind: 'feature',
        title: '會員等級到期前自動提醒客人，文案自己寫',
        description:
          '會籍到期本來就會自動判斷續等或降等，但整個過程客人不知道、店家也沒收到提示，客人往往是下次結帳發現折扣不見了才知道自己掉等。現在可以設定「到期前幾天通知客人」，訊息內容完全由您決定，還能插入客人姓名、等級、到期日、還差多少金額等變數，讓客人清楚知道再消費多少就能保住等級。系統只會通知還沒達到續等門檻的人 —— 已經達標的客人到期會自動續等，不需要被打擾。',
        howToUseSteps: [
          {
            steps: [
              '到「會員等級設置」頁面最下方的「會員到期提醒」，打開開關',
              '設定提前幾天發送（例如 30 天）',
              '寫通知標題與內容，點下方的中文變數標籤就能插入，例如 {客戶姓名}、{還差金額}',
              '可以先按「發送測試訊息給自己」，用範例資料寄一封到您的信箱確認效果',
              '確認沒問題後按「儲存設定」，之後每天早上系統會自動發送'
            ]
          }
        ],
        devNote:
          'Business schema 加 membershipExpiryReminder（enabled／daysBefore／message.title・content・showBookingButton・buttonLabel／lastSentAt／lastSentCount）。文案變數與預設值集中在 shared/constants/membershipExpiryReminder.js，後端排程、設定 API 與前端「還原預設文案」共用同一份；變數對店家一律顯示中文（{客戶姓名}），英文寫法保留為 alias 以相容舊文案。排程 runMembershipTierExpiryReminderTask 掛在 worker 的每日 10:30（避開 10:00 的堂票提醒），只撈 tierExpiresAt 落在「今天 + daysBefore」當日的客戶，並跳過 tierValidityDays = 0、renewAmount = 0 與已達標者（這三種到期都會自動續等，通知沒有意義）。發送沿用回訪提醒的 buildLineFlexMessage 與 sendRevisitReminderEmail，LINE 優先、Email 備援，Redis key membershipTierExpiry:{businessId}:{customerId}:{到期日} 防同一會籍週期重複發（TTL 45 天）。設定 API 掛在 /api/business_B/:businessId/membership-expiry-reminder，讀取所有方案皆可、修改與測試發送限商務／品牌方案。'
      }
    ]
  },
  {
    version: '2026-09-07',
    title: '9/7 結帳可以直接使用堂票，同意書能限定適用的堂票',
    summary:
      '這次三件事都圍繞著堂票。第一件是結帳可以直接使用堂票核銷：以前客人拿堂票來做服務，只能到「堂票紀錄」按扣張，那筆預約永遠停在未結帳，結帳紀錄與消費歷程也看不到；現在結帳頁會自動帶出這位客人還能用的堂票，選一下就核銷，扣張與結帳一次完成。第二件是買堂票時可以直接扣掉第一堂 —— 客人常常今天買課、今天就做第一次，不用再跑第二趟操作。第三件是購買同意書可以限定「買哪一種堂票」才發送，只有需要簽權益聲明的療程才會跳出來，不會每買一張小堂票都要點掉一次。',
    sections: [
      {
        kind: 'feature',
        title: '結帳買堂票時可以直接扣掉第一堂',
        description:
          '客人常常是「今天買課、今天就做第一次」。以前要先結帳把堂票賣出去，再到堂票紀錄找到那張票按一次扣張，兩趟操作很容易漏掉，剩餘堂數就對不起來了。現在結帳的堂票購買列多了一欄「本次核銷」，填幾堂就在結帳完成時一起扣掉，扣張紀錄也會掛在這張結帳單上。只扣堂數、不影響金額 —— 這幾堂的錢本來就含在堂票售價裡。',
        howToUseSteps: [
          {
            steps: [
              '結帳頁「堂票購買」加一列，選好範本與數量',
              '在「本次核銷」填今天要用掉幾堂（不用就留 0）',
              '完成結帳，堂票的剩餘堂數會直接扣好，不用再去堂票紀錄操作'
            ]
          }
        ],
        devNote:
          'coursePurchaseSchema 新增 redeemCount（預設 0）。結帳流程分兩段：驗證階段（checkoutController 的 purchaseRedeemPlan）確認範本屬於本店、把堂數夾到 tpl.totalCount 以內，並找出對應的服務只為了讓紀錄看得出用在哪個服務；真正的扣張排在發放之後（票要先存在才扣得動），與「使用既有堂票」共用 order.classTicketUsages，結帳紀錄裡買和用會列在同一處。同一種堂票買多張時只從第一張扣（firstTicketByTemplate）。⚠️ 與「使用既有堂票」不同，這條路徑刻意不把服務金額歸零：應收一律以前端送來的 paymentData.receivable 為準（後端不重算），後端若自作主張改某筆服務為 0，明細加總就會跟實收對不起來 —— 店家在結帳畫面看到多少就是收多少。扣張失敗只記 log 不擋單：票已經發出去了，客人不會少東西，店家事後補扣即可。B 端 TicketRow 加 .ct-redeem 欄（手機版 grid 另開一列），App 端在堂票品項下多一排加減鈕。'
      },
      {
        kind: 'improvement',
        title: '購買同意書可以限定「買哪一種堂票」才發送',
        description:
          '以前只要同意書勾了「發放堂票時」，客人買任何一種堂票都會跳出來簽。但實際上通常只有一兩種高單價療程需要簽權益聲明，買小堂票也跳同意書，櫃檯每次都要點掉，久了就變成沒人看的雜訊。現在設定同意書時可以指定「這份只適用哪幾種堂票」，不選就維持原本的所有堂票都要簽。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「同意書設定」，編輯一份用途為「購買同意書」的範本',
              '「適用購買行為」勾選「發放堂票時」後，下方會出現「限定堂票」',
              '選擇這份同意書要對應的堂票（可多選），留白代表所有堂票都要簽',
              '存檔後，結帳購買與後台手動發放兩邊都會依此判斷'
            ]
          }
        ],
        devNote:
          'ConsentFormTemplate 新增 applicableClassTicketTemplateIds（ref ClassTicketTemplate），空陣列 = 不限，沿用 applicableServiceIds 的慣例，既有範本行為不變。findPurchaseTemplates(businessId, target, opts) 加第三個參數 opts.classTicketTemplateIds：有帶時只回「不限堂票」或「限定範圍涵蓋其中至少一種」的範本；不帶時退回舊行為，設定頁預覽仍看得到全部。三個進入點都帶上了本次購買的堂票範本 ID：結帳自動發送（checkoutController 的 issuedTicketsForConsent 多存 templateId，去重後傳入 createAndSendForClassTicketPurchase）、B 端後台手動發放（ClassTicketRecordsPage 的 setConsent 帶 values.templateId）、App 手動發放（IssueClassTicketScreen 帶 templateId）。結帳完成後的提醒 Modal 因為一張單可能同時買好幾種堂票，query 參數 classTicketTemplateId 允許逗號串多個 ID，後端自行 split。B 端設定頁的「限定堂票」欄位以 Form.useWatch 監看 purchaseTargets 決定是否顯示，該 useWatch 必須寫在元件頂層 —— 放在 JSX 的條件區塊裡會變成有時呼叫有時不呼叫，違反 hooks 規則。'
      },
      {
        kind: 'feature',
        title: '結帳可以直接使用堂票（核銷）了',
        description:
          '以前客人拿堂票來做服務，只能到「堂票紀錄」按扣張 —— 票是扣掉了，但那筆預約永遠停在「未結帳」，結帳紀錄與客人的消費歷程也看不到這一堂做過，月底對帳只好翻兩個地方。現在結帳頁會自動帶出這位客人「還有剩、還沒過期、而且適用這個服務」的堂票，選一下就核銷，該服務金額歸零、扣張與結帳一次完成，預約也會正常轉成已結帳。堂票的收入在你賣票的那天就已經算過了，所以核銷不會再被算一次收入，報表數字不受影響。App 端也同步了，而且順手修掉一個問題：App 原本的「使用堂票」只扣張、服務金額照收，等於同一堂收兩次錢，現在跟電腦版一樣會把金額歸零。',
        howToUseSteps: [
          {
            steps: [
              '照平常的方式進入結帳（從預約點進去，或現場結帳選好客人與服務）',
              '選擇服務的區塊往下找到「使用堂票（核銷）」——這位客人沒有可用的票時不會出現',
              '在該服務下方選擇要用哪一張堂票，會顯示票號、剩幾堂、什麼時候到期',
              '選好之後該服務金額變成 0，照常完成結帳即可'
            ]
          }
        ],
        devNote:
          '原本只有「堂票購買」（結帳時賣票，courses.purchases → 自動發 ClassTicket），沒有折抵；ClassTicketUsage 的 checkoutRef 與 reason:\'checkout\' 從一開始就標著 Phase 2 預留，一直沒接上。全站當時的狀態是 624 筆扣張紀錄、連到預約或結帳單的 0 筆。CheckoutOrder 新增 classTicketUsages（ticketId / ticketCode / templateName / count / remainingAfter / serviceId / serviceName / deductedAmount / usageRef），source 列舉新增 class_ticket_redeem —— 整張單只有堂票折抵、沒有商品也沒有實收時才標這個值，混單仍算一般櫃檯結帳，店家在結帳紀錄才分得出哪些單真的有收錢。金額口徑：被折抵的服務 price 與 revenueAmount 一律 0，originalPrice 保留原價供顯示。收入不重複認列是因為 ReportController 用 ClassTicket.issuedAt + price 在售票當天就認列過了；業績與抽成也不重算，維持「業績歸當初賣票的人」（ClassTicketCommission 於售票時發放），這是與店家確認過的口徑。扣張刻意排在 order.save() 之後（先扣張後建單，建單失敗客人就白少一張），且用條件更新 remainingCount >= count 的 findOneAndUpdate，兩個櫃檯同時結同一張票時後到的那筆會 matchedCount=0 而不是把剩餘扣成負數。後端於 createOrder 驗證票的歸屬（同一位客人）、狀態、效期、剩餘張數，以及「折抵的服務真的在這張單上」與範本的適用服務範圍。listTicketsByCustomer 加 ?usableOnly=1&serviceId= 供結帳頁取用，客戶資料頁不帶參數仍看全部。前端折抵的做法是把該筆服務的 price 直接設成 0，讓小計、應收、業績分配沿用既有邏輯，不用在十幾處各補一次判斷；prevPrice 留著供取消折抵還原。另新增 scripts/backfillClassTicketRedeemOrder.js 補建「已扣張但沒有結帳單」的單筆紀錄（--booking= 試跑、加 --apply 寫入），刻意不會替人扣張，找不到對應的 redeem 就中止。App 端（BeautyAppJs）原本在 createOrder 之後另外呼叫一次 redeem API —— 票扣掉了但那筆扣張與訂單無關（reason 為 manual、沒有 checkoutRef），結帳紀錄與消費歷程都看不到，且服務金額照收等於重複收費；現在改為 buildOrderPayload 帶 classTicketUsages 與訂單一起送出，並在 payload 組裝的最前面把被折抵的服務 price 歸零，後續小計、應收、業績分攤全部沿用既有邏輯。票對應到哪個服務由 CheckoutScreen 的 ticketRedemptionPayload 決定：範本沒限定服務視為不限，取第一個尚未被別張票佔用的服務。CheckoutOrderDetailScreen 也加上「使用堂票（核銷）」區塊。店家面向的用詞一律是「使用堂票（核銷）」，結帳紀錄的來源篩選因欄位窄用「堂票核銷」；程式內部的識別字（class_ticket_redeem、classTicketUsages）維持不變，不隨文案調整。'
      }
    ]
  },
  {
    version: '2026-09-06',
    title: '9/6 同意書可以出問卷了，預約日曆的日／週看得到設計師',
    summary:
      '同意書多了「問卷題目」：以前只能請客人逐項打勾同意，現在可以直接在同意書裡問問題 —— 有沒有在服用的藥物、胸部是否動過手術、對什麼會過敏，單選、多選、簡答都可以，還能設成必填。客人的回答會跟著同意書一起存證，也會出現在客戶資料頁，施作前不用再翻紙本。第二件是預約日曆的「日」和「週」畫面：以前每一筆預約擠成一行、超過就被截掉，現在會依時間長度自動展開，寫出客人、服務項目和設計師，客人選「不指定人員」的單子會標橘色「待指派」。另外預約設定多了一個「一鍵暫停線上預約」的開關，公休或出國時可以先把線上預約關起來，回來再打開。',
    sections: [
      {
        kind: 'improvement',
        title: '行前提醒會帶上你的「預約須知」了',
        description:
          '客人預約的前一天，系統會自動發一則提醒（LINE 或 Email），原本只有日期、時間、項目、人數。現在會一併帶上你在「設定 → 預約須知」填的內容 —— 遲到規則、怎麼上樓、要不要提前到、能不能帶同行者，這些真正需要客人在出門前看到的事，終於有地方講了。不用另外設定，填了就會出現。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「設定」→「預約須知」',
              '寫下你希望客人出門前知道的事（例如遲到規則、上樓方式）',
              '存檔後，隔天起的行前提醒就會帶上這段內容'
            ]
          }
        ],
        devNote:
          '起因是實際看到有店家為了補這個洞，去建了一個「回訪提醒」設定並貼上行前須知的文字 —— 但回訪提醒是發給「很久沒來的客人」，方向相反，那個設定不會有作用（且其 isEnabled 為 false、已發 0 封）。改法：runBookingReminderTask 的 payload 帶入 business.bookingInstructions，lineMessaging.sendBookingReminder 的 Flex 在「此為自動發送」之前插入須知區塊（separator + 標題 + wrap 文字，截 300 字避免泡泡過長被 LINE 截斷或整則失敗），emailService.sendBookingReminderEmail 在按鈕列之前插入同樣內容（截 500 字、white-space: pre-wrap 保留換行）。註冊時預設塞的樣板「請提前預約，預約時請提供您的姓名和聯繫方式。」以 resolveBookingInstructions 濾掉 —— 全站 107 家有填須知的商家裡有 75 家從沒改過那句，而客人收到提醒時早就約好了，再叫他「請提前預約」很怪。實際會帶入的是 32 家自訂過的商家。'
      },
      {
        kind: 'improvement',
        title: '回訪提醒不會再一直重複發了',
        description:
          '再回訪提醒的條件是「距離上次到店滿 N 天」，只要客人一直沒回來，這個條件就永遠成立 —— 原本的「最小間隔天數」只是冷卻期，實際效果會變成每隔一段時間就提醒一次，沒有盡頭。現在每個提醒設定多了「最多提醒幾次」：發到指定次數就停，填 1 就是只提醒一次。另外多了一道全店的發送上限，不論你開了幾個提醒設定，同一位客人在指定天數內最多只會收到指定封數，避免回訪、生日、術後關心同時湧到同一個人身上。生日提醒不受這兩個限制 —— 一年一次的祝賀不該被行銷訊息的額度擠掉。',
        howToUseSteps: [
          {
            heading: '單一設定的次數上限',
            steps: [
              '左側選單「回訪提醒」點進要調整的設定',
              '找到「最多提醒幾次」，填 1 = 只提醒一次，留空或 0 = 不限',
              '「兩次提醒的最小間隔天數」維持原本的意思：提醒過之後要隔多久才會再提醒'
            ]
          },
          {
            heading: '全店發送上限',
            steps: [
              '「回訪提醒」列表頁最上方的「發送上限」',
              '預設是同一位客人 30 天內最多收到 3 封，可自行調整或關閉'
            ]
          }
        ],
        devNote:
          'RevisitReminderConfig.timing 新增 maxReminders（預設 2，0 = 不限）。revisitReminderService 的 bookingQuery 除既有的 reminderInterval 冷卻期外，加一條 $expr 條件：該預約的 revisitReminders 中 configId 相符的筆數 < maxReminders 才納入 —— 冷卻期只管「多久後可以再發」，管不住「總共發幾次」，revisit 的日期條件是開放式的（完成日 ≤ 今天 − N 天），沒有次數上限就是無限循環。Business 新增 revisitReminderSettings（globalCapEnabled / globalCapCount 3 / globalCapDays 30），新增 applyGlobalCap(businessId, config, recipients)：以 aggregate 跨預約累加同一位客人在期間內收到的封數，達上限即從名單移除；getRecipientsByConfig 的兩個 return 點都套用。filters.includeBirthdayCustomers 為 true 時兩種限制都跳過。新增 GET/PUT /api/business_B/:businessId/revisit-reminder-settings（brand 方案）。遷移用 scripts/backfillRevisitMaxReminders.js：既有 13 個設定中，revisit 類型設 2 次、followup 設 1 次（自動模式是單日切片，天然只發一次）、生日提醒維持不限，已執行。'
      },
      {
        kind: 'improvement',
        title: '刷卡收的定金，紀錄上看得出來了',
        description:
          '以前定金只看得到「已付」兩個字，是刷卡、現金還是匯款完全分不出來 —— 但刷卡要被收單行扣手續費、現金是直接進抽屜，對帳時是兩回事。現在預約紀錄的卡片上會直接顯示「定金 $500・刷卡」，刷卡的用不同顏色標示，滑鼠移上去還看得到手續費金額與費率；結帳紀錄的付款明細也會寫成「定金（刷卡）」，並在金流手續費下面標出其中有多少是定金刷卡產生的。',
        howToUse:
          '不用做任何事。定金收款方式在預約詳情的「定金收款方式」設定，之後就會一路顯示到結帳紀錄。',
        devNote:
          'Booking.depositPayment（method / source / feeRate / fee / cardLastFour）本來就存在，缺的是結帳單的快照與兩處列表的顯示。CheckoutOrder.payment 新增 depositMethod 與 depositFee：結帳時從 booking.depositPayment 帶入，只有這張單真的抵扣到定金（paymentData.deposit > 0）才記，避免沒抵定金卻標著「定金：刷卡」誤導對帳；depositFee 只在該筆手續費真的被這張單認列時才寫入（沿用既有的 feeSettledOrderId 防重複），重新結帳不會重複顯示。存快照而不是查 booking，是因為結帳單是存證、預約日後可能被改或刪。前端：BookingTable 的卡片在 depositStatus === "paid" 時多一顆定金徽章（刷卡用紫色系與現金區分，title 顯示手續費與費率），CheckoutRecordsPage 的付款明細把「定金」改成「定金（刷卡）」、手續費下方加一列「其中定金刷卡手續費」，匯出的付款摘要同步帶上方式。另新增 scripts/seedDemoCardDepositBooking.js 在示範店家建立一筆刷卡定金預約供驗證（--apply 寫入、--remove 清除）。'
      },
      {
        kind: 'feature',
        title: '一鍵暫停線上預約，公休出國先關起來',
        description:
          '以前要暫時不收線上預約，只能把服務一個一個下架，或把營業時間清空，回來還要一項一項改回去。現在「設定 → 預約設定」最上面有一個開關，關掉之後客人在網站與 LINE 上都送不出新的預約，你的店家頁面仍然看得到，已經成立的預約也完全不受影響，隨時可以再打開。還可以填一句原因（例如「7/1–7/10 出國進修」），客人點進預約頁時就會看到這句話，不會以為店收了。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「設定」→「預約設定」',
              '最上面「線上預約」把「接受線上預約」關掉',
              '需要的話填一句暫停原因，客人會看到',
              '按下方「保存」；要恢復就把開關打開再保存'
            ]
          }
        ],
        devNote:
          'Business.appointmentSettings 新增 onlineBookingEnabled（Boolean，預設 true）、onlineBookingPausedReason（≤100 字）、onlineBookingPausedAt（由開→關的當下才寫入，重新開啟保留上一次紀錄）。utils/bookingService.js 新增並匯出 resolveBookingAccess(business)，統一回傳 { closed, reason, message, internalReason }，取代原本只看退訂的 isBookingFrozen 單一判斷（該函式保留，既有呼叫點不動）。優先序：terminated（平台終止）> contract_expired（合約到期）> paused（店家自主）。C 端 getBusinessById 除既有的 bookingFrozen 外多回 bookingAccess；client bookingController 的 getAvailableTimesForDate 與 getAvailableTimesForDateAndStaff 在關閉時直接回空時段，避免客人選完服務、挑好時間、填完資料才在送出時被擋。beautefeel-next 的 BookingService 在進流程前先擋並顯示原因與回店家頁的按鈕。B 端 AppointmentSettings 加開關與原因欄位，關閉時跳 Modal 二次確認（誤觸的代價是店家好幾天沒單卻不知道為什麼）。App 端服務設定是逐欄送出、不帶這些欄位，後端只在欄位存在時才寫入，所以 App 存檔不會覆蓋。'
      },
      {
        kind: 'improvement',
        title: '合約到期後，線上預約會自動停止',
        description:
          '簽約期間結束後，客人將無法送出新的線上預約；你的店家頁面、後台資料、已成立的預約都完全保留，不受影響。完成續約之後會立即恢復，不需要另外設定或通知我們。到期前 30、7、3、1 天的提醒信也會一併說明這件事。',
        howToUse:
          '不用做任何事。想確認自己的合約到期日，可到「設定 → 會員資訊」查看。',
        devNote:
          'resolveBookingAccess 以 subscription.endDate 即時判斷，不用排程寫旗標 —— 續約時 endDate 一更新，預約當下就恢復，不會發生「排程關掉後、續約沒人記得打開」。只認 subscription.isContracted === true 的店家：實際資料裡有 285 家早期匯入、從未簽約、endDate 全是同一天的資料，若一併納入會一次關掉一大片。給客人看的 message 一律中性（「店家目前暫停接受線上預約」），合約狀態屬於平台與店家之間的事，不對客人揭露；要給店家看的實話放在 internalReason，B 端預約設定頁會顯示到期日並把開關 disable（否則店家會一直按那個開關以為系統壞了）。GET appointment-settings 一併回傳 bookingAccess。到期提醒信新增 bookingWillClose 區塊，只對簽約且未開啟自動續約者顯示。另新增 scripts/auditBookingAccess.js（唯讀，不寫入）稽核全站「已到期但預約仍開放」的店家，分簽約／非簽約與近 90 天是否仍有預約進來，支援 --all / --days / --csv。'
      },
      {
        kind: 'feature',
        title: '某些服務可以不讓客人指定人員',
        description:
          '有些項目你會希望依當天的人力與資歷來安排，不適合讓客人自己指名。現在每個服務都能單獨設定：客人自己指定、可指定也可以選「不指定」、或是完全不給指定由店家安排。設成「由店家指派」的服務，客人線上預約時看不到選人那一步，直接選日期時段，單子會進「待指派」等你安排人員 —— 時段的名額仍然依當天有排班、做得了這個服務的人數計算，不會因此少收。沒有特別設定的服務維持原本的行為，你不用重設任何東西。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「服務項目」點進要調整的服務',
              '找到「客人預約時可否指定人員」',
              '選「不給指定，由店家指派」後儲存',
              '之後這個服務的線上預約會出現在「待指派」，指派方式與「不指定人員」的單子相同'
            ]
          }
        ],
        devNote:
          'Business.productSchema 新增 stylistSelectionMode（enum inherit|choose|choose_or_any|assigned，預設 inherit）。utils/bookingService.js 新增並匯出 resolveStylistSelectionMode(business, product)：服務層優先，inherit 才回退 appointmentSettings.unspecifiedStylistEnabled，因此既有服務行為不變、不需資料遷移。判定一律在後端落地：assignStylist 在 assigned 時即使前端帶了 stylistId 也回 null（走既有待指派流程，createBooking 的容量重算防超收照舊生效）；client bookingController 的 getUnassignedCapacityForDate 把原本的總開關檢查換成依服務解析（choose 才回空），getAvailableTimesForDateAndStaff 在 assigned 時直接回空陣列，避免有人帶 staffId 直接打公開 API 繞過前台。business_B 的 addProduct/updateProduct 加白名單與 enum 驗證。前台 beautefeel-next：constants/clientBooking.js 新增與後端同規則的 resolveStylistSelectionMode，BookingStepOne 在 assigned 時整段選人 UI 換成說明卡並自動把選擇定為 unspecified（連帶蓋掉登入導回等情況帶進來的 staffId），BookingStepTwo 沿用既有的 unspecified 容量時段路徑不需改動，確認頁的人員顯示改為「由店家安排」。App 端服務編輯是逐欄 append FormData、不會帶 stylistSelectionMode，後端只在該欄位存在時才寫入，所以 App 存檔不會覆蓋此設定。'
      },
      {
        kind: 'feature',
        title: '同意書可以問問題了',
        description:
          '同意書設定裡多了一區「問卷題目」。以前只有「請客戶勾選的同意項目」，那是請客人同意某件事，只有勾或不勾；問卷題目是向客人問一件事，例如「有正在服用中的藥物嗎？」下面列出催經藥、避孕藥、無等選項讓他選。題型有單選、多選、簡答三種，每一題都可以設成必填 —— 必填沒答完客人就送不出去，不會再收到半份的問卷。題目跟條款一樣會在發送當下拍成快照，之後你改題目、甚至整份範本刪掉，已經簽過的紀錄仍然還原得出客人當時被問了什麼、答了什麼。',
        howToUseSteps: [
          {
            heading: '設定題目',
            steps: [
              '左側選單「同意書」→「同意書設定」',
              '編輯或新增一份範本，往下捲到「問卷題目」',
              '點「新增問卷題目」，填題目文字、選題型（單選／多選／簡答）、加選項',
              '需要客人一定要回答的題目，把右邊的開關切成「必填」'
            ]
          },
          {
            heading: '看客人的回答',
            steps: [
              '「同意書」→「簽署紀錄」點開任一筆，簽署結果下方會列出問答',
              '客戶詳情頁會顯示該客人最近一次的問卷作答',
              '預約詳情的同意書區塊，已簽且有問卷的那筆會多一顆「作答」按鈕',
              '列印／存檔的同意書也會一併帶出問答內容'
            ]
          }
        ],
        devNote:
          'ConsentFormTemplate 新增 questions（key／type single|multiple|text／label／options[{key,label}]／required／placeholder）；ConsentFormRequest 新增 questionsSnapshot；ConsentFormSignature 新增 answers（含 selectedKeys 與作答當下的 selectedLabels，題目與選項文字一起存）。consentFormService 新增三支共用函式：buildQuestionsSnapshot、normalizeAnswers（一律以快照走訪，客戶端多送的題目與不存在的選項一律忽略，必填未答回錯誤訊息）、buildContentHash（沒有作答時維持舊算式 contentSnapshot|checkedItems|signedAt，既有簽署紀錄的雜湊才不會全部驗不過）。B 端與 client 兩支 controller 都接 answers 並在寫入前驗證，signInline 的驗證錯誤改回 400 帶原訊息。範本更新時，questions 有變同樣讓 version +1（比對前先正規化欄位順序，避免單純存檔就一路加版本）。前端：ConsentFormSettingPage 新增 questions 的 Form.List（巢狀 options Form.List，依 type 切換選項清單或提示文字），送出時補 key 並擋下沒有選項的選擇題；新增共用元件 ConsentAnswerList 供簽署紀錄、客戶詳情、預約區塊三處使用；客戶端 ConsentFormSignPage 與 POS 的 PurchaseConsentModal 渲染 Radio／Checkbox.Group／TextArea 並送 answers。App 端 PurchaseConsentSheet 同步支援作答，ConsentFormTemplatesScreen 尚未提供題目編輯，但會把 questions 原樣帶回送出 —— 否則店家在 App 存一次範本就會把電腦版設定的問卷清空。',
        shopHidden: false
      },
      {
        kind: 'improvement',
        title: '預約日曆的「日」「週」看得到設計師了',
        description:
          '日和週的畫面本來就比月曆寬鬆，但每一筆預約還是照月曆那樣擠成一行，超過就被截掉，最想知道的「這節是誰的客人、誰做」反而看不到。現在會依預約長度自動展開：長一點的單子分行寫出時間、客人、服務項目和設計師，短單則收成一到兩行，不會糊掉。客人選「不指定人員」的單子會用橘色標「待指派」，一眼就知道哪幾筆還要排人。另外多了一條粉色的現在時間線，同一個時段的兩筆預約也會左右並排，不再互相蓋住。月曆維持原樣 —— 格子小，塞多了反而難讀。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「預約管理」',
              '右上角切到「週」或「日」'
            ]
          }
        ],
        devNote:
          'Appointments.js 的 renderEventContent 依 eventInfo.view.type 分流，dayGridMonth 維持原本單行排版，timeGrid 走新的 renderTimeGridEventContent。行數不看分鐘數而是回推卡片實際高度（(分鐘/30)×列高 − 內距 − 2px 餘裕）÷ 行高，桌機列高 40px、平板 34px，所以同樣 30 分鐘的預約桌機兩行、平板一行，不會塞不下硬塞。週視圖只寫開始時間、截斷用 clip 不用省略號（欄窄，省略號本身就佔兩三個字），日視圖維持完整區間與 ellipsis。設計師取 extendedProps.stylistName，空值顯示「待指派」並改用 #a8541c。FullCalendar 端補 slotDuration 30 分、slotLabelInterval 1 小時、slotEventOverlap false、nowIndicator、eventMinHeight 26／eventShortHeight 44、scrollTime 09:00、expandRows；列高改用固定 px（TIMEGRID_SLOT_HEIGHT 常數需與 CSS 同步）。休假事件在週視圖也改成上下兩行。'
      }
    ]
  },
  {
    version: '2026-09-03',
    title: '9/3 新增「班表」，客人也可以選「不指定人員」了',
    summary:
      '這次一起上兩件事。第一件是「班表」：以前排班只能一筆一筆新增休假，看不到全店一個月的樣子，現在整個月、所有員工攤在同一張表上，先選一個班別當「筆」，在格子上點一下或按著拖過去就整排填好了；排好的休假會自動同步到預約系統，客人線上就約不到休假的設計師。第二件是「不指定人員」：有些客人其實不在意誰做，只想找一個方便的時間，以前他得從員工清單裡硬挑一個，挑到的人剛好沒空就只好放棄。現在客人可以選「不指定」，系統只確認那個時段還有人手就把單收下來，由你來指派實際負責的人 —— 同時段有兩個人上班就收得下兩筆，其中一個已經被指名約走就只剩一個名額，不會超收。',
    sections: [
      {
        kind: 'feature',
        title: '整個月的班表，一眼看完',
        description:
          '橫軸是日期、縱軸是員工，每一格就是那個人那天的班。右邊會統計每個人這個月上幾天、休幾天；最下面一列是每天有幾個人上班，如果某一天是 0 人會標紅字提醒你，避免整天沒人顧店還沒發現。週末和今天都有底色，不用一格一格數。',
        howToUseSteps: [
          {
            steps: [
              '左側選單點「班表」',
              '第一次使用會請你先建立班別，例如「早班 10:00–15:00」「晚班 15:00–21:00」「特休」',
              '上方選一個班別，然後在表格上點或拖曳就能填入'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '班別和假別，你們店裡怎麼叫就怎麼設',
        description:
          '不用遷就系統的名字。每個班別可以自己取代號（會直接顯示在格子裡）、設時間、挑顏色，休假類型也一樣 —— 例休、特休、事假想分幾種就分幾種，顏色不同一眼就分得出來。之後改班別時間，已經排好的班不會被改掉，歷史班表看起來還是當時的樣子。',
        howToUseSteps: [
          {
            steps: [
              '班表頁右上角點「班別設定」',
              '新增或修改班別，填代號、選上班或休假、設時間與顏色',
              '儲存後就會出現在上方的快速填入列'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '下個月的班，可以從這個月複製',
        description:
          '大多數店的班是固定週循環。點「複製上月」會依「星期幾」對齊複製過來 —— 上個月每個週二誰上早班，這個月每個週二也照排。只會填空白的格子，已經排好的不會被蓋掉，複製完再微調就好。',
        howToUse: '班表頁右上角點「複製上月」。'
      },
      {
        kind: 'feature',
        title: '休假月曆：這個月誰哪天休，一張月曆看完',
        description:
          '班表頁右上角可以切換到「休假月曆」，用一般月曆的樣子呈現，每天列出當天休假的人和假別。要看「這週還有誰在」或「這天約得到誰」的時候比表格直觀。',
        howToUse: '班表頁上方切換「休假月曆」。'
      },
      {
        kind: 'improvement',
        title: '排了休假，客人線上就約不到那位設計師',
        description:
          '班表上排的休假會自動同步到預約系統，客人在線上預約時看不到休假設計師的時段。排了上班班別的話，線上預約也會限制在班別時間內。要留意的是：店家自己在後台建立預約不受這個限制，臨時加班、提早半小時開始這種狀況照樣排得進去。',
        howToUse: '不用做任何事，排完班就生效。',
        devNote:
          '線上預約端（client/controller/bookingController.js）在既有的 dayOff 過濾之後多套一層 filterTimesByShift：該員工當天沒有排班紀錄就不限制，排休假則整天不開放，排上班且有時間則要求服務完整落在班別區間內。unavailable-dates 的 60 天迴圈用 loadShiftMap 批次預抓，沒有新增 N+1 查詢。後台手動建立預約不走這條路徑，所以不受限。'
      },
      {
        kind: 'feature',
        title: '客人選「不指定」，時段選擇通常比較多',
        description:
          '以前客人一定要先挑一位設計師，才看得到那個人的空檔；挑到的人那天滿了，客人往往就關掉頁面了。現在預約頁的員工清單最上面多了一張「不指定人員」，選它之後看到的是「店裡還有人手」的時段 —— 只要當下還有任何一位設計師有空就約得到。客人送出後就是確認成立，不用等你回覆。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「設定」→「預約設定」',
              '把「允許不指定人員」打開並儲存（預設是關閉的）',
              '開啟後，該服務有兩位以上設計師時前台就會自動出現這個選項；只有一位設計師的服務不會顯示（沒有意義），不需要再逐項服務設定'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '同時段有幾個人上班，就收得下幾筆',
        description:
          '這不是「無限收單」。系統會看那個時段實際有幾位設計師有空 —— 兩個人上班就收兩筆不指定；如果其中一位已經被別的客人指名約走，那個時段就只剩一個名額。已經收下但還沒指派的單也會先佔住名額，不會出現「收了三筆卻只有兩個人」的狀況。排班、個人休假、店休一樣都算在內。',
        howToUse: '不用做任何事，收單時系統自動計算。'
      },
      {
        kind: 'feature',
        title: '要指派的預約，一進預約管理就看得到',
        description:
          '這種預約收進來時是沒有設計師的，如果沒人提醒很容易被滑過去 —— 它在列表裡長得跟其他單一樣，只有服務人員那欄是空的。現在預約管理頁最上面會出現一條提示，列出還沒指派的預約，點一下就能指派；左側選單的「預約管理」也會亮紅點顯示筆數。指派時系統會再確認那位設計師該時段真的有空，如果他已經被別的客人約走了會擋下來告訴你。',
        howToUseSteps: [
          {
            steps: [
              '左側選單「預約管理」（有待指派時會亮紅點）',
              '頁面最上方的提示會列出還沒指派的預約，點該筆預約',
              '選擇要負責的設計師，確認指派'
            ]
          }
        ]
      },
      {
        kind: 'improvement',
        title: '指派只有店主和店長可以做',
        description:
          '指派等於在排員工的工作，所以這個動作只開放給店主與店長。櫃台、設計師、會計看得到「還有幾筆要指派」的提示，但不能自己動手指派。',
        devNote:
          '權限代碼是 assignment:manage，刻意不放在 booking 命名空間下。後端的 hasPermission 會讓 booking:* 自動涵蓋所有 booking: 開頭的子權限，而櫃台角色本來就有 booking:*，若命名為 booking:assign 會連櫃台一起開放。'
      }
    ]
  },
  {
    version: '2026-08-27',
    title: '8/27 營收報表：客戶用儲值消費不再被當成成本，盈餘算法更貼近真實',
    summary:
      '以前客戶拿儲值金來消費時，那筆金額會被同時算進收入、又扣進營業成本，等於做了服務卻沒賺 —— 這個算法錯了，已經修正。取而代之的是「儲值折讓」：只把當初「儲值送」多送出去的那部分列為成本，因為那段錢店家從來沒收到。另外，營業成本要包含哪些項目，現在可以自己勾選了 —— 不想把金流手續費或儲值折讓算成成本的店家，取消勾選即可。儲值金餘額也修正成與「儲值記錄查詢」一致。',
    sections: [
      {
        kind: 'improvement',
        title: '客戶用儲值金消費，不再被重複扣一次',
        description:
          '假設客人之前儲值 10,000，今天用其中 5,680 做臉：\n• 以前：營業收入 +5,680，但營業成本也 +5,680，本期盈餘變成 0 —— 看起來像做白工。\n• 現在：營業收入 +5,680，營業成本不再扣這筆，盈餘就是實際賺到的。\n\n道理很簡單：客人儲值當下那筆錢還不是你的收入（是先收的錢、欠客人服務），所以系統本來就沒把它算成收入；等到客人真的來消費、服務做完了，才認列成收入。既然入帳時沒算過收入，用掉時自然也不該再扣一次成本。\n\n客戶本期用掉多少儲值，改到「預收款（儲值金）」區塊看，那才是它該待的地方。',
        howToUse:
          '不用做任何事，重新整理報表就會看到修正後的數字。過去的月份也會一起變正確。'
      },
      {
        kind: 'feature',
        title: '新增「儲值折讓」：把儲值送的部分正確列為成本',
        description:
          '如果你有做「儲值 10,000 送 1,000」這類活動，客人手上有 11,000 可用，但你實際只收到 10,000。等客人把 11,000 用完時，營業收入會照定價認列 11,000，比真實入帳多了 1,000 —— 那 1,000 是你送出去的，屬於折讓，會列進營業成本。\n\n折讓不是一次扣完，而是跟著客人每次消費按比例攤提。以上面的例子，客人每用 1 元儲值，就有約 9% 是當初送的，這部分會在那次消費時列為成本。儲值時打折（例如儲 10,000 只收 9,000）也是同樣的道理。\n\n如果你從來沒做過儲值送、也沒打過折，這一項會是 0，不影響你的數字。',
        howToUse:
          '在「營收紀錄查詢」的支出明細可以看到「儲值折讓」這一列。想知道某一筆是哪來的，到「儲值管理 → 儲值記錄查詢」查該客戶的儲值紀錄，看有沒有贈送金額。'
      },
      {
        kind: 'feature',
        title: '營業成本要包含哪些項目，可以自己決定了',
        description:
          '每間店對「成本」的認定不太一樣 —— 有人覺得刷卡手續費是銀行的事、不想算進自己的成本，有人不想把儲值折讓列進來。現在這四項可以自己勾選：\n• 金流服務費（刷卡／行動支付手續費）\n• 儲值折讓\n• 零用金\n• 薪資\n\n進貨、退款、其他支出一律計入，不開放取消 —— 進貨是毛利的定義（毛利 = 營業收入 − 進貨成本），退款是實際付出去的錢。\n\n這是全店共用的設定：老闆設定一次，所有員工、手機 App 上看到的營業成本與本期盈餘都會一致，對帳時不會各說各話。取消勾選的項目仍然會列在支出明細裡並標示「未計入」，金額看得到，只是不進總額。',
        howToUseSteps: [
          {
            heading: '設定方式',
            steps: [
              '進入「數據報表 → 營收紀錄查詢」',
              '在最上方四個數字（營業收入／營業成本／毛利／本期盈餘）下方，找到「營業成本包含」那一列',
              '勾選或取消勾選要計入的項目，系統會立刻重算並存檔',
              '需要「財務管理」權限才能調整；沒有權限的人看得到目前設定，但不能改'
            ]
          }
        ]
      },
      {
        kind: 'improvement',
        title: '儲值金餘額修正，與「儲值記錄查詢」一致',
        description:
          '營收報表的「儲值金餘額」以前與「儲值管理 → 儲值記錄查詢」對不起來，有些店家差到十幾萬。原因是兩邊算的來源不同 —— 報表漏掉了早期匯入的儲值資料。現在報表改用與儲值管理相同的客戶餘額帳本，兩邊數字會一致。\n\n同時修正：客戶之間的「儲值金轉讓」以前會被誤算成「使用的儲值金」。轉讓只是把錢從 A 搬到 B，沒有人消費、店家的負債也沒變，現在不再計入。',
        howToUse: '不用做任何事。如果你之前發現兩邊數字對不起來，現在可以再對一次。'
      },
      {
        kind: 'improvement',
        title: '「使用的儲值金」現在包含沒開結帳單的扣款',
        description:
          '「預收款（儲值金）」區塊的「使用的儲值金」，現在是客戶實際用掉的全部金額，包含直接在儲值管理扣款、沒有走結帳流程的部分。\n\n這個數字可能會比支出明細裡的「結帳單儲值付款」大 —— 差額就是「有扣儲值金但沒開結帳單」的消費。那些服務不會產生營業收入，也不會計入員工業績，建議定期對一下：到「儲值管理 → 儲值記錄查詢」，看「類型 = 扣款」而「訂單狀態」是灰色破折號的那幾筆。',
        howToUse: '不用做任何事。想查明細的話，到「儲值管理 → 儲值記錄查詢」看訂單狀態欄。'
      },
      {
        kind: 'bugfix',
        title: '零用金在報表的認列日期修正',
        description:
          '線上環境的報表在計算零用金支出時，區間會位移 8 小時，導致每月的第一天漏算、隔月第一天的單被算進來。例如查 6 月時，7/1 的零用金被算進 6 月，6/1 的卻沒被算到。',
        devNote:
          '零用金查詢用 moment 的 startOf/endOf(day)，跟著伺服器本機時區走；線上是 UTC、開發機是 UTC+8，區間差 8 小時。改用 moment-timezone 明確指定台北時區，並在 ecosystem.config.js 補上 TZ。同檔案的客戶評價報表與現金流計算也有同樣寫法，一併修正。'
      },
      {
        kind: 'bugfix',
        title: '營收報表的儲值相關數字全面校正',
        description:
          '儲值折讓、儲值金餘額、使用的儲值金三項的計算來源修正。',
        devNote:
          '一、客戶使用儲值不再計入 expense 總額（收入已認列、預收款同額減少，非成本），改以 storedValueDiscount（贈送與儲值折扣的銷售折讓）列成本。二、新增 utils/storedValueLedger.js 作為儲值資料的單一來源：合併 ValueAddedPurchase 與 CustomerBalance.transactions 並依 transactionId 去重（去重規則與 getValueAddedRecords 一致），只讀前者會漏掉批次匯入的歷史資料，導致兌換率分母被砍掉、折讓率放大數倍。三、餘額改讀 CustomerBalance 的 balanceAfter 快照，不再用入金減出金相減，也不可加總 amount（deduct 存的是正數，schema 註解原本寫錯，已一併修正）。四、轉讓的 deduct 與 topup 兩邊都排除，否則會憑空多出使用與收入。五、prepayment.usedInPeriod 改用帳本，會大於結帳單的儲值付款，差額為未開結帳單的手動扣款。'
      }
    ]
  },
  {
    version: '2026-08-25',
    title: '8/25 重要異動：結帳的「折扣 (%)」改成填折抵幾 %',
    summary:
      '「折扣 (%)」欄位的意思改了，請務必先看過再結帳：以前填 90 代表打九折，現在改成填「折抵幾 %」—— 要打九折請填 10。這與手機 App 的折扣欄位一致，兩邊不會再各算各的。輸入框下方會即時顯示這個趴數折了多少錢，套用前就能確認。另外修正一個會多收錢的問題 —— 過去在某些情況下，優惠券金額會被重複計算，導致客人的儲值金被多扣；手機 App 的結帳也補上了金流手續費欄位。',
    sections: [
      {
        kind: 'improvement',
        title: '「折扣 (%)」改成填「折抵幾 %」，並顯示折了多少錢',
        description:
          '這個欄位的意思改變了，請特別留意：\n• 以前：填 90 = 打九折、填 77 = 打 77 折（填的是「客人要付幾成」）。\n• 現在：填 10 = 折抵 10%（也就是打九折）、填 23 = 折抵 23%（打 77 折）。填的是「要折掉幾 %」。\n• 這樣改是為了跟手機 App 的折扣欄位一致 —— 以前同一個數字在電腦後台和 App 上會算出兩種金額，很容易搞混。\n• 為了避免填錯，輸入框下方會即時顯示「折抵 NT$ ○○○」，還沒按「套用」時會標註「（尚未套用）」，你可以先確認金額再送出。\n• 金額摘要那行也會寫成「折扣 10%（折抵 NT$ 300）」，結帳前再確認一次。\n\n提醒：已經完成的舊訂單不受影響，它們記錄的是折扣金額本身，不會因為這次調整而改變。',
        howToUse:
          '要打九折就填 10，打八折填 20，打 77 折填 23。填完按「套用」，先看下方顯示的折抵金額對不對，再往下結帳。',
        devNote:
          '折扣趴數語意由「付 X%」改為「折抵 X%」，公式 Math.round(小計 × (100 − pct) / 100) 改為 Math.round(小計 × pct / 100)，與 App 端 checkoutHelpers.calcDiscountAmount 的 percent 模式對齊。CheckoutPage.js 共三處複製（顯示用的 calculatedDiscountFromPercentage、套用按鈕的 handleApplyDiscountPercentage、送出時的 calculatedFinalDiscount）已一併改，桌機版與行動版的文案、placeholder 與折抵金額預覽也都更新。discountPercentage 只由套用按鈕寫入、沒有任何地方從既有訂單反推，因此歷史訂單（存的是 discountAmount 金額）不受影響。',
        shopHidden: false
      },
      {
        kind: 'bugfix',
        title: '修正：優惠券可能導致儲值金被多扣',
        description:
          '在優惠券金額與應收金額剛好差一點點的情況下，系統可能把優惠券重複計算一次，讓客人的儲值金被多扣。現在結帳前會嚴格核對「付款總額」與「應收金額」，對不上就不會送出。優惠券的算法本身不變：它視同現金，是付款方式之一，其餘付款方式會自動調整成「應收 − 優惠券」。',
        howToUse: '不用做任何事。',
        devNote:
          '優惠券語意維持「付款方式（視同現金）」不變：amounts.receivable 不扣券，calculatePaidAmount 計入 voucher。根因有三層：CheckoutPage 的「總付款金額」顯示 payableAfterVoucher（已扣券）但送出的 amounts.receivable 用未扣券的 totalAmount；自動配款把儲值金填成全額應收後不理會手動輸入的 voucher；送出前檢查容許 1 元誤差（Math.abs(totalPaid − totalAmount) > 1），剛好放行「券 = 1 元」的溢收。三處皆已修正（大字改綁 effectiveSummaryTotal、自動配款導入 keepVoucher 並把 voucher 納入 deps、容差改為嚴格比對），並在後端 createOrder 加上底線防護（付款加總 > 應收即回 400）以同時涵蓋 Web／App／舊版前端。App 端同步補上送出前檢查與收款對帳提示條（原本完全沒有），現金溢付改為收斂成實際需收金額、找零不進帳。',
        shopHidden: false
      },
      {
        kind: 'feature',
        title: 'App 結帳可以填金流手續費了',
        description:
          '手機 App 的結帳頁新增「金流手續費」欄位，跟電腦後台一樣：刷卡或行動支付時會依你設定的費率自動帶入，也可以自行修改。這筆是店家的支出，不會計入客人的已付金額，只會反映在報表的成本上。匯款手續費之類沒有費率可算的支出，也可以直接填在這裡。',
        howToUse:
          '在 App 結帳頁的「付款方式」區塊最下方就能看到。費率沿用「設定 → 金流手續費」既有的設定。',
        devNote:
          'App 端 emptyPayment 新增 paymentFee，刷卡／行動支付變動時由 effect 依 feeSettings.autoCalculate 自動帶入（與 B 端 CheckoutPage 同行為），buildOrderPayload 改為「欄位有值就用欄位值，沒填才依費率算」。paymentFee 不列入 sumNonCash／sumPaid，也不在後端 A4 的加總內。',
        shopHidden: false
      }
    ]
  },
  {
    version: '2026-08-11',
    title: '8/11 新功能：定金可以線上刷卡了',
    summary:
      '過去客人要付定金只有匯款一條路 —— 要臨櫃或開網銀轉帳，還得回來填匯款後五碼，中間任何一步卡住預約就懸在那裡。現在可以開放客人線上刷卡付定金：刷完款定金狀態自動變成已付，不用回填後五碼，你也不必再人工核對。預約頁、預約完成頁、LINE 與 Email 通知、以及「我的預約」都看得到刷卡入口。店家自己用實體刷卡機收定金時，也可以在預約詳情標記為「櫃檯刷卡」，刷卡手續費會自動計入該筆結帳的支出，不會讓報表上的收益虛高。',
    sections: [
      {
        kind: 'feature',
        title: '客人可以線上刷卡付定金',
        description:
          '開啟後，需要付定金的客人會多一個「線上刷卡付定金」的選項：\n• 刷卡完成後定金狀態自動更新為已付款，客人不需要回填匯款後五碼，你也不用人工核對。\n• 刷卡入口會出現在四個地方：預約表單（預約當下就告訴客人有這個選擇）、預約完成頁、LINE 與 Email 通知，以及客人的「我的預約」。\n• 想匯款的客人完全不受影響，兩條路並存。\n• 客人隔幾天才想付款也沒問題，「我的預約」裡隨時都能回來刷。\n• 已付款的預約會顯示付款方式、卡號末四碼與付款時間，方便對帳。\n\n這項功能需要先完成金流帳號綁定 —— 定金會直接進你自己的收單帳戶，所以必須使用貴店專屬的金流設定，請聯繫客服協助完成。（注意：「快速收款」沒有綁定也能使用，走的是系統預設設定，所以能用快速收款不代表已經完成綁定。）',
        howToUseSteps: [
          {
            heading: '開啟功能',
            steps: [
              '進入「設定 → 定金設定」，確認定金功能已開啟。',
              '打開「開放客人線上刷卡付定金」。',
              '若下方出現黃色提示，代表金流帳號尚未綁定，請聯繫客服完成綁定後即會自動生效。',
              '完成後前台的刷卡入口會自動出現，不需要再做任何設定。'
            ]
          },
          {
            heading: '用自己的刷卡機收定金',
            steps: [
              '在「定金設定」打開「允許標記櫃檯刷卡」。',
              '客人在店裡刷完卡後，到該筆預約的詳情頁把定金狀態改為「已付款」。',
              '收款方式選「櫃檯刷卡」，系統會依你設定的費率記下這筆手續費。'
            ]
          }
        ],
        devNote:
          '新增 payment_for="booking_deposit"（與既有的 "booking" 分開 —— 後者付的是整筆服務金額，成功後處理完全不同）。C 端端點 POST /api/client/bookings/:id/deposit-card-payment，只收 depositAmount，一律使用 getOwnPaymentConfig（嚴格版，不吃環境變數的預設金鑰）—— 定金是店家的錢必須直接進本店收單帳戶，與線上商店訂單同一原則；沒綁定就不開放，前台連入口都不顯示。付款結果由 utils/bookingDepositPaymentSync 同步回 Booking，Notify 與 Return 兩條路徑都掛上；因為兩者會互相競爭且順序不保證，改用原子 findOneAndUpdate 認領（條件 depositStatus !== "paid"），重複執行會把手續費算兩遍。Booking 新增 depositPayment 子文件（method / source / feeRate / fee / feeSettledOrderId / paidAt / tradeNo / cardLastFour / paymentId），費率是收款當下的快照而非結帳時查詢 —— 店家中途調費率的話這筆定金的成本會被算錯。前台旗標 allowOnlineCardPayment 由後端合併「開關已開」與「已綁定金流」兩個條件後才回傳，避免客人點下去看到錯誤頁；注意 getBusinessById 的 select 明確排除 -paymentSettings，該處改用 Business.exists 單獨判斷有無綁定。',
        shopHidden: false
      },
      {
        kind: 'improvement',
        title: '刷卡定金的手續費會自動計入報表支出',
        description:
          '刷卡收定金是有手續費的，那筆費用如果沒有被記錄，數據報表上的收益就會比實際入帳的錢還高。現在：\n• 定金以刷卡收取時，系統會依你在結帳設定裡的刷卡費率，記下這筆手續費。\n• 客人來店結帳、定金折抵時，這筆手續費會併入該張結帳單的金流手續費，報表的支出與淨利自動反映。\n• 客人取消或沒來、定金依規定不退時，系統會自動產生一筆結帳紀錄，把定金認列為收入（並帶上當初的手續費支出）—— 這筆錢實際進了你的口袋，報表就該看得到。\n• 結帳紀錄可以用「定金沒收」這個來源篩選出這類收入。',
        howToUse:
          '不用做任何事，系統會自動處理。費率沿用「結帳設定」裡既有的刷卡手續費設定。',
        devNote:
          'CheckoutOrder.source 新增 "booking_deposit"。結帳時若 paymentData.deposit > 0 且該預約的 depositPayment.fee > 0，會把該費用併入這張訂單的 payment.paymentFee；定金收款當下沒有可掛的訂單，到結帳才有歸屬對象，少了這段刷卡定金的手續費永遠不會出現在報表支出裡。取消／未到店則由 utils/bookingDepositForfeitSync 補一張結帳單（servicesAmount = receivable = paid = 定金金額，services/products 留空 —— 報表是直接讀 amounts 而非加總品項，這樣既進得了營收又不會污染服務項目分析）。認列時點是取消當下而非收款當下：在確定不退之前那筆錢是預收款不是營收。depositPayment.feeSettledOrderId 同時當「手續費已認列」與「收入已認列」的鎖，取消可能同時來自客人端與店家端，光靠 if 判斷會各建一張單讓營收變兩倍，因此改用原子認領。定金不退所以刻意沒有做作廢連動；店家若破例退款請直接作廢該張結帳單。',
        shopHidden: false
      }
    ]
  },
  {
    version: '2026-08-10',
    title: '8/10 更新：到店取貨改用 QR code 核銷，手機也能處理商店訂單',
    summary:
      '到店取貨的訂單現在會產生一組六位數取貨碼與 QR code，客人到店出示、你掃描核對後才放貨，不再只靠「我是某某某」認人。核銷可以在電腦後台用鏡頭掃，也可以直接用手機 App 掃。App 端同步補上完整的商店訂單功能：核帳、出貨、標記完成、取消與退款都能在手機上完成，收到推播點一下就直接開到那筆訂單。前台的訂單畫面也重新設計過，配色更柔和，手機與平板都能正常顯示。',
    sections: [
      {
        kind: 'feature',
        title: '到店取貨：QR code 核銷，避免拿錯與冒領',
        description:
          '過去到店取貨沒有任何憑證可以核對，客人說「我是王小姐」你就得放貨，同名同姓或代領時很容易出錯。現在：\n• 你把訂單標記為「可取貨」時，系統會自動產生一組六位數取貨碼。\n• 客人的訂單頁會顯示 QR code 與這組號碼，LINE 通知裡也會附上。\n• 你掃描後畫面會先顯示是誰的訂單、買了什麼、付款了沒有，確認無誤再按核銷，訂單即完成。\n• 貨到付款的訂單會特別提醒你交貨前要先收款，並顯示應收金額。\n• 螢幕太暗或客人手機沒電時，直接輸入那六位數也可以核銷。',
        howToUseSteps: [
          {
            heading: '在電腦後台核銷',
            steps: [
              '進入「線上商店 → 取貨核銷」，或在商店訂單頁點右上角的「取貨核銷」。',
              '點「掃描 QR code」開啟鏡頭，對準客人手機畫面上的 QR。',
              '確認畫面顯示的訂單內容與客人相符後，點「確認交貨並核銷」。',
              '鏡頭掃不到時，請客人念出畫面上的六位數，直接輸入即可。'
            ]
          },
          {
            heading: '用手機 App 核銷',
            steps: [
              '打開 App，進入「設定 → 取貨核銷」。',
              '第一次使用會詢問相機權限，允許後即可掃描。',
              '掃到之後同樣先確認訂單內容，再按「確認交貨並核銷」。'
            ]
          }
        ],
        devNote:
          '取貨碼存在 ShopOrder.delivery.pickup（code / codeIssuedAt / verifiedAt / verifiedBy / verifiedByName / verifiedVia），索引為 { business, delivery.pickup.code } sparse。碼用 crypto.randomInt 產生而非 Math.random —— 取貨碼等同提貨憑證，可預測就等於可以被冒領；唯一性只需在「同商家 × status===shipped」的範圍成立，核銷 API 一定帶 businessId 且只比對 shipped，因此同一組碼不可能被用第二次。標記可取貨時產生（utils/pickupCode.ensurePickupCode），客戶端 getMyOrderDetail 也會為功能上線前就已備妥的舊訂單補發。API：GET /api/store/shop-orders/pickup/lookup（shopOrder:view，只查不改狀態）與 POST /api/store/shop-orders/pickup/confirm（shopOrder:manage，走狀態機轉 completed，COD 於此刻入帳）；兩支都必須註冊在 /:orderId 之前，否則會被當成 orderId。查詢與核銷刻意拆成兩支：掃到就結案的話，掃錯碼或掃到別人的畫面完全沒有攔截點。C 端 QR 內容是後端產生的後台核銷網址（BUSINESS_CMS_URL/shop/pickup?code=），店家用手機原生相機掃也能直接開啟核銷頁；QR 產生器是自寫的無依賴實作（Byte mode / ECC M / 版本 1–10），已對照 npm qrcode 套件以 218 組字串逐一比對模組矩陣完全一致。',
        shopHidden: false
      },
      {
        kind: 'feature',
        title: 'App 端：商店訂單可以在手機上完整處理',
        description:
          '過去 App 只能看預約與結帳，線上商店的訂單一定要開電腦。現在 App 的「設定 → 商店訂單」可以：\n• 依待處理／待付款／已出貨／已完成分頁查看，待處理筆數會直接顯示在標題。\n• 核對轉帳匯款並核帳通過，或退回請客人重新回報。\n• 填寫物流商與單號出貨，到店取貨則標記為可取貨。\n• 標記完成、取消訂單、開始與完成退款。\n• 寫只有店家看得到的內部備註。\n收到新訂單推播時，點一下通知就會直接開到那一筆訂單。',
        howToUseSteps: [
          {
            heading: '在哪裡找到',
            steps: [
              'App 底部「設定」→ 營運管理區塊 →「商店訂單」。',
              '取貨核銷則在同一區塊的「取貨核銷」。',
              '需要更新到最新版本的 App 才會出現這兩個項目。'
            ]
          }
        ],
        devNote:
          '新增 BeautyAppJs/src/api/shop.js 與三個畫面（ShopOrdersScreen / ShopOrderDetailScreen / ShopPickupScanScreen），已在 App.js 註冊路由、SettingsScreen 加入口、pushNotifications.handleNotificationPress 加 shop_order 深連結分支（推播 data 為 { type:"shop_order", action, orderId, orderNo }）。方案門檻沿用 MembershipGuard requiredFeature="shopOrder"，App 端 featurePlanMap 已有對應項目。掃碼用 expo-camera 的 CameraView + useCameraPermissions，app.json 已加入 plugin 與相機權限文案。動作按鈕一律依後端 availableActions 產生，唯一例外是出貨鍵額外檢查 status === "to_ship" —— refunding 狀態下 shipped 也是合法轉移，但語意是「取消退款流程」，只看 availableActions 會在退款中的訂單冒出一顆「出貨」，按下去等於偷偷把退款取消掉（B 端網頁原本就有這個問題，已一併修正）。',
        shopHidden: false
      },
      // {
      //   kind: 'improvement',
      //   title: '前台畫面優化：柔和配色、手機與平板都能正常顯示',
      //   description:
      //     '客人端的購物車、結帳、訂單列表與訂單詳情重新設計過：\n• 訂單狀態的底色改用柔和的馬卡龍色系，取代原本大面積的高飽和色塊。\n• 主要按鈕（立即付款、確認收貨、送出訂單）改為停在畫面底部，客人不用捲到最下面才知道要做什麼。\n• 商品列表與商品詳情的照片改為完整顯示不裁切，直式與橫式的商品照都不會被切掉。\n• 搜尋商品的輸入框修正了會被切掉、以及在 iPhone 上點擊會整頁放大的問題。\n• 商品詳情頁加上返回按鈕。',
      //   devNote:
      //     '色票集中在 beautefeel-next/src/utils/shopApi.js 的 ORDER_STATUS_META（每個狀態給 bg / fg / border / color 四值）。iOS Safari 在字級小於 16px 的輸入框聚焦時會自動放大整頁且不會縮回，所有輸入框字級已固定 16px。商品圖改 aspect-ratio + object-fit: contain，取代原本固定高度 + cover。ShopCheckout 的 Wrapper 以 && > * 提高權重做置中，避免子元件的 margin 縮寫覆蓋掉。另修正兩個後端問題：商店設定的檢查清單因為 Business 查詢漏 select storeEnabled，導致已啟用的店家永遠顯示「尚未啟用」；購物車與結帳頁的商店名稱改讀 Business.name，不再使用會與店家改名脫鉤的 Store.name 快照。',
      //   shopHidden: false
      // }
    ]
  },
  {
    version: '2026-08-07',
    title: '8/7 新功能：線上商店（客人可在網路上下單）',
    summary:
      '新增「線上商店」模組。你可以挑選要在網路上販售的商品上架，客人在美感前台就能加入購物車、選擇宅配或到店取貨、線上刷卡或轉帳匯款下單。訂單成立後你會收到 App 推播與 LINE 通知，出貨時填入物流單號，客人也會即時收到通知。商品若有不同規格（例如容量、顏色），現在可以分別設定售價與庫存。此功能為高階方案專屬。',
    sections: [
      {
        kind: 'feature',
        title: '線上商店：客人可直接在網路上向你下單',
        description:
          '過去客人只能預約服務，想買商品得另外私訊你。現在：\n• 你可以從庫存中挑選要在網路上賣的商品上架，其餘（藥水、包材等內部耗材）維持不上架，客人看不到。\n• 客人在你的店家頁面就能瀏覽商品、加入購物車並完成結帳。\n• 付款方式由你決定要開哪幾種：線上刷卡、轉帳匯款、貨到付款。\n• 取貨方式可選宅配到府或到店取貨，運費、免運門檻與離島加價都能自訂。\n• 訂單成立時庫存會自動扣除，取消或退款時自動回補，不會賣超。',
        howToUseSteps: [
          {
            heading: '第一次設定（約 5 分鐘）',
            steps: [
              '進入「線上商店 → 商店設定」，畫面最上方會列出開店前要完成的四件事。',
              '選擇要開放的付款方式與取貨方式，並填寫退換貨政策（網路販售依法必填）。',
              '到「線上商店 → 上架商品」，把要販售的商品切換為「販售中」。',
              '回到商店設定，打開最上方的「線上商店總開關」即可開始接單。'
            ]
          },
          {
            heading: '收到訂單後',
            steps: [
              '你會收到 App 推播與 LINE 通知，「線上商店」選單也會出現待處理數字。',
              '進入「商店訂單」，轉帳訂單先核對客人回報的匯款末五碼，確認無誤後點「核帳通過」。',
              '包裝好之後點「出貨」，填入物流商與單號（到店取貨則點「標記可取貨」），客人會立即收到通知。',
              '客人確認收貨後訂單自動完成；也可以由你手動標記完成。'
            ]
          }
        ],
        devNote:
          '新增 shared/models/ShopOrder（獨立訂單模型，不再沿用 Payment；一張訂單只屬於一個 business，購物車強制依商家分開結帳故不需拆單）、Counter（訂單編號原子流水號，格式 SO+YYYYMMDD+6碼，用 Mongo $inc 而非 Redis 以避免連線未就緒時撞號）。狀態機集中在 utils/shopOrderStateMachine（8 個狀態、actor 權限、前置條件如宅配必填物流單號、核帳退回必填原因）。庫存扣補在 utils/shopOrderStock，訂單成立當下即扣、取消退款回補，用 ShopOrder.stockDeducted 旗標確保冪等（重複扣或重複補都不會有錯誤訊息，只會在盤點時發現數字不對）。計價一律走 utils/shopOrderPricing 伺服器端重算，前端傳來的 expectedTotal 只用於比對，不一致回 409。路由：business/routes/shopOrderRoutes（掛在 /api/store 之前，避免 Express 依序比對時白跑一次 storeRoutes 的 protect+requireSubscription）、client/routes/shopOrderRoutes。權限 shopOrder:view / shopOrder:manage / shopSetting:manage 已加入 middleware/permission.js 五個角色與 B 端 permission.json（designer 僅 view）。方案門檻 [business, brand]，三份 featurePlanMap 已同步並有 scripts/checkPlanConsistency.js 檢查。',
        shopHidden: false
      },
      {
        kind: 'feature',
        title: '商品規格：同一商品的不同容量／顏色可分開設定價格與庫存',
        description:
          '過去商品的「規格」只是選項文字，庫存仍然是一個總數，客人選了 30ml 你也看不出剩幾瓶。現在：\n• 在「上架商品」點商品的「規格設定」，開啟規格庫存後，系統會依你設定的規格軸（例如「容量：30ml、50ml」）自動產生所有組合。\n• 每個規格可以分別填售價、成本、庫存與料號；留空的欄位會沿用商品本身的設定。\n• 規格多的時候可用「批次」一次填入所有規格的價格或庫存。\n• 商品的總庫存會自動加總各規格的數量，庫存報表與進貨紀錄都看得到是哪一個規格。\n• 客人在前台選規格時，售完的規格會直接不能選。',
        howToUseSteps: [
          {
            heading: '如何設定規格庫存',
            steps: [
              '先在商品編輯頁設定規格軸，例如新增規格「容量」，選項填「30ml」「50ml」。',
              '到「線上商店 → 上架商品」，點該商品的「規格設定」。',
              '打開「使用規格庫存」，點「依規格軸自動產生組合」。',
              '逐列填入售價與庫存（或用「批次」一次填完），儲存即可。'
            ]
          }
        ],
        devNote:
          'Store.products 新增 variants（疊加式，非取代）：product.stock 保留為所有 variant 庫存的加總，由 storeSchema pre-save hook 自動維護。因此既有讀取 product.stock 的 28 處（統計、成本報表、低庫存判斷）完全不用改，只有實際寫入庫存的 12 處改走 utils/variantStock（checkoutController 4 處、inventoryController 2 處、storeController 6 處）。deductStock/restoreStock 在 hasVariants=true 卻未指定 variant 時會明確擋下 —— 否則扣在 product.stock 上會被 pre-save hook 重算掉，等於靜默吃掉庫存且無錯誤訊息。另新增 product.onlineStatus（與庫存模組的 status 分開），遷移一律 offline，避免開通商店時把內部耗材全推上前台。ProductPurchase.items 加 variantId/optionKey/specText。遷移腳本 scripts/migrateProductVariants.js（僅補欄位、不動庫存數字、可重跑）。',
        shopHidden: false
      },
      {
        kind: 'improvement',
        title: '線上刷卡的貨款直接進入你自己的金流帳戶',
        description:
          '線上商店的刷卡收款一律使用你自己綁定的藍新金流帳號，貨款直接進到你的收單帳戶。若尚未綁定或審核尚未通過，刷卡選項會是關閉且無法開啟的狀態，你仍可先使用轉帳匯款或貨到付款收款。',
        howToUse:
          '若要開放線上刷卡，請先到「設定 → 金流設定」完成綁定與申請；通過後回到商店設定即可開啟。',
        devNote:
          'utils/paymentConfigUtils 新增 getOwnPaymentConfig 與 canAcceptOnlinePayment（嚴格版）。既有的 getBusinessPaymentConfig 在店家未綁定時會退回平台環境變數金鑰，那對預約定金這類平台代收情境是合理的向後相容，但用在商店訂單等於貨款進到平台帳戶而非店家，因此商店模組一律走嚴格版。三重檢查：金鑰存在且格式正確、applicationStatus=approved、isOnlinePaymentEnabled=true——只看旗標不夠，旗標可能在金鑰被清空後仍為 true。shopSettingController 的 readiness 會回傳 onlinePaymentBroken 供前端顯示警示。Payment.payment_for 新增 shop_order，Notify/Return 兩端掛上 utils/shopOrderPaymentSync（冪等，已標記 paid 就不再處理；訂單已取消卻收到成功回調則記錄待人工確認，不自動處理）。',
        shopHidden: false
      }
    ]
  },
  {
    version: '2026-07-30',
    title: '7/30 新功能：薪資手動加減項、現場 LINE 綁定 QR Code',
    summary:
      '「每月員工薪資」細項頁新增「手動加項／減項」，獎金、全勤、津貼、扣款可直接登記並即時計入實際提撥薪資；同時修正了加項總計重複計入抽成的問題，部分員工的實際提撥薪資會下修為正確金額。另外「LINE 整合」設定頁新增「現場綁定 QR Code」，客人在櫃檯掃描即可完成 LINE 綁定或與店家 LINE 配對，之後預約通知直接透過 LINE 傳送。',
    sections: [
      {
        kind: 'feature',
        title: '每月員工薪資：可登記手動加項／減項',
        description:
          '過去獎金、全勤、津貼這類「薪資之外的加給」沒有地方登記。現在可以：\n• 在薪資細項頁直接新增「加項」（獎金、津貼…）或「減項」（扣款、代扣…），輸入金額、日期與說明。\n• 登記後立即計入「加項總計／減項總計」與「實際提撥薪資」，列表與細項頁同步更新。\n• 每筆紀錄都看得到、可刪除，加項明細不再是對不上的數字。\n• 操作權限：老闆與具「財務管理」權限的角色（例如店長、會計）。',
        howToUseSteps: [
          {
            heading: '如何新增加項／減項',
            steps: [
              '進入「每月員工薪資」，點該員工的「查看細項」。',
              '在「手動加項／減項」區塊點「新增」，選擇類型、輸入金額與說明後儲存。',
              '實際提撥薪資會立即更新；刪除紀錄也會即時重算。'
            ]
          }
        ],
        devNote:
          '新增 shared/models/SalaryAdjustment（businessId/employeeId/month/type add|deduct/amount/description/date/createdBy）。commissionRoutes 新增 GET/POST/DELETE /commission/salary-adjustments，權限 finance:manage（owner 的 * 亦通過）。寫入後 refreshSummaryAfterAdjustment 立即重算該員工當月 additionsTotal/deductionsTotal/netSalary。commissionService.getMonthlyAllowanceAdjustmentTotals 改讀 SalaryAdjustment＋當月員工自購扣款（EmployeeOrder salaryDeduct，從訂單重建，重算不會弄丟）。細項 API 的加項/減項明細列出手動項目（category 手動加項/手動減項）。未新增權限碼與方案門檻（沿用薪資模組既有 business/brand 門檻與 finance:manage）。'
      },
      {
        kind: 'improvement',
        title: '薪資「加項總計」計算修正（部分員工實際提撥薪資會下修）',
        description:
          '過去系統會在預約完成、儲值購買的當下，把該筆抽成「即時累加」到加項總計；但月結時總抽成已從當月所有訂單完整計算，等於同一筆抽成被計了兩次，且加項出現對不到明細的數字。本次修正後：\n• 加項總計只計入手動登記的加項；減項總計＝手動減項＋員工自購扣款。\n• 抽成一律以「總抽成」為準，已包含在基礎薪資的計算內（保底制取「保底 vs 總抽成」較大者；底薪＋抽成制為底薪＋總抽成）。\n• 部分員工的實際提撥薪資會因此「下修」，那是修掉重複計算的抽成，並非少算薪水。\n• 列表與細項頁現在永遠顯示同一份數字，不會再出現兩頁不一致。',
        howToUse: '不用做任何事，打開「每月員工薪資」即自動套用新計算。',
        devNote:
          'commissionService：停用 addCommissionToSalarySummary 事件即時累加（保留函式簽名為 no-op）；快取分支與強制重算分支共用 getMonthlyAllowanceAdjustmentTotals，並將基礎薪資規則統一為與強制重算分支相同（保底制 max(保底,總抽成)，業績階梯僅影響抽成比例）；快取刷新有變動即存回 DB（isModified 判斷），解決列表用記憶體新值、細項頁讀 DB 舊值的雙軌問題。細項頁「加項總計」卡改顯示 summary.additionsTotal（與列表同語意）。'
      },
      {
        kind: 'feature',
        title: 'LINE 整合：現場綁定 QR Code',
        description:
          '「設定 → LINE 整合」最上方新增「現場 LINE 綁定 QR Code」，可下載列印放在櫃檯：\n• 已啟用自有 LINE 官方帳號的店家：客人掃描 → 登入會員 → 自動產生配對碼 → 加店家 LINE 好友並貼上配對碼即完成配對，頁面會自動顯示成功。\n• 未啟用自有官方帳號的店家：客人掃描後完成美感平台 LINE 綁定，預約通知由平台 LINE 傳送。\n• 綁定完成後，預約確認、變更與行前提醒都會透過 LINE 通知客人。',
        howToUseSteps: [
          {
            heading: '如何使用',
            steps: [
              '進入「設定 → LINE 整合」，下載「現場 LINE 綁定 QR Code」並列印放在櫃檯。',
              '請客人結帳或候位時用手機掃描，依畫面指示完成綁定（約 30 秒）。',
              '若貴店已啟用自有 LINE 官方帳號，客人會走「與店家 LINE 配對」流程，之後通知由貴店的官方帳號發送。'
            ]
          }
        ],
        devNote:
          '前台新增 /line-connect 落地頁（依店家 line-config/status 自動分流：配對模式產生配對碼＋輪詢 pairing-status；平台模式走 LINE 登入或 /api/auth/line/bind 綁定 OAuth）。後端 authRoutes 新增 /line/bind 與 /line/bind/callback（state 存 Redis 10 分鐘、殼帳號釋放邏輯）；linePairingController.getLineConfigStatus 增回店名與加好友連結。B 端 LineSettings 新增 QR 卡片（antd QRCode，帶 businessId+storeName）。需在 LINE Developers 的 Login channel 加入 bind callback 網址。另修正 lineWebhook/authRoutes 的 redis 匯入與 setEx 參數順序（舊「綁定帳號」關鍵字流程實際上已壞，一併恢復）。'
      }
    ]
  },
  {
    version: '2026-07-23',
    title: '7/23 新功能：可設定「單筆預約人數上限」',
    summary:
      '「預約設定」新增「單筆預約人數上限」選項（1～4 位）。只提供單人服務的店家可以把上限設為 1，客人線上預約時就不會再看到人數選擇、一律以 1 位預約；後台手動建立預約也會套用同一個上限。未調整的店家維持原本最多 4 位，不受影響。',
    sections: [
      {
        kind: 'feature',
        title: '單人服務的店家，可以關閉多人預約',
        description:
          '過去客人線上預約時一律可以選擇 1～4 位同行，對只有一位服務人員、一次只能服務一位客人的店家造成困擾。現在你可以自己決定：\n• 在「預約設定」選擇單筆預約人數上限（1～4 位）。\n• 設為 1 位時，客人預約頁面不會出現「預約人數」欄位，固定以 1 位預約，頁面更簡潔。\n• 後台手動建立預約時也套用相同上限，行為一致。\n• 沒有調整的店家維持現狀（最多 4 位），不需要做任何事。',
        howToUseSteps: [
          {
            heading: '如何調整人數上限',
            steps: [
              '進入「設定 → 預約設定」，找到「單筆預約人數上限」。',
              '只提供單人服務請選「1 位（單人服務）」；可同時服務多位客人則依實際容量選 2～4 位，並儲存。',
              '儲存後立即生效：客人端預約頁的人數選項會跟著改變，設為 1 位時該欄位會直接隱藏。'
            ]
          }
        ],
        devNote:
          'Business.appointmentSettings 新增 maxParticipantsPerBooking（1–4，預設 4，既有店家行為不變）。後端：businessController.updateAppointment 讀寫並夾限 1–4；線上預約（bookingService.createBookingDeposit 路徑）與 B 端手動建立預約（business/bookingController.createBooking）建立時皆以「店家設定與系統上限取小」強制夾住 participantCount，前端無法繞過。B 端：AppointmentSettings 設定頁新增下拉；Appointments 新增預約表單人數欄位依設定渲染、上限 1 時隱藏。前台 beautefeel-next：BookingService 以 business.appointmentSettings 計算 storeMaxParticipants 傳入 BookingStepThree，上限 1 時隱藏人數欄位，localStorage 記住的舊人數會自動壓回上限內。未新增權限碼與方案門檻（屬既有預約設定的一部分）。'
      }
    ]
  },
  {
    version: '2026-07-05',
    title: '7/5 新功能：回訪提醒可設定「自動發送」',
    summary:
      '回訪提醒（含術後關心／預約結束後貼心提醒）新增「自動發送」開關。開啟後系統每天上午 09:00 依你的設定自動挑出符合條件的客人並發送（LINE 為主、Email 備援），不必再每次手動按發送。為避免打擾，同一位客人每次療程只會收到一次；首次開啟只會發給「開啟之後」新符合的客人，不會回填過去的舊客。手動「立即發送」仍然保留。此功能為品牌方案專屬。',
    sections: [
      {
        kind: 'feature',
        title: '設定好就自動發，不必再手動按發送',
        description:
          '過去回訪／術後關心提醒每次都要你進後台手動按「發送」。現在每個回訪提醒設定裡多了一個「自動發送」開關：\n• 開啟後，系統每天上午 09:00 會依該設定的條件（天數、標籤、會員等級、服務項目等）自動挑出符合的客人並發送。\n• 發送管道與手動時相同：優先用 LINE，失敗才改用 Email。\n• 防重複：同一位客人的同一次療程只會收到一次；發送失敗的會在隔天自動補送。\n• 不回填：第一次開啟時，只會發給「開啟之後」才達到條件的客人，不會一次補發一大批歷史舊客。\n\n你仍然可以隨時用「立即發送」手動送出（例如想馬上補一批）。',
        howToUseSteps: [
          {
            heading: '如何開啟自動發送',
            steps: [
              '進入左側「顧客 → 回訪提醒」，編輯或新增一個回訪提醒設定。',
              '把「自動發送」開關打開並儲存；系統會從你開啟的當下起算，之後每天 09:00 自動發送符合條件的客人。',
              '想立即補發時，仍可用列表上的「立即發送」手動送出（不影響自動排程）。'
            ]
          }
        ],
        devNote:
          'RevisitReminderConfig 新增 autoSend { enabled(default false), enabledAt, lastAutoRunAt }。收件人篩選與發送邏輯抽出共用 utils/revisitReminderService（手動 API 與排程共用）：followup 自動模式改「單日切片＋不回填（date>=enabledAt）」，去重改 $not/$elemMatch 依 configId 分開，且只在發送成功時才寫 Booking.revisitReminders（失敗可補送，保留 2 天補送窗）。scheduleTasks 新增 runRevisitReminderAutoSendTask（每 config 每日 Redis idempotency key），worker.js 以 withLock 註冊 cron 0 9 * * *（Asia/Taipei）。前端 RevisitReminderConfigPage 加「自動發送」開關（開啟寫 enabled=true＋enabledAt）。方案門檻沿用既有 revisitReminder:[brand]，未新增權限碼。App（客人端）僅接收 LINE/Email，無對應變動。'
      }
    ]
  },
  {
    version: '2026-07-02',
    title: '7/2 新功能：項目施作同意書（行前線上簽署）',
    summary:
      '新增「同意書模組」。你可以建立施作同意書範本並綁定服務項目，系統會在行前（或預約成立時、或手動）把同意書發送給客人，客人在 App／網頁閱讀、勾選同意並手寫簽名，簽完後你可在後台看到簽署狀態與留存內容。此功能為商務方案與品牌方案專屬。',
    sections: [
      {
        kind: 'feature',
        title: '建立同意書範本、綁定服務、查看簽署狀態',
        description:
          '過去施作前的同意書多半靠紙本或口頭，難以留存與查詢。現在你可以：\n• 在「同意書模組 → 同意書設定」建立範本，填寫條款內文、需客人勾選的同意項目，並設定是否需要手寫簽名。\n• 把範本綁定到特定服務項目（不綁＝不限服務）。\n• 設定發送時機：行前自動發送、預約成立即發、或僅手動發送。\n• 在「簽署紀錄」查看每筆的待簽／已簽／逾期狀態，已簽的可開啟留存檔並另存 PDF。\n\n客人端會在 App／網頁的「我的同意書」看到待簽項目，閱讀後勾選同意、手寫簽名即可完成，簽署時間與來源都會被記錄。',
        howToUseSteps: [
          {
            heading: '建立與發送',
            steps: [
              '進入左側「同意書模組 → 同意書設定」，點「新增同意書」，填寫名稱、條款、勾選項目與簽名設定，並選擇適用服務與發送時機。',
              '若設為「行前自動發送」，系統會在客人預約的前一日連同行前提醒一起送出；也可到預約詳情手動「發送同意書」。',
              '在「簽署紀錄」追蹤簽署狀態；已簽署者可開啟留存檔（可列印／另存 PDF）。'
            ]
          }
        ],
        devNote:
          '新增後端三個 model（ConsentFormTemplate / ConsentFormRequest / ConsentFormSignature）、B 端與 client 兩套路由與 controller、utils/consentFormService（發送＋通知，重用 NotificationService 站內信＋LINE）。觸發點：client/bookingController 建立預約（on_booking）、scheduleTasks 行前提醒 cron（before_visit）、B 端手動 send。權限 consentForm:view/manage（designer 僅 view）；方案門檻 business+brand（subscriptionCheck 加「項目施作同意書」feature）。前端：beauty-business 新增 ConsentForm 設定／簽署紀錄頁＋Sidebar＋permission.json＋useMembership＋MembershipInfoPage；beautefeel-next 客戶端「我的同意書」與簽署頁（canvas）；BeautyAppJs 店家端同意書管理與簽署紀錄檢視。'
      }
    ]
  },
  {
    version: '2026-07-01',
    title: '7/1 系統更新：薪資業績可自選要合併哪些業績計算',
    summary:
      '過去薪資的「總業績」只能在「技術＋產品＋儲值」與「僅技術」之間二選一，而且堂票永遠不算進去。現在改成逐項勾選：技術業績為必計基底，產品、儲值、堂票可以自由組合，勾選的項目會合併成總業績，並用來判定業績階梯。',
    sections: [
      {
        kind: 'feature',
        title: '總業績計入項目改為逐項勾選（含堂票）',
        description:
          '先前在「員工業績抽成設定」裡，總業績只有一個開關：開啟＝技術＋產品＋儲值，關閉＝只算技術，而且堂票不論怎麼設定都不會計入總業績。若你想把「技術＋堂票」合併看業績就辦不到。\n\n這次把那個開關改成四個項目的勾選：\n• 技術業績：必計基底，固定勾選、無法取消。\n• 產品業績：可勾選。\n• 儲值業績：可勾選。\n• 堂票業績：可勾選（新增）。\n\n勾選的項目會加總為「總業績」，這個總業績同時是業績階梯的判定基數——也就是說，把某個項目納入後，員工可能因此達到更高的抽成階梯。各類別自身的抽成仍依它們各自的抽成設定計算，這裡只決定要不要把金額併入總業績。',
        howToUseSteps: [
          {
            heading: '怎麼設定要合併哪些業績',
            steps: [
              '進入「員工業績抽成設定」，選擇要設定的員工。',
              '找到「總業績計入項目」，勾選要併入總業績的類別（例如只留技術＋堂票，就取消產品與儲值）。',
              '儲存設定。若要套用到多位員工，可用「套用到其他員工」。'
            ]
          },
          {
            heading: '設定後記得重新計算',
            steps: [
              '調整勾選後，畫面上顯示的總業績會即時反映。',
              '但要讓「業績階梯與技術抽成」也一起更新，該月份需要重新計算一次薪資。'
            ]
          }
        ],
        devNote:
          'EmployeeCommission 新增 tierPerformanceItems { service, product, valueAdded, classTicket }（刻意不設 default，舊資料為 undefined 時由 resolveTierPerformanceItems 依 ladderIncludesProduct 推導：product/valueAdded 沿用舊布林、classTicket 預設 false、service 恆為 true）。commissionService 主計算與輕量（非強制重算）兩條路徑均改為依勾選累加 totalSales 與 totalSalesForTier，並將堂票明細提前取得以供階梯判定；classTicketPerformance 現可選擇性計入總業績（原本恆排除）。commissionController GET/PUT 讀寫新欄位並同步維護 ladderIncludesProduct（product||valueAdded）以相容其他讀取點與腳本。前端 EmployeeCommissionSettingsPage 將單一 Switch 換成三個 Checkbox（技術鎖定必勾），fetch/儲存/套用到其他員工三處皆已接上。App（客人端）無對應變動。'
      }
    ]
  },
  {
    version: '2026-06-19',
    title: '6/19 系統更新：商品庫存可完整追溯（進出明細 / 手動調整）',
    summary:
      '這次庫存管理一次升級三件事：每個商品可查「進出庫異動明細」、明細把建檔／調整／作廢通通列出來可完整追溯、並新增兩種會留紀錄的手動調整庫存方式。點開下方各項可看詳細說明與操作步驟。',
    sections: [
      {
        kind: 'feature',
        title: '單品進出庫異動明細',
        description:
          '過去庫存管理裡，進貨是「一張單很多商品一起記」，使用紀錄又是綁在客人身上，想單獨追一個商品「進了多少、用掉多少、怎麼變成現在的庫存」沒辦法一目了然。\n\n這次在庫存管理的每個商品卡上新增「異動明細」按鈕。點開後會列出該商品的所有異動：\n• 進貨入庫（＋數量）\n• 客戶結帳消耗、員工自購、公用消耗、贈送（－數量）\n每一筆都標明日期、類型、來源（進貨人員 / 客人 / 員工）與當下結存，最後一筆結存即為目前庫存量。上方並有「現有庫存 / 期初結存 / 累計進貨 / 累計出庫」摘要。',
        howToUseSteps: [
          {
            heading: '怎麼查單一商品的進出明細',
            steps: [
              '進入「庫存管理」頁。',
              '在想查的商品卡片底部，點藍色的「異動明細」圖示。',
              '彈出視窗會顯示該商品的進出庫流水與結存，預設由新到舊排列。'
            ]
          },
          {
            heading: '金額顯示說明',
            steps: [
              '進出數量與結存所有具備庫存檢視權限的人都看得到。',
              '成本與金額欄位僅具備財務檢視權限（finance:view）的角色才會顯示。'
            ]
          }
        ],
        devNote:
          '後端新增 GET /api/store/inventory/products/:productId/ledger（掛在 /inventory 前綴，沿用 business/brand 方案門檻 + checkPermission(\'inventory:view\')）。控制器合併 ProductPurchase（入庫，依 stockDate）與 InventoryUsage（出庫，status=active，依 date）兩個資料來源，攤平到 items 層級、依日期時間排序後逐筆累計結存。結存採「以現有庫存回推期初」做法：openingBalance = currentStock −（總進−總出），確保最後一筆結存等於目前庫存，期初差額即涵蓋手動調整庫存等本明細外的異動，並回傳 computedBalance 供稽核比對。金額/成本/單價欄位以 hasPermission(\'finance:view\', req.permissions) 控管，回傳 showCost 旗標。前端在 InventoryProductCard 新增「異動明細」按鈕（inventory:view 可見），InventoryProductsPage 掛載新元件 ProductLedgerModal 呈現摘要＋表格。'
      },
      {
        kind: 'feature',
        title: '異動明細完整追溯（建檔／調整／作廢都看得到）',
        description:
          '先前的異動明細只顯示進貨與結帳消耗，遇到「系統庫存和實際盤點對不起來」時，常常找不到差異是哪來的。\n\n這次補上三項，讓追溯更完整：\n• 建檔初始庫存：商品建立時填的庫存會列為第一筆「建檔初始庫存」，看得到是哪天建的。\n• 手動調整庫存：之後若有人手動加減或設定庫存，會記錄誰、哪天、為什麼（原因／備註），並顯示在明細裡。\n• 作廢的結帳：被作廢的結帳訂單會以灰色「作廢」標示列出，不計入結存，但讓你知道曾經有過這筆。\n\n明細上方會判讀「期初結存」：正常情況應為 0（代表每一件都追溯得到）。若不為 0，會跳提示說明這是無法追溯的差異，通常就是盤點對不上的根源。',
        howToUseSteps: [
          {
            heading: '怎麼用它抓出庫存差異',
            steps: [
              '庫存管理 → 商品卡點「異動明細」。',
              '看上方「期初結存」：是 0 就代表完整追溯；不是 0 會出現黃色提示。',
              '由上到下對照每一筆進貨、結帳、調整，找出和實際盤點對不起來的環節（例如漏結帳、進貨數量打錯）。'
            ]
          }
        ],
        devNote:
          'Store 商品 schema 新增 initialStock，createProduct 建檔時寫入初始庫存。新增 StockAdjustment 模型，updateProductStock 在改庫存後寫一筆。getProductLedger 改為合併四來源：建檔初始庫存（counted 第一筆入庫，依 createdAt）、ProductPurchase（入庫）、StockAdjustment（依 delta 正負分進出）、InventoryUsage（有效計入結存、status=cancelled 僅顯示不計）。期初改以 currentStock − 已計入淨變動回推；≠0 即代表未紀錄缺口。前端 ProductLedgerModal 新增 initial／adjustment 類型樣式、作廢列、並依 openingBalance 是否為 0 顯示成功或警告提示。'
      },
      {
        kind: 'feature',
        title: '手動調整庫存（含原因，且自動留底）',
        description:
          '先前若直接在「編輯商品」改庫存數字，系統只會把數字蓋過去、不留任何紀錄，導致異動明細的「期初結存」出現查不到來源的差異。\n\n這次補上兩個入口，而且都會把這次調整寫進該商品的「異動明細」（顯示為「庫存調整（增/減）」，含原因、時間、操作人）：\n• 商品卡「調整庫存」按鈕：點開後選「增加／減少／設定為」、填數量與原因即可，會即時預覽調整後庫存。\n• 編輯商品頁：當你改動了庫存數量，下方會出現「調整原因」必填欄，送出後一併記錄。',
        howToUseSteps: [
          {
            heading: '兩種調整方式',
            steps: [
              '快速調整：庫存管理 → 商品卡點「調整庫存」(滑桿圖示) → 選操作、填數量與原因 → 確認。',
              '編輯商品時：進入編輯商品改了庫存數量後，填寫出現的「調整原因」再儲存。',
              '兩種方式調整完，打開該商品「異動明細」都會看到一筆「庫存調整」，期初結存也會回到正確值。'
            ]
          }
        ],
        devNote:
          '前端新增 StockAdjustModal（走 PATCH /products/:id/stock，operation add/subtract/set + reason 必填 + note）。InventoryProductCard 新增「調整庫存」按鈕（inventory:manage 可見）。AddInventoryProductPage 編輯模式偵測 stock 變動，顯示必填 stockChangeReason／選填 stockChangeNote。後端 updateProduct 在 stock 實際變動時寫入 StockAdjustment（operation set、reason 取 stockChangeReason）。'
      }
    ]
  },
  {
    version: '2026-06-06',
    title: '6/6 系統更新：顧客列表新增進階搜索',
    summary:
      '顧客列表頁新增「進階搜索」功能，可依標籤、性別、生日月份、會員等級、最後到店時間交叉篩選，快速找出特定族群的客戶，方便進行活動通知或關懷追蹤。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：顧客進階搜索',
        description:
          '過去顧客列表只能用姓名、電話關鍵字搜尋，或切換「當月壽星」「會員等級」篩選，無法同時設定多個條件。\n\n這次新增「進階搜索」功能，可同時組合以下任意條件篩選顧客：\n• 標籤：輸入標籤關鍵字，找出貼有特定標籤的顧客（例如「過敏」「加強關懷」）\n• 性別：篩選男性或女性顧客\n• 生日月份：找出指定月份壽星（1-12 月任選）\n• 會員等級：篩選特定等級的顧客\n• 最後到店：篩選超過一定天數未到店的顧客，找出需要主動關懷的沉睡客',
        howToUseSteps: [
          {
            heading: '開啟進階搜索',
            steps: [
              '電腦版：在顧客列表搜索框右側，找到「進階搜索」按鈕點擊即可開啟。',
              '手機 / 平板：右下角有「 進階搜索」按鈕（在「+新增顧客」按鈕正上方）。',
              '設定好篩選條件後，點「套用篩選」即可看到符合條件的顧客。',
              '若想清除所有條件恢復顯示全部顧客，點「清除篩選」即可。'
            ]
          },
          {
            heading: '適合哪些情境',
            steps: [
              '活動推播前篩選目標族群：例如同時勾選「黃卡會員」＋「女性」，找出目標客群名單。',
              '找沉睡客戶：設定「超過 90 天未到店」，找出需要主動聯繫回流的客人。',
              '生日行銷：選擇「7 月生日」，找出下個月壽星寄送祝福或優惠。',
              '特殊關注：篩選帶有「過敏」「加強關懷」等標籤的客戶，確保接待前有所準備。'
            ]
          }
        ],
        devNote:
          '顧客列表後端查詢新增三個篩選參數：性別、標籤（以逗號分隔、資料庫以「任一符合」邏輯比對）、最後到店超過幾天（以「最後到店日期早於 N 天前」查詢）。這三個參數與既有的「會員等級」篩選統一整合到同一筆資料陣列條件，確保所有條件都命中同一筆店家關係資料。前端新增進階搜索面板，電腦版以行內按鈕呈現、手機版以懸浮按鈕呈現；套用後按鈕顯示紅色數字標記提示目前啟用的條件數量。'
      }
    ]
  },
  {
    version: '2026-06-01',
    title: '6/1 系統更新：零用金支出自動計入營收報表',
    summary:
      '零用金的「支出」記錄（電費、水費、房租等雜支）現在會自動計入「營收紀錄查詢」的「其他支出」欄位，讓月底盈餘數字更貼近真實經營狀況。同時新增月份篩選功能，可快速查看指定月份的零用金明細。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：零用金支出自動反映在每月盈餘',
        description:
          '過去在零用金記錄的電費、水費、房租、耗材等小額雜支，不會出現在「營收紀錄查詢」的報表裡，導致「本期盈餘」偏高，沒辦法真實反映店家的實際獲利。\n\n這次更新後，每筆零用金「支出」記錄都會自動加總，計入「支出明細」中的「其他支出」欄位，讓你在月底對帳時看到的盈餘數字，才是扣掉所有日常開銷後的真實結果。',
        howToUseSteps: [
          {
            heading: '如何記錄讓報表數字正確',
            steps: [
              '所有日常雜支（電費、水費、房租、耗材、清潔費等），請在「薪水與業績管理 → 零用金設定」以「支出」類型記錄，並填寫明細備註。',
              '月底前往「數據報表 → 營收紀錄查詢」，選擇當月，「支出明細」中的「其他支出」即會自動包含這些金額。',
              '「本期盈餘」＝ 營業收入 － 所有支出（含進貨、薪資、零用金雜支等），可作為當月真實獲利參考。'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '新功能：零用金月份篩選',
        description:
          '零用金紀錄頁新增月份選擇器，可快速篩選指定月份的入帳與支出明細，統計數字（入帳總額、支出總額、筆數）也會同步更新。預設顯示全部月份，選完月份後按右側「✕」可回到全部。',
        howToUse: '「薪水與業績管理 → 零用金設定」，在「零用金紀錄」右上角選擇月份即可篩選。'
      },
      {
        kind: 'improvement',
        title: '重要提醒：入帳僅供老闆撥款，客戶付款請走結帳',
        description:
          '零用金的「入帳」功能是讓老闆把自己的資金撥入零用金袋使用，屬於內部資金移轉，不會計入營業收入。\n\n如果不小心把客戶付的現金存入零用金「入帳」，這筆收入就不會出現在營收報表裡，造成帳目短少。\n\n正確流程：客戶付款 → 走「結帳」流程 → 進入營業收入；日常雜支 → 走零用金「支出」→ 計入其他支出。\n\n系統在新增入帳記錄時已加入提示，提醒操作人員注意。',
        howToUse: '依照「結帳收款 → 零用金補充 → 零用金支出」的流程操作，帳目即可保持準確。'
      }
    ]
  },
  {
    version: '2026-05-18',
    title: '5/18 系統更新：堂票銷售抽成、支付方式記錄',
    summary:
      '堂票模組新增銷售抽成設定（階梯制，可選擇是否扣除刷卡手續費），並在「發放堂票」視窗加入支付方式選擇（現金／刷卡／轉帳）。抽成結果自動計入員工每月薪資報表。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：堂票銷售抽成設定（階梯制）',
        description:
          '過去堂票售出後沒有對應的員工抽成機制，只能手動記帳。\n\n這次新增「堂票銷售抽成設定」，與儲值抽成一樣支援階梯制：依員工當月堂票銷售業績，對照不同門檻套用不同抽成比例。同時支援兩種計算規則：\n• 規則一（按業績抽成）：售價 × 抽成%，不扣任何費用。\n• 規則二（按業績抽成並扣手續費）：刷卡付款時自動扣除金流手續費，現金／轉帳則不扣。\n\n計算結果自動整合進員工每月薪資報表，月底無需另外補算。',
        howToUseSteps: [
          {
            heading: '設定堂票抽成',
            steps: [
              '左側選單「薪水與業績管理 → 堂票銷售抽成設定」。',
              '選擇計算規則（按業績 / 按業績扣手續費）。',
              '按「新增階梯」設定不同業績門檻對應的抽成比例。',
              '點「保存設定」即生效，下次計算薪資時自動套用。'
            ]
          }
        ],
        devNote: 'ClassTicketCommission model（tiers + commissionRule）；commissionService 新增 getMonthlyClassTicketCommissionDetails；整合進 calculateMonthlyCommissions；MonthlySalarySummary 新增 classTicketPerformance / classTicketCommission 欄位。路由：GET/PUT /api/businesses/:id/class-ticket/commission。'
      },
      {
        kind: 'feature',
        title: '新功能：發放堂票時記錄支付方式',
        description:
          '手動發放堂票的視窗新增「支付方式」必填欄位（現金 / 刷卡 / 轉帳），讓店家留下正確的收款紀錄。\n\n支付方式也會顯示在堂票詳情抽屜，並納入 CSV 匯出欄位。「按業績扣手續費」抽成規則會依照此欄位判斷是否扣除刷卡費用。',
        howToUse: '「堂票紀錄 → 發放堂票」視窗，選擇對應支付方式後完成發放即可。'
      }
    ]
  },
  {
    version: '2026-05-16',
    title: '5/16 系統更新：員工可從前台隱藏（眼睛開關）',
    summary:
      '員工管理頁每張員工卡片新增「眼睛」開關。打開後該員工會從客人看得到的所有地方消失：店家頁的設計師清單、預約時的設計師選單、搜尋結果、員工專頁、作品集、人氣榜。**既有預約一律保留**，B 端後台仍可正常管理該員工的預約、班表、結帳。員工管理頁多一個「前台隱藏中」分頁集中管理；隱藏中的員工狀態 badge 會變橘色「啟用 · 前台隱藏」一眼可辨。最常用情境是老闆想暫時不接新客（如休假、出差、訓練新人期間），不必整個刪掉帳號。預設老闆與店長有此權限，可自行開放給其他角色。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：員工前台顯示／隱藏切換',
        description:
          '過去若想暫時讓某位員工不出現在客人面前（例如老闆自己不接客、設計師休長假、新人訓練期），只能用「停用帳號」或「刪除員工」處理，但這會把後台管理也一起鎖住、班表跟既有預約也跟著消失。\n\n這次改成「前台顯示」與「帳號狀態」分離：\n• 員工管理頁每張卡片新增「眼睛」按鈕（顯示中 / 已隱藏）。\n• 點一下即可切換，無需確認彈窗（樂觀更新、錯誤會自動回滾）。\n• 隱藏後：客人在店家頁、預約頁、搜尋、員工專頁、作品集、設計師排行榜全部看不到此員工。\n• 既有預約完全保留：客人不會收到取消通知，B 端後台仍然能看到、修改、結帳這些預約。\n• 員工 B 端帳號照常可用（仍可登入、看自己預約、被指派服務）。\n• 隨時可再點一次切回顯示，整個動作可逆。',
        howToUseSteps: [
          {
            heading: '一、把員工從前台隱藏',
            steps: [
              '左側選單「員工管理」進入員工列表。',
              '在要隱藏的員工卡片找到「眼睛」按鈕（顯示「顯示中」表示目前客人看得到）。',
              '點一下按鈕，按鈕會變成劃線眼睛、文字顯示「已隱藏」。',
              '客人端立即看不到此員工（前台無快取延遲）。'
            ]
          },
          {
            heading: '二、把員工切回前台顯示',
            steps: [
              '在員工列表找到該員工（隱藏中的員工不會從 B 端消失，只是前台看不到）。',
              '點劃線眼睛圖示「已隱藏」按鈕，即可切回顯示。'
            ]
          },
          {
            heading: '三、誰能切換這個開關',
            steps: [
              '預設老闆（owner）與店長（manager）有此權限。',
              '若想授權給其他角色（如資深設計師、櫃台），到「員工管理 → 該員工 → 管理許可權」勾選「切換員工前台顯示」即可。',
              '所有方案（含免費）都能使用此功能，不會有方案限制。'
            ]
          },
          {
            heading: '四、適用情境',
            steps: [
              '老闆想暫停接客（休假、出國、處理行政、進修）但不想整個刪帳號。',
              '設計師長假、產假、留職停薪期間。',
              '新進員工尚在訓練期，不希望客人預約到。',
              '兼職員工某段期間不到班，不想被預約到尷尬時段。'
            ]
          }
        ],
        devNote:
          'BusinessUser 與 Business.stylists 內嵌物件各加 hiddenFromFrontend: Boolean (default false)。新增 PATCH /api/employees/:businessId/:employeeId/visibility 端點，需要 employee:edit_visibility 權限（owner 自帶 *）；同時更新 BusinessUser 與對應的 Business.stylists 條目（以 _id 為主、username 為舊資料相容）。C 端過濾點：getBusinessById、getAllStylist、getStylistByType、getStylistDetails（hidden 回 404）、getAllStylistsServiceTime、getDisabledDatesForProduct、getAllStylistsPortfolios、searchController.getAvailableTimesForProduct，全部以 filterVisibleStylists() helper 過濾 hiddenFromFrontend === true。Booking 相關查詢與 B 端管理路徑刻意不過濾，確保既有預約能正常顯示與處理。'
      }
    ]
  },
  {
    version: '2026-05-13',
    title: '5/13 系統更新：儲值購買支援金流手續費',
    summary:
      '儲值購買的付款方式拆成「刷卡」「行動支付」兩個欄位（原本只有合併的「行動支付/刷卡」），並會依「設定 → 金流手續費」的費率自動算出該筆儲值的手續費。手續費會從營收扣除（呈現實際入帳）、也會從儲值抽成基數扣除，與結帳的金流邏輯完全一致。結帳頁的儲值 modal 與獨立的儲值購買頁同步更新。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：儲值購買支援金流手續費',
        description:
          '過去儲值購買時的付款只有現金/匯款/應收/行動支付刷卡（合一）四個欄位，店家用刷卡或行動支付收儲值款項時，金流手續費沒有被系統記錄，月底對帳常常少算 2-3% 的成本。\n\n這次比照結帳的設計：\n• 刷卡 / 行動支付拆兩個欄位、各自顯示對應費率\n• 自動依費率算出該筆儲值的「金流手續費」\n• 儲值收入報表會自動扣手續費，呈現「實際入帳」\n• 儲值抽成基數也會扣手續費後再算比例\n\n結帳頁的儲值 modal 與獨立的儲值購買頁兩處都同步更新。',
        howToUseSteps: [
          {
            heading: '一、確認費率設定',
            steps: [
              '左側選單「設定 → 金流手續費」確認費率（預設刷卡 2.8%、行動支付 2.5%）。',
              '可隨時修改；後續儲值會以當下費率為快照存入紀錄，不影響歷史資料。'
            ]
          },
          {
            heading: '二、儲值購買時',
            steps: [
              '進「儲值購買」或從結帳頁開啟「儲值金加值」彈窗。',
              '若客戶用刷卡 → 填「刷卡」欄位；用 Line Pay / Apple Pay 等 → 填「行動支付」欄位。',
              '系統會即時顯示「金流手續費：NT$ X」提示，並自動帶入到送出資料中。',
              '若有特殊情況可手動覆寫「金流手續費」欄位（在儲值購買頁顯示）。'
            ]
          },
          {
            heading: '三、報表會看到什麼',
            steps: [
              '營收報表的「儲值收入」會自動扣除該筆手續費，呈現真實入帳。',
              '例：客戶儲 10,000 刷卡 → 手續費 280 → 報表呈現 9,720。'
            ]
          },
          {
            heading: '四、抽成會看到什麼',
            steps: [
              '儲值抽成基數從 總付款金額 改為 (總付款金額 − 手續費)，比較貼近真實獲利。',
              '例：客戶儲 10,000 刷卡、手續費 280、抽成 5% → 抽成從 500 變 486。'
            ]
          }
        ],
        devNote:
          'ValueAddedPurchase model 加 cardPayment/mobilePayment/paymentFee/cardFeeRate/mobilePaymentFeeRate 五個欄位；mobileCardSwipe 保留為 cardPayment+mobilePayment 的雙寫舊欄位，向後相容報表查詢。balanceController.topupBalance 在收到 paymentDetails 時接收 cardPayment+mobilePayment（電子金額），驗證 paymentSum；paymentFee 前端可覆寫、否則依 cardRate/mobileRate 自動算。commissionService.handleValueAddedCommission 與 calculateValueAddedCommission 的 amount/monthlySales 改用 max(0, totalPayment − paymentFee)。ReportController 第一個 totalValueAdded 加扣 paymentFee；預收款/累計餘額/儲值流水（cash flow accounting）維持 gross，不扣手續費。前端 ValueAddedPurchasePage 加 fetchPaymentFeeSettings；FormRow→PaymentFieldRow 2x2 排版；刷卡/行動支付各帶費率 label 與 /settings/paymentFee 修改連結；onChange 自動算 paymentFee，可手動覆寫。CheckoutPage topupPaymentDetails 加 cardPayment/mobilePayment 欄位，提交時送 paymentFee/cardFeeRate/mobilePaymentFeeRate。'
      }
    ]
  },
  {
    version: '2026-05-12',
    title: '5/12 系統更新：產品銷售抽成可選「按真實利潤扣手續費」',
    summary:
      '產品銷售抽成設定新增第二種計算規則：（商品實收 − 成本）× 抽成% − 該商品分攤之金流手續費。「商品實收」會自動扣除折扣、優惠券、會員折扣的分攤，反映真實獲利。店家可在後台自由切換，舊規則（商品業績 × 抽成%）依然保留。成本自動從庫存模組讀取；手續費僅在含刷卡 / 行動支付的訂單會列入分攤，現金與轉帳訂單免扣。最低保證 0，員工不會倒貼。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：產品銷售抽成新增「真實利潤扣手續費」規則',
        description:
          '過去產品抽成只有一種計算方式：商品銷售金額 × 抽成%。對於毛利率不同的商品（高價低毛利 vs 低價高毛利），不分高低一律按業績算容易導致店家虧本。這次新增規則「(商品實收 − 成本) × 抽成% − 該商品手續費」，能更貼近真實獲利分配。\n\n關鍵特色：商品實收會自動扣除整單折扣、優惠券、會員折扣的分攤——例如客戶用優惠券打 8 折，原價 200 的商品實收只算 160，避免店家在折扣訂單上倒貼抽成。\n\n店家可依商品結構自由選用：\n• 商品標準化、成本資料未齊全 → 用舊規則（按業績）\n• 想精準分攤利潤、有完整成本資料 → 用新規則（按真實利潤扣手續費）',
        howToUseSteps: [
          {
            heading: '一、先在庫存管理填好成本',
            steps: [
              '左側選單「庫存管理 → 商品管理」。',
              '每個商品有「成本」欄位，請填寫進貨單價。',
              '未填寫成本的商品，新規則會以 0 計算（等同售價全為利潤），可能與預期不符。'
            ]
          },
          {
            heading: '二、設定抽成規則',
            steps: [
              '左側選單「設定 / 抽成 → 產品銷售抽成設定」。',
              '上方「計算規則」二選一：「按業績抽成」或「按利潤抽成扣手續費」。',
              '輸入抽成比例（0-100%），按「保存設定」。'
            ]
          },
          {
            heading: '三、計算邏輯（新規則）',
            steps: [
              '商品 A：售價 1000、成本 600、數量 1，抽成 30%',
              '訂單原價 1000、用優惠券折抵 100 → 實收 900',
              '商品實收 = 1000 × (900 / 1000) = 900（自動含折扣分攤）',
              '利潤 = 商品實收 900 − 成本 600 = 300',
              '抽成（扣費前）= 300 × 30% = 90',
              '若訂單刷卡 700、現金 200，信用卡手續費 2.8%→ 整單手續費 19.6',
              '商品 A 分攤手續費 = 19.6 × (900 / 900) ≈ 19.6',
              '最終抽成 = 90 − 19.6 ≈ 70（負值歸 0，員工不倒貼）'
            ]
          },
          {
            heading: '四、何時用哪一種？',
            steps: [
              '◆ 商品 SKU 多元、毛利差異大 → 用新規則更公平',
              '◆ 商品標準化、毛利穩定、想保留簡單 → 用舊規則',
              '◆ 全店都是現金 / 轉帳收款 → 新規則的手續費部分 = 0，等同 (售價-成本)×%'
            ]
          }
        ],
        devNote:
          'ProductSalesCommission schema 加 commissionRule enum [sales_rate, profit_rate_minus_fee]，default sales_rate。commissionController.getProductSalesCommission / updateProductSalesCommission 接收並回傳 commissionRule。utils/commissionService.getMonthlyProductSalesCommissionDetails 依 rule 走兩條公式：profit_rate_minus_fee 時建 storeProductCostMap（Store.products._id → cost），按 (price-cost)*qty*rate% 計算 baseCommission；手續費分攤 = order.payments.paymentFee × (productPerformance / orderActual)；最終 max(0, baseCommission - fee)。新增 detail 欄位 unitCost / lineProfit / lineFee 供報表追蹤。前端 ProductSalesCommissionSettingsPage 加 Radio.Group 規則選擇器（卡片式），加 Alert 提示先設成本。'
      }
    ]
  },
  {
    version: '2026-05-10',
    title: '5/10 系統更新：會員等級自動升降等',
    summary:
      '會員等級不再只是「店家手動指定」。現在每個等級可以設定升等門檻、會籍效期、續等門檻；客戶結帳時系統會自動累加實付金額，達標即時升等（支援跳等）；會籍到期由每日 06:00 排程處理續等／降等。客戶在會員中心多了「我的會員等級」入口，可看到目前等級、累計金額、距下級還差多少、會籍到期日。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：會員等級自動升降等',
        description:
          '過去設定會員等級後，等級永久不變，全靠店家手動調整。現在每個等級新增 4 個欄位：升等門檻（累計實付金額達標即升等）、續等門檻（會籍到期時達標可維持）、會籍效期（多少天）、升等後累計處理（歸零或不歸零）。客戶結帳時系統會自動累加實付金額（應收帳款 − 優惠券抵扣），達到下一級門檻即時升等，支援一次跳多級。會籍到期由每天早上 06:00 排程自動評估續等／降等，並寫入完整異動歷程供日後查詢。',
        howToUseSteps: [
          {
            heading: '一、設定升等規則',
            steps: [
              '左側選單「客戶 → 會員等級設置」。',
              '編輯任一等級，往下捲找到「自動升降等規則」區塊。',
              '升等門檻：累計實付達此金額即可升至此級（例：30000）。',
              '續等門檻：會籍到期時要達多少才能維持（例：20000）。',
              '會籍效期：此等級可維持幾天（常見 365 天 = 一年；填 0 = 永久）。',
              '升等後累計處理：歸零（最常見）／不歸零（讓下一級更易達成）。'
            ]
          },
          {
            heading: '二、結帳即時升等',
            steps: [
              '客戶結帳完成後，系統立刻把實付金額累加到「該客戶在你店裡的累計」。',
              '若累計金額達到任何更高級的門檻 → 立即升等。',
              '一次達到多級門檻（例如新客直接消費 5 萬）→ 跳等到最高達標等級。',
              '訂單作廢時，金額會從累計扣回（不會反向降等）。'
            ]
          },
          {
            heading: '三、會籍到期處理',
            steps: [
              '系統每天 06:00（台灣時間）自動檢查當天到期的會員。',
              '累計達續等門檻 → 維持原等級，累計歸零、效期重新起算。',
              '不達標 → 依累計金額降至最高符合的等級（找不到符合的就降至預設等級）。',
              '所有異動都會記錄在客戶資料的「等級異動歷程」。'
            ]
          },
          {
            heading: '四、客戶端看得到',
            steps: [
              '顧客登入會員中心 → 個人中心多了「我的會員等級」入口。',
              '進去可看到每家店的目前等級、累計金額、會籍到期日。',
              '若還沒達最高級，會顯示「距下一級還差 NT$ XXX」進度條，鼓勵消費。',
              '已是最高級會顯示「續等門檻進度」（若有設）。'
            ]
          },
        ],
        devNote:
          'Business.membershipTiers schema 加 5 欄：upgradeAmount/renewAmount/tierValidityDays/onUpgradeRule(reset|continue|lookback)/lookbackDays。Customer.businessRelations[].businessSpecificData 加 5 欄：tierAssignedAt/tierExpiresAt/tierAccumulatedAmount/tierAccumulationStartDate/tierHistory[]。新建 shared/services/membershipTierService.js 提供 addPaidAmount / subtractPaidAmount / applyTierChange / evaluateForExpiry / runDailyEvaluation。checkoutController.createOrder 在 order.save() 後呼叫 addPaidAmount（paid = receivable − voucher，不含 storedValue 抵扣），即時評估升等並 customer.save()。voidOrder 在訂單作廢後呼叫 subtractPaidAmount 回沖（不會觸發降等）。worker.js 加 cron `0 6 * * *` Asia/Taipei 觸發 runMembershipTierEvaluationTask，掃 Business.membershipTiers.upgradeAmount > 0 的店家逐一跑 runDailyEvaluation。後台 MembershipTierSettingsPage 加 4+1 個 Form.Item，businessController.createMembershipTier / updateMembershipTier 接收新欄位並 normalize。client/controller/membershipController.js 提供 GET /api/client/membership 回傳每家店的當前等級、累計、距下級、續等資訊與最近 10 筆 history。前端 next 新增 app/account/membership/page.js + src/container/MyMembershipPage.js（卡片 + Progress + 異動歷程 Timeline），UserDashBoard 加「我的會員等級」入口。Migration script: scripts/initMembershipTierTracking.js，--dry-run / --execute / --businessId，idempotent（已有 tierAssignedAt 自動跳過）。'
      }
    ]
  },
  {
    version: '2026-05-09',
    title: '5/9 系統更新：堂票模組上線(品牌方案限定)',
    summary:
      '本次新增「堂票模組」：店家可建立堂票範本（總張數、有效天數、適用服務、售價），手動發放給會員或在結帳時直接加購；客戶可在會員中心「我的堂票」查詢剩餘張數、到期日與使用歷程。後台可隨時扣張、退張、作廢，所有操作會留下完整歷程。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：堂票模組（上線）',
        description:
          '把過去要靠紙本記錄的「療程套票／次數券」整個搬進系統。店家可以一次設定好範本（例：新客套組 3 張，有效 90 天，售價 $3998），之後每次發放／核銷／到期，系統都會自動算剩餘張數、自動標到期、保留完整使用紀錄。客戶在自己的會員中心也看得到自己有哪幾張、還剩幾張、什麼時候到期。',
        howToUseSteps: [
          {
            heading: '一、建立堂票範本',
            steps: [
              '左側選單「堂票模組 → 堂票設定」。',
              '右上角點「新增堂票」。',
              '填名稱、可使用張數（總次數）、有效期限（領取後 N 天）、售價。',
              '可選「適用服務」：限定這張堂票只能折抵某些服務；不選＝不限服務。',
              '存檔後該範本即可發放。'
            ]
          },
          {
            heading: '二、後台手動發放',
            steps: [
              '左側選單「堂票模組 → 堂票紀錄列表」。',
              '右上角點「發放堂票」，選範本與會員、選銷售人員，送出。',
              '系統會自動產生 8 碼堂票編號（例：T6D3ANE5），並寫入有效期。'
            ]
          },
          {
            heading: '三、結帳時加購（最常用）',
            steps: [
              '到結帳頁，下方「加購堂票」按鈕。',
              '選範本、設數量（買幾張）、單價可現場改價。',
              '結帳完成後系統會自動為該客戶產生對應數量的堂票，每張獨立編號與到期日。',
              '若該結帳訂單之後被作廢，對應堂票會一併標為「已作廢」。'
            ]
          },
          {
            heading: '四、扣張／退張／作廢',
            steps: [
              '在堂票紀錄列表點任一張票的「詳情」。',
              '右上角可選「扣張」（核銷一次）／「退張」（誤扣回沖）／「作廢」。',
              '若範本綁定了適用服務，扣張時要選對應服務；不限服務的範本可選填。',
              '所有操作會記錄在「使用歷程」時間軸，含操作員、時間、服務、剩餘張數。'
            ]
          },
          {
            heading: '五、客戶端查詢',
            steps: [
              '客戶登入會員中心後，下方「我的堂票」入口可查所有商家發放的堂票。',
              '可依「可使用 / 使用完畢 / 已過期」分頁篩選。',
              '點任一張卡片可看完整使用歷程。'
            ]
          }
        ],
        devNote:
          '新增 3 個 Mongoose Model：ClassTicketTemplate / ClassTicket / ClassTicketUsage（皆 soft delete + business scope index）。新增 business + client 兩組 controller/route，掛 /api/businesses/:bid/class-tickets 與 /api/client/class-tickets。權限：DEFAULT_ROLE_PERMISSIONS 加 classTicket:view / classTicket:manage（manager / receptionist / accountant 預設給）。useMembership 加 classTicket feature flag（business / brand 方案開放）。Sidebar / NavBar / App.js 路由註冊。CheckoutOrder 既有 courses.purchases 重用：createOrder 後依 purchases 自動 issue ClassTicket（per row × quantity 各自獨立 ticketCode），voidOrder 連動將 sourceRefId=order._id 的票一律 voided。createOrder 與時間驗證放寬：堂票-only 訂單不再要求 endTime/timeRange。CheckoutPage 加堂票購買 UI 與小計顯示，CheckoutRecordsPage 詳情卡新增「堂票購買」section + 金額資訊「堂票小計」。前端 next：app/account/class-tickets + MyClassTicketsPage。歷史資料匯入腳本 scripts/importClassTickets.js（多店通用、idempotent、--dry-run/--execute、ticketCodeToPhone disambiguation）。'
      }
    ]
  },
  {
    version: '2026-05-08',
    title: '5/8 系統更新',
    summary:
      '本次共 3 項更新，圍繞庫存與結帳紀錄的日常流程：商品管理可一鍵跳出進貨 Modal 連續補貨、結帳紀錄可摺疊耗材方便截圖給客人看、進貨與拆公用都加上分類與關鍵字篩選。',
    sections: [
      {
        kind: 'feature',
        title: '新功能：商品管理一鍵進貨（快速補貨）',
        description:
          '過去要對某項商品進貨，必須切到「進貨管理」頁再從整份清單裡找到該商品。現在商品管理頁的每張商品卡都有「進貨」快捷按鈕（購物車圖示），點下去會直接彈出視窗、商品已預先帶入，填完員工／日期／數量送出，視窗就會自動關閉並更新庫存。整個流程不會離開商品管理頁，特別適合一次要補好幾個商品的場景。',
        howToUseSteps: [
          {
            heading: '快速進貨流程',
            steps: [
              '到「庫存管理 → 商品管理」。',
              '在要進貨的商品卡上點「購物車」圖示。',
              '會跳出視窗，該商品已自動加入進貨清單，調整數量／單價。',
              '填寫經手人、購買方式、入庫／付款日期 → 送出。',
              '視窗關閉後庫存即時更新，可立刻對下一個商品再點一次「購物車」。'
            ]
          }
        ],
        devNote:
          'InventoryPurchasePage 改為支援 embedded props（embedded / preselectedProductId / onSuccess / onClose），由 InventoryProductsPage 託管 Modal。InventoryProductCard 新增 onPurchase callback，沒有 callback 時 fallback 至原 navigate 行為。Modal 用 nonce key + destroyOnClose 強制重置內部狀態，避免連續開啟時殘留上次的選擇。'
      },
      {
        kind: 'improvement',
        title: '結帳紀錄可摺疊耗材（截圖友善）',
        description:
          '服務搭配的耗材（藥水、染膏等）原本會跟一般商品一起列在結帳紀錄裡，店家反映在截圖給客人看時不太方便。現在三個會看到結帳紀錄的地方都加上「顯示／隱藏耗材」切換按鈕，預設摺疊耗材；要看完整明細時再點開即可。',
        howToUseSteps: [
          {
            heading: '受惠的三個地方',
            steps: [
              '「結帳記錄查詢」頁，每筆訂單卡片的商品明細區。',
              '顧客詳細資料 → 點開預約看到的結帳紀錄視窗',
              '預約詳細資訊頁 → 已結帳預約的「結帳紀錄」區塊。'
            ]
          },
          {
            heading: '操作方式',
            steps: [
              '預設只會看到一般銷售商品，旁邊會顯示「顯示耗材（N）」連結。',
              '需要核對時點一下，所有耗材會展開、每筆右側標上灰色「耗材」標籤。',
              '再點一次「隱藏耗材」即可恢復摺疊狀態，每張紀錄卡的展開狀態各自獨立。'
            ]
          }
        ],
        devNote:
          '在 CheckoutRecordsPage / CustomerDetail / BookingDetail 三處新增 ProductsSection（或 BookingProductsSection）小元件，內含 useState 控制 showConsumables。判斷依據為 product.isConsumable（CheckoutPage 提交時已帶入的旗標）。當 regulars 為空、consumables > 0 時顯示「本次共 N 項耗材，已隱藏」提示，避免商品明細看起來像空的。'
      },
      {
        kind: 'improvement',
        title: '進貨 & 拆公用：加入分類與關鍵字篩選',
        description:
          '商品多了之後，要在進貨或拆公用頁找到特定商品很費神。兩個頁面現在都加上「分類下拉 + 關鍵字搜尋」，先選分類再點「添加商品」，下拉內就只剩該分類下的商品；下拉選項右側也會顯示分類標籤，多店多品項時更容易辨識。',
        howToUseSteps: [
          {
            heading: '建議流程（先篩選、再添加）',
            steps: [
              '到「庫存管理 → 進貨管理」或「庫存管理 → 拆公用」。',
              '在頂部選擇分類（例如「染膏」）或輸入關鍵字（商品名稱／廠商）。',
              '篩選後右側會顯示「已篩選：N / 總數」可確認範圍。',
              '點「添加商品」新增一列，下拉打開只會看到符合條件的商品。',
              '同一張單據可隨時切換分類，已選的商品仍會保留在原本的列上、不會被洗掉。'
            ]
          }
        ],
        devNote:
          'InventoryPurchasePage 與 InventoryPublicUsePage 都載入 categories，並以分類 ID + 關鍵字過濾出 visibleProducts；每列 Select 在過濾後會自動把「目前已選」商品保留在選項中（避免切換分類後值消失）。下拉選項以 Tag 顯示分類名稱，optionFilterProp 改為 label 以與顯示格式一致。'
      }
    ]
  },
  {
    version: '2026-04-29',
    title: '4/29 系統更新',
    summary:
      '本次共 3 項更新，包含庫存與預約相關的 bug 修正、操作體驗優化，加上「服務搭配耗材」「庫存可拆 0.5」「預約整理時間」三個全新功能。',
    sections: [
      {
        kind: 'bugfix',
        title: '庫存使用記錄分頁排序錯亂修正',
        description:
          '進到「庫存管理 → 使用記錄」時，原本翻到第 2 頁會發現順序錯亂，再點回第 1 頁甚至會顯示去年的舊資料。已修正為永遠按日期由新到舊排序，不論翻多少頁都不會跑掉。',
        howToUse: '不用做任何事，重新整理頁面就會看到正確順序。',
        devNote:
          'Ant Design Table 的 sorter.field 在某些情境會殘留前次點過的欄位（例如 totalAmount），即使 sorter.order 已被清空，前端仍把殘留 field 帶到後端造成排序由「金額大到小」。前端 InventoryUsagePage 改成「order 為 ascend/descend 才送 sortField」，後端 inventoryController 加白名單 + sortOrder 校驗，雙保險。'
      },
      {
        kind: 'bugfix',
        title: '預約自動行前提醒不再打擾「未確認」客人',
        description:
          '之前如果店家把「預約預設狀態」設為「確認中」，客人下了預約還沒被店家確認，第二天系統還是會誤發行前提醒給客人。現在改成只發給「已確認」的預約，避免造成困擾。',
        howToUse:
          '不用做任何事。如果你的店有開啟「預設狀態 = 確認中」，這個更新已經自動生效。',
        devNote:
          'utils/scheduleTasks.js 的 runBookingReminderTask 查詢條件從 status: { $in: [\'pending\', \'confirmed\'] } 改為 status: \'confirmed\'。cron 每天 11:30（台灣時間）執行。'
      },
      {
        kind: 'feature',
        title: '新功能：服務搭配的耗材／商品',
        description:
          '可以在「服務」上預先設定要用到的耗材（藥水、染膏、護髮膜等）。客人結帳時系統會自動把這些耗材帶到商品欄、自動扣庫存，預設不算商品銷售業績。如果店員結帳當下要把它另外賣給客人，再勾選「加入業績計算」就會走一般商品銷售流程。',
        howToUseSteps: [
          {
            heading: '一、設定耗材',
            steps: [
              '先確認該耗材已經在「庫存管理 → 商品管理」裡建立過。',
              '到「服務」→ 點開要編輯的服務（例如「燙髮」）→ 滑到下方「服務搭配的耗材／商品」區塊。',
              '點「新增耗材」→ 從下拉挑商品 → 設數量（可以填 0.5 之類的小數）→ 決定「預設加入業績」要不要打開（預設關）→ 可填備註（例如「短髮 1 條／長髮 2 條」）。',
              '保存。'
            ]
          },
          {
            heading: '二、結帳時的行為',
            steps: [
              '客人結帳時只要選了該服務，購物車的「商品」欄會自動跳出這幾筆耗材。',
              '每筆耗材會標示「耗材來源：自動帶入：燙髮」，旁邊有「加入業績計算」勾選框。',
              '勾起來：單價自動帶入庫存的「建議售價」、可指定銷售人員，跟一般商品銷售完全一樣。',
              '不勾：庫存照扣、成本照算，但不會列進客人帳單金額、不會算員工商品業績、不會算商品銷售抽成。'
            ]
          }
        ]
      },
      {
        kind: 'bugfix',
        title: '服務列表「啟用 / 禁用」按鈕誤跳轉修正',
        description:
          '原本在服務列表頁點開三點選單按「啟用」或「禁用」，會被丟進編輯頁面。現在改成就地切換狀態，留在原列表頁。',
        howToUse: '不用做任何事。要編輯服務時點卡片本身或選單裡的「編輯」即可。',
        devNote:
          'AntD v5 Dropdown 預設把選單浮層掛在觸發按鈕父節點，選單項是 li[role=menuitem] 不是 button，原本卡片 onClick 只擋了 closest("button") 因此點選單項會冒泡觸發卡片導航。已加上 .ant-dropdown / [role=menuitem] / .ant-popover 的 closest 排除。'
      },
      {
        kind: 'bugfix',
        title: '服務搭配耗材保存失敗修正',
        description:
          '原本設好耗材後保存會跳「對應的庫存商品不存在」錯誤，即使商品確實存在。已修正。',
        howToUse:
          '若你之前因為這個問題沒能存成功，再開一次服務編輯頁、設好耗材重存即可。',
        devNote:
          'parseAndValidateConsumables / resolveServiceConsumables 在 businessController 中查 Store 用 findOne({ businessId })，但 Store schema 欄位是 business（不是 businessId），導致永遠回傳 null → productMap 是空 → 任何 productId 都 throw 「對應的庫存商品不存在」。已改為 findOne({ business: businessId }) 與 codebase 其他地方一致。'
      },
      {
        kind: 'feature',
        title: '新功能：庫存可拆 0.5（小數扣庫存）',
        description:
          '可以記錄「半瓶染膏」「1.5 條護髮膜」這類非整數的拆量使用。過去只能填整數，現在拆公用、結帳搭配耗材、員工自購等地方全部打開到小數（最低 0.01）。所有計算都做了精度防呆，不會出現「明明扣完卻剩 0.0000001」這種怪怪的數量。',
        howToUseSteps: [
          {
            heading: '拆公用 0.5 瓶',
            steps: [
              '到「庫存管理 → 拆公用」。',
              '選商品 → 數量直接打 0.5（旁邊會顯示「瓶」這類單位）。',
              '保存後庫存會正確扣除 0.5。'
            ]
          },
          {
            heading: '服務耗材設成半瓶',
            steps: [
              '到服務編輯 → 耗材區。',
              '數量打 0.5 → 保存。',
              '結帳時這個服務帶出的耗材就會以 0.5 計算。'
            ]
          }
        ]
      },
      {
        kind: 'feature',
        title: '新功能：預約整理／銜接時間',
        description:
          '有些店家不想要預約時間銜接得太剛好，例如一筆預約完成後需要 30 分鐘整理時間才能再排下一筆。現在可以設「全店預設值」，也可以針對單一服務（例如燙髮）特別覆寫。多服務一筆預約時系統會取最大值，避免被「最複雜的那個服務」拖到。',
        howToUseSteps: [
          {
            heading: '情境 A：全店都要 15 分鐘整理時間',
            steps: [
              '到「設定 → 預約設定」。',
              '「整理時間（分鐘）」填 15 → 保存。',
              '從此每筆預約結束後 15 分鐘內不會再排到下一筆。'
            ]
          },
          {
            heading: '情境 B：燙髮要 30 分鐘、其他服務照舊',
            steps: [
              '全店預設保留 0 或 15。',
              '到「服務」→ 編輯「燙髮」→「整理時間（覆寫）」填 30 → 保存。',
              '只有燙髮會多留 30 分鐘 buffer，其他服務照全店預設走。'
            ]
          },
          {
            heading: '情境 C：恢復某個服務沿用全店預設',
            steps: [
              '服務編輯 → 把「整理時間（覆寫）」清空 → 保存。',
              '該服務改回沿用全店預設值。'
            ]
          }
        ]
      }
    ]
  }
]
